import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import UseAuthContext from '../context/AuthContext';
import { USER_ROLES } from '../utils/constants';
import { getWindowOrientation, resolveBuildingWall, isWindowFenestration } from '../utils/wallOrientation';
import { aggregatePumpSectionGroupsByUnit, groupByUnitZoneSectionField, isEquipmentGainSection, isPumpSection, isPumpWattField, parsePumpZone, prepareUnitZoneTableRows, PUMP_SECTION_LABEL, shouldUseUnitZoneGrouping, sortPumpUnitRows, type UnitZoneTableView } from '../utils/pumpMismatchDisplay';
import UnitZoneViewToggle from '../components/ReportViewer/UnitZoneViewToggle';
import {
  buildZoneFloorAreasFromSurfaces,
  formatArea,
  formatZoneFloorAreaDisplay,
  getZoneFloorArea,
  lookupZoneFloorArea,
  parseNumericValue,
} from '../utils/energyPlusFloorArea';
import {
  formatNumberOfPeopleDisplay,
  getNumericMismatchDifference,
  getNumericMismatchTolerance,
  numbersMatchToTenth,
} from '../config/numericMismatchTolerance';
import {
  availScheduleFieldsEquivalent,
  closestCodeArrayValues,
  compareCompactScheduleData,
  normalizeScheduleDataForCompare,
} from '../utils/scheduleAvailEquivalence';

// Styled components
const Container = styled(Paper)(({ theme }) => ({
  maxWidth: '95%',
  minWidth: '1200px',
  margin: '0 auto',
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const Header = styled(Box)({
  background: '#2c3e50',
  color: 'white',
  padding: '20px',
  textAlign: 'center',
});

const TabsContainer = styled(Box)({
  display: 'flex',
  background: '#34495e',
  overflowX: 'auto',
});

const TabButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  background: active ? '#1abc9c' : 'none',
  border: 'none',
  color: 'white',
  padding: '15px 20px',
  fontSize: '16px',
  transition: 'background 0.3s',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  fontWeight: active ? 'bold' : 'normal',
  '&:hover': {
    background: active ? '#1abc9c' : '#3c5570',
  },
}));

const TabContent = styled(Box)<{ active?: boolean }>(({ active }) => ({
  display: active ? 'block' : 'none',
  padding: '20px',
  animation: active ? 'fadeIn 0.5s' : 'none',
  overflowX: 'auto',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

const Section = styled(Box)({
  marginBottom: '25px',
  border: '1px solid #e1e1e1',
  borderRadius: '5px',
  overflow: 'hidden',
  minWidth: '100%',
});

const SectionHeader = styled(Box)({
  background: '#ecf0f1',
  padding: '12px 15px',
  fontWeight: 'bold',
  borderBottom: '1px solid #e1e1e1',
});

const SectionContent = styled(Box)({
  padding: '15px',
  overflowX: 'auto',
});

const DropArea = styled(Box)<{ highlight?: boolean }>(({ highlight }) => ({
  border: '2px dashed #3498db',
  borderRadius: '5px',
  padding: '30px',
  textAlign: 'center',
  marginBottom: '20px',
  backgroundColor: highlight ? '#e1f5fe' : '#f8f9fa',
  borderColor: highlight ? '#1abc9c' : '#3498db',
  transition: 'all 0.3s',
  cursor: 'pointer',
}));

const FileInputWrapper = styled(Box)({
  position: 'relative',
  display: 'inline-block',
  marginTop: '10px',
});

const FileInputButton = styled(Button)({
  background: '#9b59b6',
  color: 'white',
  border: 'none',
  padding: '10px 15px',
  borderRadius: '4px',
  fontWeight: 'bold',
  textTransform: 'none',
  '&:hover': {
    background: '#8e44ad',
  },
});

const StyledTable = styled(Table)({
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  '& th': {
    backgroundColor: '#f8f9fa',
    fontWeight: 600,
  },
  '& tr:hover': {
    backgroundColor: '#f5f5f5',
  },
});

const StyledTableCell = styled(TableCell)({
  padding: '10px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #e1e1e1',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  verticalAlign: 'top',
});

const CodeMatchCell = styled(StyledTableCell)({
  backgroundColor: '#d4edda !important',
});

const ComplianceMatchCell = styled(StyledTableCell)({
  backgroundColor: '#d4edda !important',
});

const HeightVerificationCell = styled(StyledTableCell)({
  backgroundColor: '#fff3cd !important',
  color: '#856404',
  fontWeight: 'bold',
});

const NameCell = styled(StyledTableCell)({
  maxWidth: '250px',
  minWidth: '200px',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
});

const DebugInfo = styled(Box)({
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeaa7',
  borderRadius: '4px',
  padding: '10px',
  margin: '10px 0',
  fontFamily: 'monospace',
  fontSize: '12px',
  maxHeight: '200px',
  overflowY: 'auto',
});

const CodeValuesInfo = styled(Box)({
  backgroundColor: '#e8f5e8',
  borderLeft: '4px solid #2ecc71',
  padding: '10px 15px',
  margin: '10px 0',
  borderRadius: '4px',
});

// Types
interface ZoneData {
  [key: string]: any;
}

interface WindowAnalysis {
  [zoneName: string]: {
    displayName: string;
    floorArea: number;
    totalWindows: number;
    orientations: { North: number; South: number; East: number; West: number; Unknown: number };
    windowDetails: { [orientation: string]: string[] };
    hasNaturalVentilation: boolean;
    differentOrientations: number;
  };
}

interface CodeData {
  objects?: {
    [key: string]: any;
  };
}

interface ComparisonResult {
  section: string;
  zone: string;
  field: string;
  value: any;
  codeValue: any;
  isMatch: boolean;
  displayValue: string;
  includeInMismatchReport?: boolean;
}

const EnergyPlusViewer: React.FC = () => {
  const { role } = useContext(UseAuthContext);
  const isAdmin = role === USER_ROLES.ADMIN;
  
  const [activeTab, setActiveTab] = useState('files');
  const [mainData, setMainData] = useState<any>(null);
  const zoneSurfaceFloorAreas = useMemo(
    () => buildZoneFloorAreasFromSurfaces(mainData),
    [mainData]
  );
  const [codeData, setCodeData] = useState<CodeData | null>(null);
  const [mainFileName, setMainFileName] = useState('');
  const [codeFileName, setCodeFileName] = useState('');
  const [mainFileStatus, setMainFileStatus] = useState('Main JSON: Not loaded');
  const [codeFileStatus, setCodeFileStatus] = useState('Code JSON: Not loaded');
  const [mainDropHighlight, setMainDropHighlight] = useState(false);
  const [codeDropHighlight, setCodeDropHighlight] = useState(false);
  const [unitZoneView, setUnitZoneView] = useState<UnitZoneTableView>('byRevisionValue');

  const mainFileInputRef = useRef<HTMLInputElement>(null);
  const codeFileInputRef = useRef<HTMLInputElement>(null);
  const mainDropAreaRef = useRef<HTMLDivElement>(null);
  const codeDropAreaRef = useRef<HTMLDivElement>(null);

  // Helper function to format keys for display
  const formatKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Helper function to check if an array represents a numeric range
  const isNumericRange = (arr: any[]): boolean => {
    return arr.length === 2 && 
           typeof arr[0] === 'number' && 
           typeof arr[1] === 'number';
  };

  // Helper function to check if values match
  const valuesMatch = (value1: any, value2: any, section?: string, field?: string): boolean => {
    // Fields that should use discrete value checking instead of range checking
    const discreteValueFields = [
      'minimum_surface_convection_heat_transfer_coefficient_value',
      'surface_temperature_upper_limit'
    ];
    const isDiscreteValueField = section === 'HeatBalanceAlgorithm' && 
      field && discreteValueFields.includes(field.toLowerCase());

    if (Array.isArray(value1) && Array.isArray(value2)) {
      return JSON.stringify(value1) === JSON.stringify(value2);
    } else if (Array.isArray(value1) && !Array.isArray(value2)) {
      // For discrete value fields, check if value exists in array (not a range)
      if (isDiscreteValueField) {
        // Convert both to numbers for comparison
        const numValue2 = Number(value2);
        return value1.some(arrVal => {
          const numArrVal = Number(arrVal);
          return !isNaN(numValue2) && !isNaN(numArrVal) && numValue2 === numArrVal;
        });
      }
      // Check if array represents a numeric range
      if (isNumericRange(value1)) {
        const [min, max] = value1;
        const numValue = Number(value2);
        return !isNaN(numValue) && numValue >= min && numValue <= max;
      }
      return value1.includes(value2);
    } else if (!Array.isArray(value1) && Array.isArray(value2)) {
      // For discrete value fields, check if value exists in array (not a range)
      if (isDiscreteValueField) {
        // Convert both to numbers for comparison
        const numValue1 = Number(value1);
        return value2.some(arrVal => {
          const numArrVal = Number(arrVal);
          return !isNaN(numValue1) && !isNaN(numArrVal) && numValue1 === numArrVal;
        });
      }
      // Check if array represents a numeric range
      if (isNumericRange(value2)) {
        const [min, max] = value2;
        const numValue = Number(value1);
        return !isNaN(numValue) && numValue >= min && numValue <= max;
      }
      // Fall back to existing includes logic for non-range arrays
      return value2.includes(value1);
    }
    // Number of People: float sums (3.998…) must match expected integers at 0.1.
    if (section === 'People' && String(field || '').replace(/\s+/g, ' ').trim() === 'Number of People') {
      return numbersMatchToTenth(value1, value2);
    }
    return value1 == value2;
  };

  // Helper function to get severity color based on numeric difference
  const getSeverityColor = (value: any, codeValue: any, section?: string, field?: string): string => {
    const numValue = parseFloat(value);
    const numCodeValue = parseFloat(codeValue);
    
    if (!isNaN(numValue) && !isNaN(numCodeValue) && 
        value !== null && value !== undefined && 
        codeValue !== null && codeValue !== undefined) {
      const difference = getNumericMismatchDifference(numValue, numCodeValue);
      const tolerance = getNumericMismatchTolerance(numCodeValue, section, field);
      return difference >= tolerance ? '#e74c3c' : '#ff9800';
    }
    
    return '#e74c3c';
  };

  // Helper function to find data by pattern
  const findDataByPattern = (data: any, patterns: string[]): any => {
    for (const pattern of patterns) {
      if (data[pattern]) {
        return data[pattern];
      }
    }
    return null;
  };

  // Helper function to find all AirflowNetwork keys
  const findAirflowNetworkKeys = (data: any): string[] => {
    if (!data) return [];
    return Object.keys(data).filter(key => key.startsWith('AirflowNetwork:'));
  };

  // Fields to exclude from AirflowNetwork display
  const EXCLUDED_AIRFLOWNETWORK_FIELDS = [
    'height_factor_for_opening_factor_1',
    'height_factor_for_opening_factor_2',
    'height_factor_for_opening_factor_3',
    'height_factor_for_opening_factor_4',
    "opening_factor_1",
    "opening_factor_2",
    "opening_factor_3",
    "opening_factor_4",
    "start_height_factor_for_opening_factor_1",
    "start_height_factor_for_opening_factor_2",
    "start_height_factor_for_opening_factor_3",
    "start_height_factor_for_opening_factor_4",
    "width_factor_for_opening_factor_1",
    "width_factor_for_opening_factor_2",
    "width_factor_for_opening_factor_3",
    "width_factor_for_opening_factor_4",
  ];

  // Sections to exclude from AirflowNetwork display
  const EXCLUDED_AIRFLOWNETWORK_SECTIONS = [
    'AirflowNetwork:MultiZone:ExternalNode',
    'AirflowNetwork:MultiZone:Zone',
  ];

  // Fields to exclude per section (section-specific exclusions)
  const EXCLUDED_AIRFLOWNETWORK_FIELDS_BY_SECTION: { [key: string]: string[] } = {
    'AirflowNetwork:MultiZone:Surface': [
      'leakage_component_name',
      'surface_name',
      'external_node_name',
    ],
  };

  const OBJECT_ZONE_PREFIXES = [
    'People',
    'Miscellaneous Gain',
    'Equipment Gain',
    'Computing Gain',
    'Catering Gain',
    'Process Gain',
    'General lighting',
    'Infiltration',
    'Nat Vent',
    'Ideal Loads Air System',
    'Ideal Loads Air',
  ].sort((a, b) => b.length - a.length);

  const OBJECT_ZONE_SUFFIXES = [
    'General lighting',
    'Miscellaneous gain',
    'Computing gain',
    'Equipment gain',
    'Catering gain',
    'Process gain',
    'Infiltration',
    'Nat Vent',
    'Ideal Loads Air System',
    'Ideal Loads Air',
  ].sort((a, b) => b.length - a.length);

  // DesignBuilder writes "General Lighting {zone}"; older files use "General lighting".
  const isGeneralLightingKey = (key: string): boolean =>
    String(key || '').toLowerCase().includes('general lighting');

  // Helper function to extract zone name from various key patterns (case-insensitive)
  const extractZoneName = (key: string): string | null => {
    if (!key) return null;

    const compact = key.replace(/\s+/g, ' ').trim();
    const lower = compact.toLowerCase();

    for (const label of OBJECT_ZONE_PREFIXES) {
      const prefix = `${label.toLowerCase()} `;
      if (lower.startsWith(prefix)) {
        const zone = compact.slice(prefix.length).trim();
        if (zone) return zone;
      }
    }

    for (const label of OBJECT_ZONE_SUFFIXES) {
      const needle = ` ${label.toLowerCase()}`;
      const idx = lower.indexOf(needle);
      if (idx > 0) {
        const zone = compact.slice(0, idx).trim();
        if (zone) return zone;
      }
    }

    return null;
  };

  const findThermostatKeyForZone = (thermostatData: Record<string, unknown> | null, zoneName: string): string | null => {
    if (!thermostatData || !zoneName) return null;
    return Object.keys(thermostatData).find(
      (key) => key.includes(zoneName) || key.includes(`Zone ${zoneName}`)
    ) ?? null;
  };

  // Helper function to get code value for area
  /**
   * Maps an area value to the corresponding value from valueArray based on area_range thresholds.
   * 
   * Logic:
   * - area_range is in descending order (e.g., [300,150,125,100])
   * - valueArray has one more element than area_range (e.g., [0.08,0.07,0.06,0.05,0.04])
   * - Mapping:
   *   - area > areaRange[0] → valueArray[0]
   *   - areaRange[1] < area ≤ areaRange[0] → valueArray[1]
   *   - areaRange[2] < area ≤ areaRange[1] → valueArray[2]
   *   - areaRange[3] < area ≤ areaRange[2] → valueArray[3]
   *   - area ≤ areaRange[3] → valueArray[4] (last element)
   * 
   * Example:
   * - area_range: [300,150,125,100]
   * - people_per_zone_floor_area: [0.08,0.07,0.06,0.05,0.04]
   * - area = 75 → returns 0.04 (area ≤ 100)
   * - area = 110 → returns 0.05 (100 < area ≤ 125)
   * - area = 200 → returns 0.07 (150 < area ≤ 300)
   * - area = 350 → returns 0.08 (area > 300)
   */
  const getCodeValueForArea = (area: number, areaRange: number[], valueArray: any[]): any => {
    for (let i = 0; i < areaRange.length; i++) {
      if (area > areaRange[i]) {
        return valueArray[i];
      }
    }
    return valueArray[valueArray.length - 1];
  };

  // People/Area + zone refs (9.4 / 23.1) — mirrored from api/utils/energyPlusFieldHelpers.js
  const getZoneRef = (obj: any): string | null => {
    if (!obj) return null;
    return (
      obj.zone_or_zonelist_or_space_or_spacelist_name ||
      obj.zone_or_zonelist_name ||
      obj.zone_or_space_name ||
      obj.zone_name ||
      null
    );
  };

  const getPeoplePerFloorArea = (peopleObj: any): any => {
    if (!peopleObj) return undefined;
    if (peopleObj.people_per_floor_area != null && peopleObj.people_per_floor_area !== '') {
      return peopleObj.people_per_floor_area;
    }
    if (peopleObj.people_per_zone_floor_area != null && peopleObj.people_per_zone_floor_area !== '') {
      return peopleObj.people_per_zone_floor_area;
    }
    const floorPerPerson = Number(peopleObj.floor_area_per_person);
    if (
      peopleObj.number_of_people_calculation_method === 'Area/Person' &&
      Number.isFinite(floorPerPerson) &&
      floorPerPerson > 0
    ) {
      return 1 / floorPerPerson;
    }
    return undefined;
  };

  const pickFirstDefined = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const key of keys) {
      if (obj[key] != null && obj[key] !== '') {
        return obj[key];
      }
    }
    return undefined;
  };

  /** Lights Watts/Area: 24.1+ watts_per_floor_area, 9.4–23.2 watts_per_zone_floor_area */
  const getWattsPerFloorArea = (lightsObj: any): any =>
    pickFirstDefined(lightsObj, ['watts_per_floor_area', 'watts_per_zone_floor_area']);

  /** OtherEquipment Power/Area: 24.1+ power_per_floor_area, 9.4–23.2 power_per_zone_floor_area */
  const getPowerPerFloorArea = (equipObj: any): any =>
    pickFirstDefined(equipObj, ['power_per_floor_area', 'power_per_zone_floor_area']);

  // Window Analysis Function
  const analyzeZoneWindows = useCallback((jsonData: any): WindowAnalysis | { error: string } => {
    const results: WindowAnalysis = {};
    
    const eligibleZones = Object.entries(jsonData.Zone || {})
      .filter(([name, zone]: [string, any]) => zone.part_of_total_floor_area === 'Yes')
      .map(([name]) => name);

    if (eligibleZones.length === 0) return { error: 'No eligible zones found' };

    const ventilatedZones = new Set<string>();
    Object.entries(jsonData['ZoneVentilation:DesignFlowRate'] || {}).forEach(([ventName, ventData]: [string, any]) => {
      const zoneName = getZoneRef(ventData) || ventName.split(' ')[0];
      if (eligibleZones.includes(zoneName)) {
        if (ventData.design_flow_rate > 0.0001) {
          ventilatedZones.add(zoneName);
        }
      }
    });

    const fenestrationData = jsonData['FenestrationSurface:Detailed'] ||
      jsonData['FenestrationSurface'] ||
      jsonData['Window'] ||
      jsonData['Windows'] ||
      {};

    const buildingSurfaceData = jsonData['BuildingSurface:Detailed'] ||
      jsonData['BuildingSurface'] ||
      jsonData['Wall'] ||
      jsonData['Walls'] ||
      {};

    eligibleZones.forEach((zoneName) => {
      const zone = jsonData.Zone[zoneName];
      const hasNaturalVent = ventilatedZones.has(zoneName);

      const zoneWindows = Object.entries(fenestrationData)
        .filter(([windowName, window]: [string, any]) => {
          if (!isWindowFenestration(window)) return false;
          return window.building_surface_name?.includes(zoneName) ||
            window.zone_name === zoneName ||
            windowName.includes(zoneName) ||
            (window.surface_name && window.surface_name.includes(zoneName));
        });

      const orientations: { [key: string]: number } = { North: 0, South: 0, East: 0, West: 0, Unknown: 0 };
      const windowDetails: { [key: string]: string[] } = { North: [], South: [], East: [], West: [], Unknown: [] };

      zoneWindows.forEach(([windowName, window]: [string, any]) => {
        const wallName = window.building_surface_name || window.surface_name;
        const wall = resolveBuildingWall(jsonData, buildingSurfaceData, wallName);
        const orientation = getWindowOrientation(windowName, window, wall);

        if (orientations[orientation] !== undefined) {
          orientations[orientation]++;
          windowDetails[orientation].push(windowName);
        } else {
          orientations['Unknown']++;
          windowDetails['Unknown'].push(windowName);
        }
      });

      const differentOrientations = Object.entries(orientations)
        .filter(([dir, count]) => count > 0 && dir !== 'Unknown')
        .length;

      results[zoneName] = {
        displayName: hasNaturalVent ? `${zoneName} - Natural Ventilation` : zoneName,
        floorArea: zone.floor_area,
        totalWindows: zoneWindows.length,
        orientations: orientations as { North: number; South: number; East: number; West: number; Unknown: number },
        windowDetails,
        hasNaturalVentilation: hasNaturalVent,
        differentOrientations,
      };
    });

    return results;
  }, []);

  // File handling functions
  const handleMainFileSelect = useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setMainFileName(`Selected file: ${file.name}`);
      setMainFileStatus(`Main JSON: Loaded (${file.name})`);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          setMainData(data);
        } catch (error) {
          alert('Invalid JSON file. Please check your file.');
          console.error(error);
          setMainFileStatus('Main JSON: Error loading file');
        }
      };
      reader.readAsText(file);
    }
  }, []);

  const handleCodeFileSelect = useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setCodeFileName(`Selected file: ${file.name}`);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsed = JSON.parse(e.target?.result as string);
          setCodeData(parsed);
          // Auto-detect EP schema from Code JSON (23.1 uses people_per_floor_area)
          const is231 = parsed?.objects?.People?.people_per_floor_area !== undefined
            || String(JSON.stringify(parsed?.objects?.Version || '')).includes('23.1');
          setCodeFileStatus(
            `Code JSON: Loaded (${file.name})${is231 ? ' [EP 23.1]' : ' [EP 9.4]'}`
          );
        } catch (error) {
          alert('Invalid Code JSON file. Please check your file.');
          console.error(error);
          setCodeFileStatus('Code JSON: Error loading file');
        }
      };
      reader.readAsText(file);
    }
  }, []);

  // Drag and drop handlers
  const preventDefaults = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMainDrop = (e: React.DragEvent) => {
    preventDefaults(e);
    setMainDropHighlight(false);
    const files = e.dataTransfer.files;
    handleMainFileSelect(files);
  };

  const handleCodeDrop = (e: React.DragEvent) => {
    preventDefaults(e);
    setCodeDropHighlight(false);
    const files = e.dataTransfer.files;
    handleCodeFileSelect(files);
  };

  // Set up drag and drop event listeners
  useEffect(() => {
    const mainDropArea = mainDropAreaRef.current;
    const codeDropArea = codeDropAreaRef.current;

    if (!mainDropArea || !codeDropArea) return;

    const handleMainDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMainDropHighlight(true);
    };

    const handleMainDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMainDropHighlight(false);
    };

    const handleCodeDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCodeDropHighlight(true);
    };

    const handleCodeDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCodeDropHighlight(false);
    };

    ['dragenter', 'dragover'].forEach((eventName) => {
      mainDropArea.addEventListener(eventName, handleMainDragEnter);
      codeDropArea.addEventListener(eventName, handleCodeDragEnter);
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      mainDropArea.addEventListener(eventName, handleMainDragLeave);
      codeDropArea.addEventListener(eventName, handleCodeDragLeave);
    });

    return () => {
      ['dragenter', 'dragover'].forEach((eventName) => {
        mainDropArea.removeEventListener(eventName, handleMainDragEnter);
        codeDropArea.removeEventListener(eventName, handleCodeDragEnter);
      });

      ['dragleave', 'drop'].forEach((eventName) => {
        mainDropArea.removeEventListener(eventName, handleMainDragLeave);
        codeDropArea.removeEventListener(eventName, handleCodeDragLeave);
      });
    };
  }, []);

  // Helper component to render table from object with code matching
  const renderTableFromObject = (obj: any, codeObject: any = null, sectionName?: string) => {
    if (!obj || Object.keys(obj).length === 0) {
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>
          No data available
        </Box>
      );
    }

    return (
      <StyledTable>
        <TableBody>
          {Object.entries(obj).map(([key, value]) => {
            const displayValue = value === null || value === undefined
              ? ''
              : typeof value === 'object'
              ? JSON.stringify(value)
              : String(value);

            const isMatch = codeObject && codeObject[key] !== undefined
              ? valuesMatch(value, codeObject[key], sectionName, key)
              : false;

            const CellComponent = isMatch ? CodeMatchCell : StyledTableCell;

            return (
              <TableRow key={key}>
                <StyledTableCell sx={{ width: '30%' }}>{formatKey(key)}</StyledTableCell>
                <CellComponent sx={{ width: '70%' }}>{displayValue}</CellComponent>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    );
  };

  // Extraction functions
  const extractBuildingInfo = (data: any) => {
    const buildingInfo: any = {};
    const fieldsToExtract = [
      'loads_convergence_tolerance_value',
      'maximum_number_of_warmup_days',
      'minimum_number_of_warmup_days',
      'solar_distribution',
      'temperature_convergence_tolerance_value',
      'terrain',
      'north_axis'
    ];

    if (data.Building && data.Building.Volume) {
      const buildingVolume = data.Building.Volume;
      fieldsToExtract.forEach(field => {
        if (buildingVolume[field] !== undefined) {
          buildingInfo[field] = buildingVolume[field];
        }
      });
    }
    return buildingInfo;
  };

  const extractSiteLocation = (data: any) => {
    const siteLocation: any = {};
    const fieldsToExtract = ['elevation', 'latitude', 'longitude', 'time_zone'];
    const siteLocationData = findDataByPattern(data, ['Site:Location', 'SiteLocation', 'Location']);
    if (siteLocationData) {
      const locationKey = Object.keys(siteLocationData)[0];
      const locationData = siteLocationData[locationKey];
      fieldsToExtract.forEach(field => {
        if (locationData[field] !== undefined) {
          siteLocation[field] = locationData[field];
        }
      });
    }
    return siteLocation;
  };

  const extractGroundReflectance = (data: any) => {
    const groundReflectance: any = {};
    const fieldsToExtract = [
      'january_ground_reflectance', 'february_ground_reflectance', 'march_ground_reflectance',
      'april_ground_reflectance', 'may_ground_reflectance', 'june_ground_reflectance',
      'july_ground_reflectance', 'august_ground_reflectance', 'september_ground_reflectance',
      'october_ground_reflectance', 'november_ground_reflectance', 'december_ground_reflectance'
    ];
    const groundReflectanceData = findDataByPattern(data, ['Site:GroundReflectance', 'GroundReflectance', 'Ground_Reflectance']);
    if (groundReflectanceData) {
      const reflectanceKey = Object.keys(groundReflectanceData)[0];
      const reflectanceData = groundReflectanceData[reflectanceKey];
      fieldsToExtract.forEach(field => {
        if (reflectanceData[field] !== undefined) {
          groundReflectance[field] = reflectanceData[field];
        }
      });
    }
    return groundReflectance;
  };

  const extractSnowModifier = (data: any) => {
    const snowModifier: any = {};
    const fieldsToExtract = ['daylighting_ground_reflected_solar_modifier', 'ground_reflected_solar_modifier'];
    const snowModifierData = findDataByPattern(data, [
      'Site:GroundReflectance:SnowModifier',
      'GroundReflectance:SnowModifier',
      'GroundReflectanceSnowModifier',
      'Ground_Reflectance_Snow_Modifier'
    ]);
    if (snowModifierData) {
      const modifierKey = Object.keys(snowModifierData)[0];
      const modifierData = snowModifierData[modifierKey];
      fieldsToExtract.forEach(field => {
        if (modifierData[field] !== undefined) {
          snowModifier[field] = modifierData[field];
        }
      });
    }
    return snowModifier;
  };

  const extractGroundTemperature = (data: any, type: string) => {
    const groundTemp: any = {};
    let fieldsToExtract: string[] = [];
    let patterns: string[] = [];

    if (type === 'BuildingSurface') {
      fieldsToExtract = [
        'january_ground_temperature', 'february_ground_temperature', 'march_ground_temperature',
        'april_ground_temperature', 'may_ground_temperature', 'june_ground_temperature',
        'july_ground_temperature', 'august_ground_temperature', 'september_ground_temperature',
        'october_ground_temperature', 'november_ground_temperature', 'december_ground_temperature'
      ];
      patterns = [
        'Site:GroundTemperature:BuildingSurface',
        'GroundTemperature:BuildingSurface',
        'GroundTemperatureBuildingSurface',
        'Ground_Temperature_Building_Surface'
      ];
    } else if (type === 'Deep') {
      fieldsToExtract = [
        'january_deep_ground_temperature', 'february_deep_ground_temperature', 'march_deep_ground_temperature',
        'april_deep_ground_temperature', 'may_deep_ground_temperature', 'june_deep_ground_temperature',
        'july_deep_ground_temperature', 'august_deep_ground_temperature', 'september_deep_ground_temperature',
        'october_deep_ground_temperature', 'november_deep_ground_temperature', 'december_deep_ground_temperature'
      ];
      patterns = [
        'Site:GroundTemperature:Deep',
        'GroundTemperature:Deep',
        'GroundTemperatureDeep',
        'Ground_Temperature_Deep'
      ];
    } else if (type === 'FCfactorMethod') {
      // Check if GroundFCfactorMethod is used in any surface
      const buildingSurfaceData = data['BuildingSurface:Detailed'] || data['BuildingSurface'] || {};
      const hasGroundFCfactorMethod = Object.values(buildingSurfaceData).some((surface: any) => 
        surface.outside_boundary_condition === 'GroundFCfactorMethod'
      );

      if (hasGroundFCfactorMethod) {
        fieldsToExtract = [
          'january_ground_temperature', 'february_ground_temperature', 'march_ground_temperature',
          'april_ground_temperature', 'may_ground_temperature', 'june_ground_temperature',
          'july_ground_temperature', 'august_ground_temperature', 'september_ground_temperature',
          'october_ground_temperature', 'november_ground_temperature', 'december_ground_temperature'
        ];
        patterns = [
          'Site:GroundTemperature:FCfactorMethod',
          'GroundTemperature:FCfactorMethod',
          'GroundTemperatureFCfactorMethod',
          'Ground_Temperature_FCfactor_Method'
        ];
      }
    } else if (type === 'Shallow') {
      fieldsToExtract = [
        'january_surface_ground_temperature', 'february_surface_ground_temperature', 'march_surface_ground_temperature',
        'april_surface_ground_temperature', 'may_surface_ground_temperature', 'june_surface_ground_temperature',
        'july_surface_ground_temperature', 'august_surface_ground_temperature', 'september_surface_ground_temperature',
        'october_surface_ground_temperature', 'november_surface_ground_temperature', 'december_surface_ground_temperature'
      ];
      patterns = [
        'Site:GroundTemperature:Shallow',
        'GroundTemperature:Shallow',
        'GroundTemperatureShallow',
        'Ground_Temperature_Shallow'
      ];
    }

    const groundTempData = findDataByPattern(data, patterns);
    if (groundTempData) {
      const tempKey = Object.keys(groundTempData)[0];
      const tempData = groundTempData[tempKey];
      fieldsToExtract.forEach(field => {
        if (tempData[field] !== undefined) {
          groundTemp[field] = tempData[field];
        }
      });
    }
    return groundTemp;
  };

  // Helper function to find the closest value in an array to a target number
  const findClosestValueInArray = (arr: any[], target: number): number | null => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    if (typeof target !== 'number' || isNaN(target)) return null;
    
    // Filter out non-numeric values and convert to numbers
    const numericArray = arr
      .map(val => typeof val === 'number' ? val : Number(val))
      .filter(val => !isNaN(val));
    
    if (numericArray.length === 0) return null;
    
    // Find the closest value
    let closest = numericArray[0];
    let minDiff = Math.abs(target - closest);
    
    for (let i = 1; i < numericArray.length; i++) {
      const diff = Math.abs(target - numericArray[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closest = numericArray[i];
      }
    }
    
    return closest;
  };

  const getComparisonDataForObject = (
    sectionName: string,
    zoneName: string,
    dataObj: any,
    codeObj: any
  ): ComparisonResult[] => {
    if (!dataObj) return [];
    
    return Object.entries(dataObj).map(([key, value]) => {
      const displayValue = value === null || value === undefined
        ? ''
        : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);

      let codeValue = codeObj ? codeObj[key] : undefined;

      // Handle maximum_hvac_iterations: find closest value in code array to revision value
      if (sectionName === 'ConvergenceLimits' && key === 'maximum_hvac_iterations' && 
          Array.isArray(codeValue) && typeof value === 'number' && !isNaN(value)) {
        const closestValue = findClosestValueInArray(codeValue, value);
        if (closestValue !== null) {
          codeValue = closestValue;
        }
      }

      const isMatch = codeValue !== undefined
        ? valuesMatch(value, codeValue, sectionName, key)
        : false;

      return {
        section: sectionName,
        zone: zoneName,
        field: key,
        value: value,
        codeValue: codeValue,
        isMatch,
        displayValue
      };
    });
  };

  // Get data for Site tab
  const getSiteData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    let results: ComparisonResult[] = [];

    // Building Information
    const buildingInfo = extractBuildingInfo(data);
    if (Object.keys(buildingInfo).length > 0) {
      const codeBuildingInfo = code?.objects?.Building || null;
      results = results.concat(getComparisonDataForObject('Building Information', 'Global', buildingInfo, codeBuildingInfo));
    }

    // Site:Location
    const siteLocationData = extractSiteLocation(data);
    if (Object.keys(siteLocationData).length > 0) {
       let codeSiteLocation = null;
      if (code?.objects?.['Site:Location']) {
        const codeSiteLoc = code.objects['Site:Location'];
        const firstKey = Object.keys(codeSiteLoc)[0];
        codeSiteLocation = codeSiteLoc[firstKey];
      }
      results = results.concat(getComparisonDataForObject('Site:Location', 'Global', siteLocationData, codeSiteLocation));
    }

    // Site:GroundReflectance
    const groundReflectanceData = extractGroundReflectance(data);
    if (Object.keys(groundReflectanceData).length > 0) {
      const codeGroundReflectance = code?.objects?.['Site:GroundReflectance'] || null;
      results = results.concat(getComparisonDataForObject('Site:GroundReflectance', 'Global', groundReflectanceData, codeGroundReflectance));
    }

    // Site:GroundReflectance:SnowModifier
    const snowModifierData = extractSnowModifier(data);
    if (Object.keys(snowModifierData).length > 0) {
      const codeSnowModifier = code?.objects?.['Site:GroundReflectance:SnowModifier'] || null;
      results = results.concat(getComparisonDataForObject('Site:GroundReflectance:SnowModifier', 'Global', snowModifierData, codeSnowModifier));
    }

    // Ground Temperature sections
    ['BuildingSurface', 'Deep', 'FCfactorMethod', 'Shallow'].forEach(type => {
      const tempData = extractGroundTemperature(data, type);
      if (Object.keys(tempData).length > 0) {
        const codeKey = `Site:GroundTemperature:${type}`;
        const codeTemp = code?.objects?.[codeKey] || null;
        results = results.concat(getComparisonDataForObject(`Site:GroundTemperature:${type}`, 'Global', tempData, codeTemp));
      }
    });

    return results;
  }, []);

  // Get data for Simulation tab
  const getSimulationData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    let results: ComparisonResult[] = [];
    
    const patterns = [
      { key: 'EnvironmentalImpactFactors', patterns: ['EnvironmentalImpactFactors', 'Environmental_Impact_Factors'] },
      { key: 'ConvergenceLimits', patterns: ['ConvergenceLimits', 'Convergence_Limits'] },
      { key: 'HeatBalanceAlgorithm', patterns: ['HeatBalanceAlgorithm', 'Heat_Balance_Algorithm'] },
      { key: 'RunPeriod', patterns: ['RunPeriod', 'Run_Period'] },
      { key: 'SurfaceConvectionAlgorithm:Inside', patterns: ['SurfaceConvectionAlgorithm:Inside', 'InsideConvectionAlgorithm', 'SurfaceConvectionAlgorithmInside'] },
      { key: 'SurfaceConvectionAlgorithm:Outside', patterns: ['SurfaceConvectionAlgorithm:Outside', 'OutsideConvectionAlgorithm', 'SurfaceConvectionAlgorithmOutside'] },
      { key: 'Timestep', patterns: ['Timestep', 'TimeStep', 'Time_step'] },
      { key: 'SimulationControl', patterns: ['SimulationControl', 'Simulation_Control'] },
      { key: 'ShadowCalculation', patterns: ['ShadowCalculation', 'Shadow_Calculation'] },
      { key: 'Building', patterns: ['Building', 'BuildingInfo'] },
    ];

    patterns.forEach(({ key, patterns: patternList }) => {
      const foundData = findDataByPattern(data, patternList);
      if (foundData) {
        const dataKey = Object.keys(foundData)[0];
        const codeDataObj = code?.objects?.[key] || null;
        results = results.concat(getComparisonDataForObject(key, 'Global', foundData[dataKey], codeDataObj));
      }
    });

    return results;
  }, []);

  // Shared Schedule Helpers
  const normalizeDateString = (value: any): any => {
    if (typeof value !== 'string') return value;
    
    // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
    const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
    const mmddMatch = value.match(mmddPattern);
    
    if (mmddMatch) {
      const month = parseInt(mmddMatch[2], 10);
      const day = parseInt(mmddMatch[3], 10);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (month >= 1 && month <= 12) {
        return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
      }
    }
    
    // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
    const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
    const ddMonthFullMatch = value.match(ddMonthFullPattern);
    
    if (ddMonthFullMatch) {
      const day = ddMonthFullMatch[2];
      const monthFullName = ddMonthFullMatch[3];
      const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
      if (monthIndex !== -1) {
        return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
      }
    }
    
    // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
    const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
    if (ddmonPattern.test(value)) {
      return value;
    }
    
    // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
    const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
    const timeMatch = value.match(timePattern);
    if (timeMatch) {
      const hours = parseInt(timeMatch[2], 10);
      const minutes = timeMatch[3];
      // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
      const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
      return `${timeMatch[1]}${normalizedHours}:${minutes}`;
    }
    
    // Pattern for "For: Alldays" or "For: AllDays" - normalize to "For: AllDays"
    const forPattern = /^(For:\s*)(Alldays|AllDays)$/i;
    const forMatch = value.match(forPattern);
    if (forMatch) {
      return `${forMatch[1]}AllDays`;
    }
    
    // Pattern for "For: Weekdays" / "For: Weekends" - normalize case
    const forWeekPattern = /^(For:\s*)(Weekdays|Weekends)$/i;
    const forWeekMatch = value.match(forWeekPattern);
    if (forWeekMatch) {
      const dayType = forWeekMatch[2].charAt(0).toUpperCase() + forWeekMatch[2].slice(1).toLowerCase();
      return `${forWeekMatch[1]}${dayType}`;
    }
    
    // Return unchanged for other values (numbers, etc.)
    return value;
  };

  const formatScheduleData = (dataArray: any[]): string => {
    if (!dataArray || dataArray.length === 0) return '';
    return dataArray.map((item: any) => {
      const fieldValue = item.field;
      // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
      const normalizedValue = normalizeDateString(fieldValue);
      return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
    }).join(', ');
  };

  const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
    const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
    if (byTime !== null) return byTime;
    const normalized1 = normalizeScheduleDataForCompare(dataArray1);
    const normalized2 = normalizeScheduleDataForCompare(dataArray2);
    // Require real schedule payloads — empty vs empty is not a match.
    if (!normalized1?.length || !normalized2?.length) return false;
    if (normalized1.length !== normalized2.length) return false;
    return normalized1.every((item1, index) => {
      const item2 = normalized2[index];
      // Normalize date strings before comparison
      const normalizedField1 = normalizeDateString(item1.field);
      const normalizedField2 = normalizeDateString(item2?.field);
      if (valuesMatch(normalizedField1, normalizedField2)) return true;
      return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
    });
  };

  const getPeopleData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const peopleData = findDataByPattern(data, ['People', 'Person', 'Occupancy']);
    if (!peopleData) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];

    const codePeople = code?.objects?.People || null;
    
    // Code Schedules
    const codeScheduleName = codePeople?.number_of_people_schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    const codeAirVelocityScheduleName = codePeople?.air_velocity_schedule_name;
    const codeAirVelocityScheduleData = codeAirVelocityScheduleName && codeScheduleCompact ? codeScheduleCompact[codeAirVelocityScheduleName] : null;
    const codeAirVelocityScheduleDataArray = codeAirVelocityScheduleData?.data || [];

    const codeActivityLevelScheduleName = codePeople?.activity_level_schedule_name;
    const codeActivityLevelScheduleData = codeActivityLevelScheduleName && codeScheduleCompact ? codeScheduleCompact[codeActivityLevelScheduleName] : null;
    const codeActivityLevelScheduleDataArray = codeActivityLevelScheduleData?.data || [];

    const codeWorkEfficiencyScheduleName = codePeople?.work_efficiency_schedule_name;
    const codeWorkEfficiencyScheduleData = codeWorkEfficiencyScheduleName && codeScheduleCompact ? codeScheduleCompact[codeWorkEfficiencyScheduleName] : null;
    const codeWorkEfficiencyScheduleDataArray = codeWorkEfficiencyScheduleData?.data || [];

    return Object.entries(peopleData).flatMap(([key, item]: [string, any]) => {
      const zoneName = extractZoneName(key) || key;
      let peopleEntry = item;
      if (extractZoneName(key) && peopleData) {
        const entryKey = Object.keys(peopleData).find(k => k === `People ${extractZoneName(key)}`);
        if (entryKey) peopleEntry = peopleData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'People';

      // People/Area - only show if calculation method is not "People"
      if (peopleEntry.number_of_people_calculation_method !== 'People') {
        const revisionPeoplePerArea = getPeoplePerFloorArea(peopleEntry);
        const codePeoplePerArea = getPeoplePerFloorArea(codePeople);
        const peoplePerArea = revisionPeoplePerArea === 0 ? '0' : (revisionPeoplePerArea || '');
        results.push({
          section, zone: zoneName, field: 'People/Area', value: revisionPeoplePerArea, 
          codeValue: codePeoplePerArea,
          isMatch: codePeople && valuesMatch(revisionPeoplePerArea, codePeoplePerArea, section, 'People/Area'),
          displayValue: String(peoplePerArea)
        });
      }

      // People Schedule
      const scheduleName = peopleEntry.number_of_people_schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'People Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      // Clothing Method
      results.push({
        section, zone: zoneName, field: 'Clothing Method', value: peopleEntry.clothing_insulation_calculation_method,
        codeValue: codePeople?.clothing_insulation_calculation_method,
        isMatch: codePeople && valuesMatch(peopleEntry.clothing_insulation_calculation_method, codePeople.clothing_insulation_calculation_method, section, 'Clothing Method'),
        displayValue: peopleEntry.clothing_insulation_calculation_method || ''
      });

      // Air Velocity
      const airVelocityScheduleName = peopleEntry.air_velocity_schedule_name;
      const airVelocityScheduleData = airVelocityScheduleName && scheduleCompact ? scheduleCompact[airVelocityScheduleName] : null;
      const airVelocityScheduleDataArray = airVelocityScheduleData?.data || [];
      const airVelocityScheduleDisplayValue = formatScheduleData(airVelocityScheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Air Velocity', value: airVelocityScheduleName,
        codeValue: formatScheduleData(codeAirVelocityScheduleDataArray) || codeAirVelocityScheduleName,
        isMatch: code && compareScheduleData(airVelocityScheduleDataArray, codeAirVelocityScheduleDataArray),
        displayValue: airVelocityScheduleDisplayValue || airVelocityScheduleName || ''
      });

      // Activity Level
      const activityLevelScheduleName = peopleEntry.activity_level_schedule_name;
      const activityLevelScheduleData = activityLevelScheduleName && scheduleCompact ? scheduleCompact[activityLevelScheduleName] : null;
      const activityLevelScheduleDataArray = activityLevelScheduleData?.data || [];
      const activityLevelScheduleDisplayValue = formatScheduleData(activityLevelScheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Activity Level', value: activityLevelScheduleName,
        codeValue: formatScheduleData(codeActivityLevelScheduleDataArray) || codeActivityLevelScheduleName,
        isMatch: code && compareScheduleData(activityLevelScheduleDataArray, codeActivityLevelScheduleDataArray),
        displayValue: activityLevelScheduleDisplayValue || activityLevelScheduleName || ''
      });

      // Work Efficiency
      const workEfficiencyScheduleName = peopleEntry.work_efficiency_schedule_name;
      const workEfficiencyScheduleData = workEfficiencyScheduleName && scheduleCompact ? scheduleCompact[workEfficiencyScheduleName] : null;
      const workEfficiencyScheduleDataArray = workEfficiencyScheduleData?.data || [];
      const workEfficiencyScheduleDisplayValue = formatScheduleData(workEfficiencyScheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Work Efficiency', value: workEfficiencyScheduleName,
        codeValue: formatScheduleData(codeWorkEfficiencyScheduleDataArray) || codeWorkEfficiencyScheduleName,
        isMatch: code && compareScheduleData(workEfficiencyScheduleDataArray, codeWorkEfficiencyScheduleDataArray),
        displayValue: workEfficiencyScheduleDisplayValue || workEfficiencyScheduleName || ''
      });

      return results;
    });
  }, []);

  const getLightsData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const lightsData = findDataByPattern(data, ['Lights', 'Lighting', 'Light']);
    if (!lightsData) return [];

    const filteredLightsData: any = {};
    Object.entries(lightsData).forEach(([key, value]) => {
      if (isGeneralLightingKey(key)) {
        filteredLightsData[key] = value;
      }
    });
    if (Object.keys(filteredLightsData).length === 0) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeGeneralLighting = code?.objects?.Lights?.['General lighting'] || null;
    
    const codeScheduleName = codeGeneralLighting?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    return Object.entries(filteredLightsData).flatMap(([key, item]: [string, any]) => {
      const zoneName = extractZoneName(key) || key;
      let lightsEntry = item;
      if (extractZoneName(key) && lightsData) {
        const entryKey = Object.keys(lightsData).find(k => k.includes(extractZoneName(key)!) && isGeneralLightingKey(k));
        if (entryKey) lightsEntry = lightsData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'Lights';

      // Helper function to get closest value if codeValue is array
      const getClosestCodeValue = (value: any, codeValue: any): any => {
        if (Array.isArray(codeValue) && typeof value === 'number' && !isNaN(value)) {
          const closestValue = findClosestValueInArray(codeValue, value);
          return closestValue !== null ? closestValue : codeValue;
        }
        return codeValue;
      };

      // Watts/Area
      const wattsValue = getWattsPerFloorArea(lightsEntry) === 0 ? '0' :
        (Array.isArray(getWattsPerFloorArea(lightsEntry)) ?
          getWattsPerFloorArea(lightsEntry).join(', ') :
          getWattsPerFloorArea(lightsEntry) || '');

      let wattsCodeValue = getWattsPerFloorArea(codeGeneralLighting);
      // Apply closest value logic for Watts/Area
      if (wattsCodeValue !== undefined && !Array.isArray(getWattsPerFloorArea(lightsEntry))) {
        wattsCodeValue = getClosestCodeValue(getWattsPerFloorArea(lightsEntry), wattsCodeValue);
      }

      let wattsMatch = false;
      if (codeGeneralLighting && getWattsPerFloorArea(codeGeneralLighting) !== undefined) {
        const codeValue = wattsCodeValue;
        if (Array.isArray(codeValue) && !Array.isArray(getWattsPerFloorArea(lightsEntry))) {
          wattsMatch = codeValue.includes(getWattsPerFloorArea(lightsEntry));
        } else if (Array.isArray(codeValue) && Array.isArray(getWattsPerFloorArea(lightsEntry))) {
          wattsMatch = codeValue.some((val: any) => getWattsPerFloorArea(lightsEntry).includes(val));
        } else {
          wattsMatch = valuesMatch(getWattsPerFloorArea(lightsEntry), codeValue, section, 'Watts/Area');
        }
      }

      results.push({
        section, zone: zoneName, field: 'Watts/Area', value: getWattsPerFloorArea(lightsEntry),
        codeValue: wattsCodeValue,
        isMatch: wattsMatch,
        displayValue: String(wattsValue)
      });

      // Fraction Radiant
      const fractionRadiantValue = lightsEntry.fraction_radiant;
      const fractionRadiantCodeValue = getClosestCodeValue(fractionRadiantValue, codeGeneralLighting?.fraction_radiant);
      results.push({
        section, zone: zoneName, field: 'Frac. Radiant', value: fractionRadiantValue,
        codeValue: fractionRadiantCodeValue,
        isMatch: codeGeneralLighting && valuesMatch(fractionRadiantValue, fractionRadiantCodeValue, section, 'Frac. Radiant'),
        displayValue: lightsEntry.fraction_radiant === 0 ? '0' : String(lightsEntry.fraction_radiant || '')
      });

      // Fraction Replaceable
      const fractionReplaceableValue = lightsEntry.fraction_replaceable;
      const fractionReplaceableCodeValue = getClosestCodeValue(fractionReplaceableValue, codeGeneralLighting?.fraction_replaceable);
      results.push({
        section, zone: zoneName, field: 'Frac. Replaceable', value: fractionReplaceableValue,
        codeValue: fractionReplaceableCodeValue,
        isMatch: codeGeneralLighting && valuesMatch(fractionReplaceableValue, fractionReplaceableCodeValue, section, 'Frac. Replaceable'),
        displayValue: lightsEntry.fraction_replaceable === 0 ? '0' : String(lightsEntry.fraction_replaceable || '')
      });

      // Fraction Visible
      const fractionVisibleValue = lightsEntry.fraction_visible;
      const fractionVisibleCodeValue = getClosestCodeValue(fractionVisibleValue, codeGeneralLighting?.fraction_visible);
      results.push({
        section, zone: zoneName, field: 'Frac. Visible', value: fractionVisibleValue,
        codeValue: fractionVisibleCodeValue,
        isMatch: codeGeneralLighting && valuesMatch(fractionVisibleValue, fractionVisibleCodeValue, section, 'Frac. Visible'),
        displayValue: lightsEntry.fraction_visible === 0 ? '0' : String(lightsEntry.fraction_visible || '')
      });

      // Return Air Frac.
      const returnAirFractionValue = lightsEntry.return_air_fraction;
      const returnAirFractionCodeValue = getClosestCodeValue(returnAirFractionValue, codeGeneralLighting?.return_air_fraction);
      results.push({
        section, zone: zoneName, field: 'Return Air Frac.', value: returnAirFractionValue,
        codeValue: returnAirFractionCodeValue,
        isMatch: codeGeneralLighting && valuesMatch(returnAirFractionValue, returnAirFractionCodeValue, section, 'Return Air Frac.'),
        displayValue: lightsEntry.return_air_fraction === 0 ? '0' : String(lightsEntry.return_air_fraction || '')
      });

      // Schedule
      const scheduleName = lightsEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      return results;
    });
  }, []);

  const getComputingData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const equipmentData = findDataByPattern(data, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
    if (!equipmentData) return [];

    const computingEquipment: any = {};
    for (const [key, equipment] of Object.entries(equipmentData)) {
      if (key.toLowerCase().includes('computing gain')) {
        computingEquipment[key] = equipment;
      }
    }
    if (Object.keys(computingEquipment).length === 0) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeComputing = code?.objects?.OtherEquipment?.['Computing gain'] || null;
    
    const codeScheduleName = codeComputing?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    return Object.entries(computingEquipment).flatMap(([key, item]: [string, any]) => {
      const zoneName = extractZoneName(key) || key;
      let computingEntry = item;
      if (extractZoneName(key) && equipmentData) {
        let entryKey = Object.keys(equipmentData).find(k => k === `${extractZoneName(key)} Computing gain`);
        if (!entryKey) {
          const zone = extractZoneName(key);
          entryKey = Object.keys(equipmentData).find(k =>
            !!zone && k.includes(zone) && k.toLowerCase().includes('computing gain') && !k.match(/Computing gain \d+$/i)
          );
        }
        if (entryKey) computingEntry = equipmentData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'Computing Gain';

      // Power/Area
      results.push({
        section, zone: zoneName, field: 'Power/Area', value: getPowerPerFloorArea(computingEntry),
        codeValue: getPowerPerFloorArea(codeComputing),
        isMatch: codeComputing && valuesMatch(getPowerPerFloorArea(computingEntry), getPowerPerFloorArea(codeComputing), section, 'Power/Area'),
        displayValue: getPowerPerFloorArea(computingEntry) === 0 ? '0' : String(getPowerPerFloorArea(computingEntry) || '')
      });

      // Fraction Latent
      results.push({
        section, zone: zoneName, field: 'Frac. Latent', value: computingEntry.fraction_latent,
        codeValue: codeComputing?.fraction_latent,
        isMatch: codeComputing && valuesMatch(computingEntry.fraction_latent, codeComputing.fraction_latent, section, 'Frac. Latent'),
        displayValue: computingEntry.fraction_latent === 0 ? '0' : String(computingEntry.fraction_latent || '')
      });

      // Fraction Lost
      results.push({
        section, zone: zoneName, field: 'Frac. Lost', value: computingEntry.fraction_lost,
        codeValue: codeComputing?.fraction_lost,
        isMatch: codeComputing && valuesMatch(computingEntry.fraction_lost, codeComputing.fraction_lost, section, 'Frac. Lost'),
        displayValue: computingEntry.fraction_lost === 0 ? '0' : String(computingEntry.fraction_lost || '')
      });

      // Fraction Radiant
      results.push({
        section, zone: zoneName, field: 'Frac. Radiant', value: computingEntry.fraction_radiant,
        codeValue: codeComputing?.fraction_radiant,
        isMatch: codeComputing && valuesMatch(computingEntry.fraction_radiant, codeComputing.fraction_radiant, section, 'Frac. Radiant'),
        displayValue: computingEntry.fraction_radiant === 0 ? '0' : String(computingEntry.fraction_radiant || '')
      });

      // Schedule
      const scheduleName = computingEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      return results;
    });
  }, []);

  const getEquipmentData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const equipmentData = findDataByPattern(data, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
    if (!equipmentData) return [];

    const equipmentGain: any = {};
    for (const [key, equipment] of Object.entries(equipmentData)) {
      if (key.toLowerCase().includes('equipment gain')) {
        equipmentGain[key] = equipment;
      }
    }
    if (Object.keys(equipmentGain).length === 0) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeEquipment = code?.objects?.OtherEquipment?.['Equipment gain'] || null;

    const codeScheduleName = codeEquipment?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    return Object.entries(equipmentGain).flatMap(([key, item]: [string, any]) => {
      const zoneName = extractZoneName(key) || key;
      let equipmentEntry = item;
      if (extractZoneName(key) && equipmentData) {
        const zone = extractZoneName(key);
        const entryKey = Object.keys(equipmentData).find(k =>
          !!zone && k.includes(zone) && k.toLowerCase().includes('equipment gain')
        );
        if (entryKey) equipmentEntry = equipmentData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'Equipment Gain';

      // Power/Area
      results.push({
        section, zone: zoneName, field: 'Power/Area', value: getPowerPerFloorArea(equipmentEntry),
        codeValue: getPowerPerFloorArea(codeEquipment),
        isMatch: codeEquipment && valuesMatch(getPowerPerFloorArea(equipmentEntry), getPowerPerFloorArea(codeEquipment), section, 'Power/Area'),
        displayValue: getPowerPerFloorArea(equipmentEntry) === 0 ? '0' : String(getPowerPerFloorArea(equipmentEntry) || '')
      });

      // Fraction Latent
      results.push({
        section, zone: zoneName, field: 'Frac. Latent', value: equipmentEntry.fraction_latent,
        codeValue: codeEquipment?.fraction_latent,
        isMatch: codeEquipment && valuesMatch(equipmentEntry.fraction_latent, codeEquipment.fraction_latent, section, 'Frac. Latent'),
        displayValue: equipmentEntry.fraction_latent === 0 ? '0' : String(equipmentEntry.fraction_latent || '')
      });

      // Fraction Lost
      results.push({
        section, zone: zoneName, field: 'Frac. Lost', value: equipmentEntry.fraction_lost,
        codeValue: codeEquipment?.fraction_lost,
        isMatch: codeEquipment && valuesMatch(equipmentEntry.fraction_lost, codeEquipment.fraction_lost, section, 'Frac. Lost'),
        displayValue: equipmentEntry.fraction_lost === 0 ? '0' : String(equipmentEntry.fraction_lost || '')
      });

      // Fraction Radiant
      results.push({
        section, zone: zoneName, field: 'Frac. Radiant', value: equipmentEntry.fraction_radiant,
        codeValue: codeEquipment?.fraction_radiant,
        isMatch: codeEquipment && valuesMatch(equipmentEntry.fraction_radiant, codeEquipment.fraction_radiant, section, 'Frac. Radiant'),
        displayValue: equipmentEntry.fraction_radiant === 0 ? '0' : String(equipmentEntry.fraction_radiant || '')
      });

      // Schedule
      const scheduleName = equipmentEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      return results;
    });
  }, []);

  const getPumpData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const equipmentData = findDataByPattern(data, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
    if (!equipmentData) return [];

    const pumpEquipment: any = {};
    for (const [key, equipment] of Object.entries(equipmentData)) {
      if (key.toLowerCase().includes('pump')) {
        // Numeric design_level only (0 included). Missing/null ≠ a real ceiling fan.
        // Number(null) === 0, so null must be rejected explicitly.
        const level = (equipment as any)?.design_level;
        if (
          equipment != null &&
          level !== null &&
          level !== undefined &&
          level !== '' &&
          Number.isFinite(Number(level))
        ) {
          pumpEquipment[key] = equipment;
        }
      }
    }
    if (Object.keys(pumpEquipment).length === 0) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    
    // Get zone data for floor_area lookup
    const zoneData = findDataByPattern(data, ['Zone', 'Zones']);
    const surfaceFloorAreas = buildZoneFloorAreasFromSurfaces(data);
    
    // Find pump entry in code data - try exact match "pump etc" first, then any pump entry
    let codePump = null;
    if (code?.objects?.OtherEquipment) {
      codePump = code.objects.OtherEquipment['pump etc'] || null;
      if (!codePump) {
        // Find first entry with "pump" in key
        for (const [key, value] of Object.entries(code.objects.OtherEquipment)) {
          if (key.toLowerCase().includes('pump')) {
            codePump = value;
            break;
          }
        }
      }
    }

    const codeScheduleName = codePump?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    return Object.entries(pumpEquipment).flatMap(([key, item]: [string, any]) => {
      const zoneName = extractZoneName(key) || getZoneRef(item) || key;
      let pumpEntry = item;
      if (extractZoneName(key) && equipmentData) {
        const entryKey = Object.keys(equipmentData).find(k => 
          k.includes(extractZoneName(key)!) && k.toLowerCase().includes('pump')
        );
        if (entryKey) pumpEntry = equipmentData[entryKey];
      }

      // Get zone floor_area for Watt/Area calculation
      let floorArea = 0;
      if (zoneData && zoneName) {
        floorArea = lookupZoneFloorArea(zoneName, zoneData as Record<string, { floor_area?: unknown }>, surfaceFloorAreas);
      }

      // Calculate Watt/Area (design_level / floor_area)
      let wattPerArea = 'N/A';
      if (pumpEntry.design_level !== null && pumpEntry.design_level !== undefined && floorArea > 0) {
        wattPerArea = (pumpEntry.design_level / floorArea).toFixed(2);
      }

      const results: ComparisonResult[] = [];
      const section = 'Pump';

      // Watt (renamed from Design Level)
      results.push({
        section, zone: zoneName, field: 'Watt', value: pumpEntry.design_level,
        codeValue: codePump?.design_level,
        isMatch: codePump && valuesMatch(pumpEntry.design_level, codePump.design_level, section, 'Watt'),
        displayValue: pumpEntry.design_level === 0 ? '0' : String(pumpEntry.design_level || '')
      });

      // Watt/Area
      results.push({
        section, zone: zoneName, field: 'Watt/Area', value: wattPerArea !== 'N/A' ? parseFloat(wattPerArea) : null,
        codeValue: null,
        isMatch: false,
        displayValue: wattPerArea
      });

      // Design Level Calculation Method
      results.push({
        section, zone: zoneName, field: 'Design Level Calc Method', value: pumpEntry.design_level_calculation_method,
        codeValue: codePump?.design_level_calculation_method,
        isMatch: codePump && valuesMatch(pumpEntry.design_level_calculation_method, codePump.design_level_calculation_method, section, 'Design Level Calc Method'),
        displayValue: pumpEntry.design_level_calculation_method || ''
      });

      // Fraction Latent
      results.push({
        section, zone: zoneName, field: 'Frac. Latent', value: pumpEntry.fraction_latent,
        codeValue: codePump?.fraction_latent,
        isMatch: codePump && valuesMatch(pumpEntry.fraction_latent, codePump.fraction_latent, section, 'Frac. Latent'),
        displayValue: pumpEntry.fraction_latent === 0 ? '0' : String(pumpEntry.fraction_latent || '')
      });

      // Fraction Lost
      results.push({
        section, zone: zoneName, field: 'Frac. Lost', value: pumpEntry.fraction_lost,
        codeValue: codePump?.fraction_lost,
        isMatch: codePump && valuesMatch(pumpEntry.fraction_lost, codePump.fraction_lost, section, 'Frac. Lost'),
        displayValue: pumpEntry.fraction_lost === 0 ? '0' : String(pumpEntry.fraction_lost || '')
      });

      // Fraction Radiant
      results.push({
        section, zone: zoneName, field: 'Frac. Radiant', value: pumpEntry.fraction_radiant,
        codeValue: codePump?.fraction_radiant,
        isMatch: codePump && valuesMatch(pumpEntry.fraction_radiant, codePump.fraction_radiant, section, 'Frac. Radiant'),
        displayValue: pumpEntry.fraction_radiant === 0 ? '0' : String(pumpEntry.fraction_radiant || '')
      });

      // Fan Schedule (renamed from Schedule)
      const scheduleName = pumpEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Fan Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      // Cooling Setpoint Fan Schedule - find ThermostatSetpoint:DualSetpoint for this zone
      const thermostatData = findDataByPattern(data, ['ThermostatSetpoint:DualSetpoint']);
      let setpointScheduleName: string | null = null;
      let setpointScheduleDataArray: any[] = [];
      let setpointScheduleDisplayValue = '';
      
      if (thermostatData && zoneName) {
        const thermostatKey = findThermostatKeyForZone(thermostatData as Record<string, unknown>, zoneName);

        if (thermostatKey && (thermostatData as any)[thermostatKey]) {
          setpointScheduleName = (thermostatData as any)[thermostatKey].cooling_setpoint_temperature_schedule_name;
          if (setpointScheduleName && scheduleCompact) {
            const setpointScheduleData = (scheduleCompact as any)[setpointScheduleName];
            setpointScheduleDataArray = setpointScheduleData?.data || [];
            setpointScheduleDisplayValue = formatScheduleData(setpointScheduleDataArray);
          }
        }
      }
      
      // Get code setpoint schedule if available
      const codeThermostatData = code?.objects?.['ThermostatSetpoint:DualSetpoint'];
      let codeSetpointScheduleName: string | null = null;
      let codeSetpointScheduleDataArray: any[] = [];
      if (codeThermostatData && zoneName) {
        const codeThermostatKey = findThermostatKeyForZone(codeThermostatData as Record<string, unknown>, zoneName);
        if (codeThermostatKey && codeThermostatData[codeThermostatKey]) {
          codeSetpointScheduleName = codeThermostatData[codeThermostatKey].cooling_setpoint_temperature_schedule_name;
          if (codeSetpointScheduleName && codeScheduleCompact) {
            const codeSetpointScheduleData = codeScheduleCompact[codeSetpointScheduleName];
            codeSetpointScheduleDataArray = codeSetpointScheduleData?.data || [];
          }
        }
      }
      
      results.push({
        section, zone: zoneName, field: 'Setpoint Schedule', value: setpointScheduleName,
        codeValue: formatScheduleData(codeSetpointScheduleDataArray) || codeSetpointScheduleName,
        isMatch: code && compareScheduleData(setpointScheduleDataArray, codeSetpointScheduleDataArray),
        displayValue: setpointScheduleDisplayValue || setpointScheduleName || ''
      });

      return results;
    });
  }, []);

  const getInfiltrationData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const infiltrationData = findDataByPattern(data, ['ZoneInfiltration:DesignFlowRate', 'Infiltration', 'ZoneInfiltration']);
    if (!infiltrationData) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeInfiltration = code?.objects?.['ZoneInfiltration:DesignFlowRate'] || null;

    const codeScheduleName = codeInfiltration?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    const zoneData = findDataByPattern(data, ['Zone', 'Zones']);

    return Object.entries(infiltrationData).flatMap(([key, item]: [string, any]) => {
      const zoneNameFromKey = extractZoneName(key) || key;
      let infiltrationEntry = item;
      if (extractZoneName(key) && infiltrationData) {
        const entryKey = Object.keys(infiltrationData).find(k => 
          k.includes(extractZoneName(key)!) && k.includes('Infiltration')
        );
        if (entryKey) infiltrationEntry = infiltrationData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'Infiltration';
      const zoneName = getZoneRef(infiltrationEntry) || key;

      // Flow Rate
      const flowRateValue = infiltrationEntry.design_flow_rate === 0 ? '0' : (infiltrationEntry.design_flow_rate || '');
      const flowRateMatch = flowRateValue !== '' && codeInfiltration && codeInfiltration.design_flow_rate !== undefined 
        ? valuesMatch(infiltrationEntry.design_flow_rate, codeInfiltration.design_flow_rate, section, 'Flow Rate')
        : false;
      results.push({
        section, zone: zoneName, field: 'Flow Rate', value: infiltrationEntry.design_flow_rate,
        codeValue: codeInfiltration?.design_flow_rate,
        isMatch: flowRateMatch,
        displayValue: String(flowRateValue)
      });

      // Calc Method
      results.push({
        section, zone: zoneName, field: 'Calc Method', value: infiltrationEntry.design_flow_rate_calculation_method,
        codeValue: codeInfiltration?.design_flow_rate_calculation_method,
        isMatch: codeInfiltration && valuesMatch(infiltrationEntry.design_flow_rate_calculation_method, codeInfiltration.design_flow_rate_calculation_method, section, 'Calc Method'),
        displayValue: infiltrationEntry.design_flow_rate_calculation_method || ''
      });

      // Coefficients
      ['constant_term_coefficient', 'temperature_term_coefficient', 'velocity_term_coefficient', 'velocity_squared_term_coefficient'].forEach(field => {
         const val = infiltrationEntry[field];
         const displayVal = val === 0 ? '0' : (val || '');
         results.push({
           section, zone: zoneName, field: field, value: val,
           codeValue: codeInfiltration?.[field],
           isMatch: codeInfiltration && valuesMatch(val, codeInfiltration[field], section, field),
           displayValue: String(displayVal)
         });
      });

      // Schedule
      const scheduleName = infiltrationEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      // ACH
      let calculatedACH = 'N/A';
      if (infiltrationEntry.air_changes_per_hour !== undefined) {
        calculatedACH = String(infiltrationEntry.air_changes_per_hour);
      } else if (infiltrationEntry.design_flow_rate === 0) {
        calculatedACH = '0';
      } else if (zoneData && zoneData[zoneName] && zoneData[zoneName].volume && infiltrationEntry.design_flow_rate !== undefined && infiltrationEntry.design_flow_rate !== null) {
        calculatedACH = ((3600 * infiltrationEntry.design_flow_rate) / zoneData[zoneName].volume).toFixed(2);
      }
      results.push({
        section, zone: zoneName, field: 'ACH', value: calculatedACH,
        codeValue: codeInfiltration?.air_changes_per_hour,
        isMatch: codeInfiltration && valuesMatch(calculatedACH, codeInfiltration.air_changes_per_hour, section, 'ACH'),
        displayValue: calculatedACH
      });

      return results;
    });
  }, []);

  const getNatVentData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    if (findAirflowNetworkKeys(data).length > 0) return [];
    const natVentData = findDataByPattern(data, ['ZoneVentilation:DesignFlowRate', 'NaturalVentilation', 'ZoneVentilation']);
    if (!natVentData) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeVentilation = code?.objects?.['ZoneVentilation:DesignFlowRate'] || null;

    const codeScheduleName = codeVentilation?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    const zoneData = findDataByPattern(data, ['Zone', 'Zones']);

    return Object.entries(natVentData).flatMap(([key, item]: [string, any]) => {
      const zoneNameFromKey = extractZoneName(key) || key;
      let natVentEntry = item;
      if (extractZoneName(key) && natVentData) {
        const entryKey = Object.keys(natVentData).find(k => 
          k.includes(extractZoneName(key)!) && k.includes('Nat Vent')
        );
        if (entryKey) natVentEntry = natVentData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'Nat Vent';
      const zoneName = getZoneRef(natVentEntry) || key;

      // Check if design_flow_rate is 0 - if so, properties are irrelevant
      const isFlowRateZero = !natVentEntry.design_flow_rate || natVentEntry.design_flow_rate === 0;

      // Flow Rate
      const flowRateValue = natVentEntry.design_flow_rate === 0 ? '0' : (natVentEntry.design_flow_rate || '');
      // When design_flow_rate is 0, always consider it a match
      const flowRateMatch = isFlowRateZero 
        ? true 
        : (flowRateValue !== '' && codeVentilation && codeVentilation.design_flow_rate !== undefined 
          ? valuesMatch(natVentEntry.design_flow_rate, codeVentilation.design_flow_rate, section, 'Flow Rate')
          : false);
      results.push({
        section, zone: zoneName, field: 'Flow Rate', value: natVentEntry.design_flow_rate,
        codeValue: codeVentilation?.design_flow_rate,
        isMatch: flowRateMatch,
        displayValue: String(flowRateValue)
      });

      // Calculate ACH early to check if it's 0
      let calculatedACH = 'N/A';
      if (natVentEntry.design_flow_rate_calculation_method === 'AirChanges/Hour' && natVentEntry.air_changes_per_hour !== undefined) {
        calculatedACH = String(natVentEntry.air_changes_per_hour);
      } else if (natVentEntry.design_flow_rate === 0) {
        calculatedACH = '0';
      } else if (zoneData && zoneData[zoneName] && zoneData[zoneName].volume && natVentEntry.design_flow_rate !== undefined && natVentEntry.design_flow_rate !== null) {
        calculatedACH = ((3600 * natVentEntry.design_flow_rate) / zoneData[zoneName].volume).toFixed(2);
      }
      const isACHZero = calculatedACH === '0' || (natVentEntry.air_changes_per_hour !== undefined && natVentEntry.air_changes_per_hour === 0);
      
      // Properties are irrelevant when flow rate is 0 or ACH is 0
      const arePropertiesIrrelevant = isFlowRateZero || isACHZero;

      // Calc Method
      // When flow rate is 0 or ACH is 0, properties are irrelevant, so mark as match
      results.push({
        section, zone: zoneName, field: 'Calc Method', value: natVentEntry.design_flow_rate_calculation_method,
        codeValue: codeVentilation?.design_flow_rate_calculation_method,
        isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.design_flow_rate_calculation_method, codeVentilation.design_flow_rate_calculation_method, section, 'Calc Method')),
        displayValue: natVentEntry.design_flow_rate_calculation_method || ''
      });

      // Coefficients & Params
      const params = [
        'constant_term_coefficient', 'temperature_term_coefficient', 'velocity_term_coefficient', 'velocity_squared_term_coefficient',
        'ventilation_type', 'delta_temperature', 'fan_pressure_rise', 'fan_total_efficiency',
        'maximum_indoor_temperature', 'maximum_outdoor_temperature', 'maximum_wind_speed',
        'minimum_indoor_temperature', 'minimum_outdoor_temperature'
      ];

      params.forEach(field => {
         const val = natVentEntry[field];
         const displayVal = val === 0 ? '0' : (val || '');
         // When flow rate is 0 or ACH is 0, properties are irrelevant, so mark as match
         results.push({
           section, zone: zoneName, field: field, value: val,
           codeValue: codeVentilation?.[field],
           isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(val, codeVentilation[field], section, field)),
           displayValue: String(displayVal)
         });
      });

      // Schedule
      const scheduleName = natVentEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      // When flow rate is 0 or ACH is 0, schedule is irrelevant, so mark as match
      results.push({
        section, zone: zoneName, field: 'Schedule', value: scheduleName,
        codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
        isMatch: arePropertiesIrrelevant ? true : (code && compareScheduleData(scheduleDataArray, codeScheduleDataArray)),
        displayValue: scheduleDisplayValue || scheduleName || ''
      });

      // ACH
      // When design_flow_rate is 0, ACH should match if code file's air_changes_per_hour is null, undefined, or 0
      let achMatch = false;
      if (isFlowRateZero) {
        const codeACH = codeVentilation?.air_changes_per_hour;
        achMatch = codeACH === null || codeACH === undefined || codeACH === 0;
      } else if (isACHZero) {
        // When ACH is 0, it's a match if code file's air_changes_per_hour is null, undefined, or 0
        const codeACH = codeVentilation?.air_changes_per_hour;
        achMatch = codeACH === null || codeACH === undefined || codeACH === 0 || valuesMatch(calculatedACH, codeACH, section, 'ACH');
      } else {
        // Normal comparison
        achMatch = codeVentilation && codeVentilation.air_changes_per_hour !== null && valuesMatch(calculatedACH, codeVentilation.air_changes_per_hour, section, 'ACH');
      }
      
      results.push({
        section, zone: zoneName, field: 'ACH', value: calculatedACH,
        codeValue: codeVentilation?.air_changes_per_hour,
        isMatch: achMatch,
        displayValue: calculatedACH
      });

      // ACH is the converted flow. At 2 or more, the zone multiplier must be 1.
      const zone = zoneData && zoneData[zoneName];
      const multiplierRaw = zone?.multiplier;
      const multiplier = multiplierRaw === undefined || multiplierRaw === null || multiplierRaw === ''
        ? 1
        : Number(multiplierRaw);
      const achNumber = Number(calculatedACH);
      if (Number.isFinite(multiplier) && Number.isFinite(achNumber) && achNumber >= 2) {
        results.push({
          section, zone: zoneName, field: 'Multiplier', value: multiplier,
          codeValue: 1,
          isMatch: multiplier === 1,
          displayValue: String(multiplier)
        });
      }

      return results;
    });
  }, []);

  const getMechVentData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const mechVentData = findDataByPattern(data, ['DesignSpecification:OutdoorAir', 'MechanicalVentilation', 'OutdoorAir']);
    if (!mechVentData) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeOutdoorAir = code?.objects?.['DesignSpecification:OutdoorAir'] || null;
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeOAScheduleName = codeOutdoorAir?.outdoor_air_schedule_name;
    const codeOAScheduleData = codeOAScheduleName && codeScheduleCompact ? codeScheduleCompact[codeOAScheduleName] : null;
    const codeOAScheduleDataArray = codeOAScheduleData?.data || [];

    return Object.entries(mechVentData).flatMap(([key, item]: [string, any]) => {
      const results: ComparisonResult[] = [];
      const section = 'Mech Vent';
      const zoneName = key; // Assuming key is zone name or related

      // Check if outdoor_air_flow_air_changes_per_hour is 0 - if so, no need to check mismatch
      const isOACHZero = item.outdoor_air_flow_air_changes_per_hour === 0 || item.outdoor_air_flow_air_changes_per_hour === null || item.outdoor_air_flow_air_changes_per_hour === undefined || item.outdoor_air_flow_air_changes_per_hour === '';

      // Outdoor Air ACH
      const oaACHValue = item.outdoor_air_flow_air_changes_per_hour === 0 ? '0' : (item.outdoor_air_flow_air_changes_per_hour || '');
      // When Outdoor Air ACH is 0, always consider it a match
      const oaACHMatch = isOACHZero 
        ? true 
        : (oaACHValue !== '' && codeOutdoorAir && codeOutdoorAir.outdoor_air_flow_air_changes_per_hour !== null && codeOutdoorAir.outdoor_air_flow_air_changes_per_hour !== undefined
          ? valuesMatch(item.outdoor_air_flow_air_changes_per_hour, codeOutdoorAir.outdoor_air_flow_air_changes_per_hour, section, 'Outdoor Air ACH')
          : false);
      results.push({
        section, zone: zoneName, field: 'Outdoor Air ACH', value: item.outdoor_air_flow_air_changes_per_hour,
        codeValue: codeOutdoorAir?.outdoor_air_flow_air_changes_per_hour,
        isMatch: oaACHMatch,
        displayValue: String(oaACHValue)
      });

      // OA Method is always required. Sum (or a blank method) is a mismatch even when ACH is 0 or absent.
      results.push({
        section, zone: zoneName, field: 'OA Method', value: item.outdoor_air_method,
        codeValue: codeOutdoorAir?.outdoor_air_method,
        isMatch: !!(codeOutdoorAir && valuesMatch(item.outdoor_air_method, codeOutdoorAir.outdoor_air_method, section, 'OA Method')),
        displayValue: item.outdoor_air_method || ''
      });

      // OA Schedule — resolve Schedule:Compact on revision (same pattern as People schedules)
      const oaScheduleName = item.outdoor_air_schedule_name;
      const revOAScheduleData = oaScheduleName && scheduleCompact ? scheduleCompact[oaScheduleName] : null;
      const revOAScheduleDataArray = revOAScheduleData?.data || [];
      const oaScheduleDisplayValue = formatScheduleData(revOAScheduleDataArray);
      let oaScheduleMatch = true;
      if (!isOACHZero) {
        if (!codeOutdoorAir) {
          oaScheduleMatch = false;
        } else if (revOAScheduleDataArray.length > 0 && codeOAScheduleDataArray.length > 0) {
          oaScheduleMatch = compareScheduleData(revOAScheduleDataArray, codeOAScheduleDataArray);
        } else {
          oaScheduleMatch = valuesMatch(oaScheduleName, codeOutdoorAir.outdoor_air_schedule_name, section, 'OA Schedule');
        }
      }
      results.push({
        section, zone: zoneName, field: 'OA Schedule', value: oaScheduleName,
        codeValue: formatScheduleData(codeOAScheduleDataArray) || codeOAScheduleName,
        isMatch: oaScheduleMatch,
        displayValue: oaScheduleDisplayValue || oaScheduleName || ''
      });

      return results;
    });
  }, []);

  // Helper function to format schedule data with array replacement for Cooling Setpoint
  // Replaces arrays in code schedule with the closest value to the corresponding revision value
  const formatScheduleDataWithArrayReplacement = (revisionDataArray: any[], codeDataArray: any[], options: { closestByTime?: boolean } = {}): string => {
    if (!codeDataArray || codeDataArray.length === 0) return '';
    const closestByTime = options.closestByTime
      ? closestCodeArrayValues(revisionDataArray, codeDataArray, normalizeDateString)
      : null;
    
    // Helper to find closest value in array to target
    const findClosestValue = (arr: any[], target: number): any => {
      if (!Array.isArray(arr) || arr.length === 0) return arr;
      return arr.reduce((closest, current) => {
        const currentNum = typeof current === 'number' ? current : parseFloat(current);
        const closestNum = typeof closest === 'number' ? closest : parseFloat(closest);
        if (isNaN(currentNum)) return closest;
        if (isNaN(closestNum)) return current;
        return Math.abs(currentNum - target) < Math.abs(closestNum - target) ? current : closest;
      });
    };

    // Helper to check if a value is numeric (excluding date/time strings)
    const isNumeric = (val: any): boolean => {
      if (typeof val === 'number') return true;
      if (typeof val === 'string') {
        // Skip date/time strings like "Through:", "For:", "Until:"
        if (val.includes(':') || val.includes('Through') || val.includes('For') || val.includes('Until')) {
          return false;
        }
        return !isNaN(parseFloat(val)) && isFinite(parseFloat(val));
      }
      return false;
    };

    // Helper to find minimum numeric value in array
    const findMinimumValue = (arr: any[]): number | null => {
      if (!Array.isArray(arr) || arr.length === 0) return null;
      const numericValues = arr
        .map(val => {
          if (typeof val === 'number') return val;
          if (typeof val === 'string' && isNumeric(val)) {
            return parseFloat(val);
          }
          return null;
        })
        .filter((val): val is number => val !== null && !isNaN(val));
      
      if (numericValues.length === 0) return null;
      return Math.min(...numericValues);
    };

    return codeDataArray.map((codeItem: any, index: number) => {
      const codeFieldValue = codeItem.field;
      
      // If code has an array, try to find corresponding numeric value in revision
      if (Array.isArray(codeFieldValue)) {
        if (closestByTime && closestByTime.has(index)) {
          return String(closestByTime.get(index));
        }
        // Find corresponding revision value at same index
        const revisionItem = revisionDataArray[index];
        if (revisionItem) {
          // Normalize revision field value to ensure consistent date format (e.g., "Through: 31 Dec")
          const revisionFieldValue = normalizeDateString(revisionItem.field);
          // Check if revision value is numeric
          if (isNumeric(revisionFieldValue)) {
            const revisionNum = typeof revisionFieldValue === 'number' ? revisionFieldValue : parseFloat(revisionFieldValue);
            if (!isNaN(revisionNum)) {
              // Find closest value in array to revision value
              const closestValue = findClosestValue(codeFieldValue, revisionNum);
              return typeof closestValue === 'string' ? closestValue : String(closestValue);
            }
          }
        }
        // No revision schedule at all: keep the minimum.
        // A real schedule that doesn't line up index-by-index shows the allowed list.
        const revisionHasNumeric = (revisionDataArray || []).some((item: any) =>
          isNumeric(normalizeDateString(item?.field))
        );
        if (!revisionHasNumeric) {
          const minValue = findMinimumValue(codeFieldValue);
          return minValue !== null ? String(minValue) : codeFieldValue.join(', ');
        }
        return codeFieldValue.join(', ');
      }
      
      // Not an array, return as string
      return typeof codeFieldValue === 'string' ? codeFieldValue : String(codeFieldValue);
    }).join(', ');
  };

  const getHVACData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const hvacData = findDataByPattern(data, ['ZoneHVAC:IdealLoadsAirSystem', 'HVAC', 'IdealLoadsAirSystem']);
    if (!hvacData) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeHVAC = code?.objects?.['ZoneHVAC:IdealLoadsAirSystem'] || null;

    // Get ThermostatSetpoint:DualSetpoint data for setpoint schedule lookup
    const thermostatData = findDataByPattern(data, ['ThermostatSetpoint:DualSetpoint']);
    const codeThermostatData = code?.objects?.['ThermostatSetpoint:DualSetpoint'];

    const codeCoolingScheduleName = codeHVAC?.cooling_availability_schedule_name;
    const codeHeatingScheduleName = codeHVAC?.heating_availability_schedule_name;
    const codeCoolingScheduleData = codeCoolingScheduleName && codeScheduleCompact ? codeScheduleCompact[codeCoolingScheduleName] : null;
    const codeCoolingScheduleDataArray = codeCoolingScheduleData?.data || [];
    const codeHeatingScheduleData = codeHeatingScheduleName && codeScheduleCompact ? codeScheduleCompact[codeHeatingScheduleName] : null;
    const codeHeatingScheduleDataArray = codeHeatingScheduleData?.data || [];

    return Object.entries(hvacData).flatMap(([key, item]: [string, any]) => {
      const zoneName = extractZoneName(key) || getZoneRef(item) || key;
      let hvacEntry = item;
      const extractedZone = extractZoneName(key);
      if (extractedZone && hvacData) {
        const entryKey = Object.keys(hvacData).find(k =>
          k.includes(extractedZone) && k.toLowerCase().includes('ideal loads air')
        );
        if (entryKey) hvacEntry = hvacData[entryKey];
      }

      const results: ComparisonResult[] = [];
      const section = 'HVAC';

      const simpleFields = [
        'humidification_control_type', 'dehumidification_control_type', 'heat_recovery_type',
        'cooling_limit', 'heating_limit', 'outdoor_air_economizer_type',
        'maximum_heating_supply_air_humidity_ratio', 'maximum_heating_supply_air_temperature',
        'minimum_cooling_supply_air_humidity_ratio', 'minimum_cooling_supply_air_temperature'
      ];

      simpleFields.forEach(field => {
        const val = item[field];
        const displayVal = val === 0 ? '0' : (val || '');
        results.push({
          section, zone: zoneName, field: field, value: val,
          codeValue: codeHVAC?.[field],
          isMatch: codeHVAC && valuesMatch(val, codeHVAC[field], section, field),
          displayValue: String(displayVal)
        });
      });

      // Cooling Schedule
      const coolingScheduleName = hvacEntry?.cooling_availability_schedule_name;
      const coolingScheduleData = coolingScheduleName && scheduleCompact ? scheduleCompact[coolingScheduleName] : null;
      const coolingScheduleDataArray = coolingScheduleData?.data || [];
      const coolingDisplayValue = formatScheduleData(coolingScheduleDataArray);
      
      // Format code cooling schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeCoolingScheduleFormattedValue = formatScheduleData(codeCoolingScheduleDataArray);
      if (codeCoolingScheduleDataArray.length > 0) {
        codeCoolingScheduleFormattedValue = formatScheduleDataWithArrayReplacement(
          coolingScheduleDataArray,
          codeCoolingScheduleDataArray
        );
      }
      
      results.push({
        section, zone: zoneName, field: 'Cool Avail Schedule', value: coolingScheduleName,
        codeValue: codeCoolingScheduleFormattedValue || codeCoolingScheduleName,
        isMatch: code && compareScheduleData(coolingScheduleDataArray, codeCoolingScheduleDataArray),
        displayValue: coolingDisplayValue || coolingScheduleName || ''
      });
  
      // Heating Schedule
      const heatingScheduleName = hvacEntry?.heating_availability_schedule_name;
      const heatingScheduleData = heatingScheduleName && scheduleCompact ? scheduleCompact[heatingScheduleName] : null;
      const heatingScheduleDataArray = heatingScheduleData?.data || [];
      const heatingDisplayValue = formatScheduleData(heatingScheduleDataArray);
      
      // Format code heating schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeHeatingScheduleFormattedValue = formatScheduleData(codeHeatingScheduleDataArray);
      if (codeHeatingScheduleDataArray.length > 0) {
        codeHeatingScheduleFormattedValue = formatScheduleDataWithArrayReplacement(
          heatingScheduleDataArray,
          codeHeatingScheduleDataArray
        );
      }
      
      results.push({
        section, zone: zoneName, field: 'Heat Avail Schedule', value: heatingScheduleName,
        codeValue: codeHeatingScheduleFormattedValue || codeHeatingScheduleName,
        isMatch: code && compareScheduleData(heatingScheduleDataArray, codeHeatingScheduleDataArray),
        displayValue: heatingDisplayValue || heatingScheduleName || ''
      });

      // Look up Cooling and Heating Setpoint Schedules from ThermostatSetpoint:DualSetpoint
      let coolingSetpointScheduleName: string | null = null;
      let coolingSetpointScheduleDataArray: any[] = [];
      let coolingSetpointDisplayValue = '';

      let heatingSetpointScheduleName: string | null = null;
      let heatingSetpointScheduleDataArray: any[] = [];
      let heatingSetpointDisplayValue = '';

      if (thermostatData && zoneName) {
        const thermostatKey = findThermostatKeyForZone(thermostatData as Record<string, unknown>, zoneName);

        if (thermostatKey && (thermostatData as any)[thermostatKey]) {
          const thermostatEntry = (thermostatData as any)[thermostatKey];
          
          // Get cooling setpoint schedule
          coolingSetpointScheduleName = thermostatEntry.cooling_setpoint_temperature_schedule_name;
          if (coolingSetpointScheduleName && scheduleCompact) {
            const coolingSetpointScheduleData = (scheduleCompact as any)[coolingSetpointScheduleName];
            coolingSetpointScheduleDataArray = coolingSetpointScheduleData?.data || [];
            coolingSetpointDisplayValue = formatScheduleData(coolingSetpointScheduleDataArray);
          }

          // Get heating setpoint schedule
          heatingSetpointScheduleName = thermostatEntry.heating_setpoint_temperature_schedule_name;
          if (heatingSetpointScheduleName && scheduleCompact) {
            const heatingSetpointScheduleData = (scheduleCompact as any)[heatingSetpointScheduleName];
            heatingSetpointScheduleDataArray = heatingSetpointScheduleData?.data || [];
            heatingSetpointDisplayValue = formatScheduleData(heatingSetpointScheduleDataArray);
          }
        }
      }

      // Get code setpoint schedules if available
      // Code file has direct structure (no zone keys), use it directly
      let codeCoolingSetpointScheduleName: string | null = null;
      let codeCoolingSetpointScheduleDataArray: any[] = [];
      let codeHeatingSetpointScheduleName: string | null = null;
      let codeHeatingSetpointScheduleDataArray: any[] = [];
      if (codeThermostatData) {
        codeCoolingSetpointScheduleName = codeThermostatData.cooling_setpoint_temperature_schedule_name;
        if (codeCoolingSetpointScheduleName && codeScheduleCompact) {
          const codeCoolingSetpointScheduleData = codeScheduleCompact[codeCoolingSetpointScheduleName];
          codeCoolingSetpointScheduleDataArray = codeCoolingSetpointScheduleData?.data || [];
        }

        codeHeatingSetpointScheduleName = codeThermostatData.heating_setpoint_temperature_schedule_name;
        if (codeHeatingSetpointScheduleName && codeScheduleCompact) {
          const codeHeatingSetpointScheduleData = codeScheduleCompact[codeHeatingSetpointScheduleName];
          codeHeatingSetpointScheduleDataArray = codeHeatingSetpointScheduleData?.data || [];
        }
      }

      // Compare setpoint schedules and add to results
      const coolingSetpointMatches = code && compareScheduleData(coolingSetpointScheduleDataArray, codeCoolingSetpointScheduleDataArray);
      const heatingSetpointMatches = code && compareScheduleData(heatingSetpointScheduleDataArray, codeHeatingSetpointScheduleDataArray);

      // Format code cooling setpoint schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeCoolingSetpointFormattedValue = formatScheduleData(codeCoolingSetpointScheduleDataArray);
      if (codeCoolingSetpointScheduleDataArray.length > 0) {
        codeCoolingSetpointFormattedValue = formatScheduleDataWithArrayReplacement(
          coolingSetpointScheduleDataArray,
          codeCoolingSetpointScheduleDataArray,
          { closestByTime: true }
        );
      }

      // Format code heating setpoint schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeHeatingSetpointFormattedValue = formatScheduleData(codeHeatingSetpointScheduleDataArray);
      if (codeHeatingSetpointScheduleDataArray.length > 0) {
        codeHeatingSetpointFormattedValue = formatScheduleDataWithArrayReplacement(
          heatingSetpointScheduleDataArray,
          codeHeatingSetpointScheduleDataArray
        );
      }

      results.push({
        section, zone: zoneName, field: 'Cooling Setpoint Schedule', value: coolingSetpointScheduleName,
        codeValue: codeCoolingSetpointFormattedValue || codeCoolingSetpointScheduleName,
        isMatch: coolingSetpointMatches,
        displayValue: coolingSetpointDisplayValue || coolingSetpointScheduleName || ''
      });

      results.push({
        section, zone: zoneName, field: 'Heating Setpoint Schedule', value: heatingSetpointScheduleName,
        codeValue: codeHeatingSetpointFormattedValue || codeHeatingSetpointScheduleName,
        isMatch: heatingSetpointMatches,
        displayValue: heatingSetpointDisplayValue || heatingSetpointScheduleName || ''
      });

      return results;
    });
  }, []);

  const extractZoneNameFromExternalNodeHelper = (externalNodeName: string, zoneData: any): string | null => {
    if (!externalNodeName || !zoneData) return null;
    
    // Collect all matching zones (to find the longest match)
    const matchingZones: { zoneName: string; length: number }[] = [];
    
    for (const [zoneKey, zoneEntry] of Object.entries(zoneData)) {
      const zoneName = (zoneEntry as any)?.zone_name;
      if (zoneName && typeof zoneName === 'string' && externalNodeName.includes(zoneName)) {
        matchingZones.push({ zoneName, length: zoneName.length });
      }
    }
    
    // Return the longest matching zone name (most specific match)
    if (matchingZones.length > 0) {
      matchingZones.sort((a, b) => b.length - a.length);
      return matchingZones[0].zoneName;
    }
    
    return null;
  };

  const getAirflowNetworkData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const airflowNetworkKeys = findAirflowNetworkKeys(data);
    if (airflowNetworkKeys.length === 0) return [];

    const zoneData = findDataByPattern(data, ['AirflowNetwork:MultiZone:Zone']);
    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    
    const codeZone = code?.objects?.['AirflowNetwork:MultiZone:Zone'] || null;
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    
    const codeVentingScheduleName = codeZone?.venting_availability_schedule_name;
    const codeVentingScheduleData = codeVentingScheduleName && codeScheduleCompact ? codeScheduleCompact[codeVentingScheduleName] : null;
    const codeVentingScheduleDataArray = codeVentingScheduleData?.data || [];
    
    const codeTempScheduleName = codeZone?.ventilation_control_zone_temperature_setpoint_schedule_name;
    const codeTempScheduleData = codeTempScheduleName && codeScheduleCompact ? codeScheduleCompact[codeTempScheduleName] : null;
    const codeTempScheduleDataArray = codeTempScheduleData?.data || [];

    return airflowNetworkKeys.flatMap(key => {
      if (EXCLUDED_AIRFLOWNETWORK_SECTIONS.includes(key)) return [];
      
      const sectionData = data[key];
      if (!sectionData || Object.keys(sectionData).length === 0) return [];

      const sectionName = key.replace(/^AirflowNetwork:/, '');
      const codeSectionData = code?.objects?.[key] || null;
      const sectionExcludedFields = EXCLUDED_AIRFLOWNETWORK_FIELDS_BY_SECTION[key] || [];

      return Object.entries(sectionData)
        .filter(([itemKey, item]: [string, any]) => {
            if (key === 'AirflowNetwork:MultiZone:Surface') {
              const externalNodeName = item?.external_node_name;
              return externalNodeName && typeof externalNodeName === 'string' && externalNodeName.includes('Win');
            }
            if (key === 'AirflowNetwork:MultiZone:WindPressureCoefficientValues') {
              return itemKey && typeof itemKey === 'string' && itemKey.includes('Win');
            }
            return true;
        })
        .flatMap(([itemKey, item]: [string, any]) => {
          const zoneName = itemKey; // Using itemKey as generic identifier/zone
          
          let ventingScheduleMatch = false;
          let ventingScheduleDisplayValue = '';
          let tempScheduleMatch = false;
          let tempScheduleDisplayValue = '';
          
          if (key === 'AirflowNetwork:MultiZone:Surface' && item.external_node_name) {
            const extractedZoneName = extractZoneNameFromExternalNodeHelper(item.external_node_name, zoneData);
            if (extractedZoneName && zoneData) {
              let matchedZoneEntry: any = null;
              for (const [zoneKey, zoneEntry] of Object.entries(zoneData)) {
                if ((zoneEntry as any)?.zone_name === extractedZoneName) {
                  matchedZoneEntry = zoneEntry;
                  break;
                }
              }
              if (matchedZoneEntry) {
                if (matchedZoneEntry.venting_availability_schedule_name) {
                  const scheduleName = matchedZoneEntry.venting_availability_schedule_name;
                  const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
                  const scheduleDataArray = scheduleData?.data || [];
                  ventingScheduleDisplayValue = formatScheduleData(scheduleDataArray) || scheduleName;
                  ventingScheduleMatch = code && compareScheduleData(scheduleDataArray, codeVentingScheduleDataArray);
                }
                if (matchedZoneEntry.ventilation_control_zone_temperature_setpoint_schedule_name) {
                  const scheduleName = matchedZoneEntry.ventilation_control_zone_temperature_setpoint_schedule_name;
                  const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
                  const scheduleDataArray = scheduleData?.data || [];
                  tempScheduleDisplayValue = formatScheduleData(scheduleDataArray) || scheduleName;
                  tempScheduleMatch = code && compareScheduleData(scheduleDataArray, codeTempScheduleDataArray);
                }
              }
            }
          }

          return Object.keys(item).flatMap(param => {
             if (EXCLUDED_AIRFLOWNETWORK_FIELDS.includes(param) || sectionExcludedFields.includes(param)) return [];
             
             const value = item[param];
             let displayValue = value === null || value === undefined ? '' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
             let isMatch = code && codeSectionData && codeSectionData[param] !== undefined ? valuesMatch(value, codeSectionData[param], key, param) : false;
             let codeValue = codeSectionData?.[param];
             
             // Special handling for schedules
             if (key === 'AirflowNetwork:MultiZone:Surface' && param === 'venting_availability_schedule_name') {
                displayValue = ventingScheduleDisplayValue || displayValue;
                isMatch = ventingScheduleMatch;
                codeValue = formatScheduleData(codeVentingScheduleDataArray) || codeVentingScheduleName;
             }
             if (key === 'AirflowNetwork:MultiZone:Surface' && param === 'ventilation_control_zone_temperature_setpoint_schedule_name') {
                displayValue = tempScheduleDisplayValue || displayValue;
                isMatch = tempScheduleMatch;
                codeValue = formatScheduleData(codeTempScheduleDataArray) || codeTempScheduleName;
             }

             return [{
               section: sectionName,
               zone: zoneName,
               field: param,
               value: value,
               codeValue: codeValue,
               isMatch: isMatch,
               displayValue: displayValue
             }];
          });
        });
    });
  }, []);

  const getDaylightingData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const daylightingControlsData = findDataByPattern(data, ['Daylighting:Controls']);
    if (!daylightingControlsData) return [];

    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    const codeDaylightingControls = code?.objects?.['Daylighting:Controls'] || null;

    const codeAvailabilityScheduleName = codeDaylightingControls?.availability_schedule_name;
    const codeAvailabilityScheduleData = codeAvailabilityScheduleName && codeScheduleCompact ? codeScheduleCompact[codeAvailabilityScheduleName] : null;
    const codeAvailabilityScheduleDataArray = codeAvailabilityScheduleData?.data || [];

    // Extract illuminance_setpoint from code control_data array
    let codeIlluminanceSetpoint = null;
    if (codeDaylightingControls?.control_data && Array.isArray(codeDaylightingControls.control_data) && codeDaylightingControls.control_data.length > 0) {
      const firstControlData = codeDaylightingControls.control_data[0];
      if (firstControlData && firstControlData.illuminance_setpoint_at_reference_point !== undefined) {
        codeIlluminanceSetpoint = firstControlData.illuminance_setpoint_at_reference_point;
      }
    }

    return Object.entries(daylightingControlsData).flatMap(([key, item]: [string, any]) => {
      const zoneName = item.zone_name || key;
      const results: ComparisonResult[] = [];
      const section = 'Daylighting';

      // Availability Schedule
      const availabilityScheduleName = item.availability_schedule_name;
      const availabilityScheduleData = availabilityScheduleName && scheduleCompact ? scheduleCompact[availabilityScheduleName] : null;
      const availabilityScheduleDataArray = availabilityScheduleData?.data || [];
      const availabilityScheduleDisplayValue = formatScheduleData(availabilityScheduleDataArray);
      results.push({
        section, zone: zoneName, field: 'Availability Schedule', value: availabilityScheduleName,
        codeValue: formatScheduleData(codeAvailabilityScheduleDataArray) || codeAvailabilityScheduleName,
        isMatch: code && compareScheduleData(availabilityScheduleDataArray, codeAvailabilityScheduleDataArray),
        displayValue: availabilityScheduleDisplayValue || availabilityScheduleName || ''
      });

      // Daylighting Method
      results.push({
        section, zone: zoneName, field: 'Daylighting Method', value: item.daylighting_method,
        codeValue: codeDaylightingControls?.daylighting_method,
        isMatch: codeDaylightingControls && valuesMatch(item.daylighting_method, codeDaylightingControls.daylighting_method, section, 'Daylighting Method'),
        displayValue: item.daylighting_method || ''
      });

      // Helper function to get closest value if codeValue is array
      const getClosestCodeValue = (value: any, codeValue: any): any => {
        if (Array.isArray(codeValue) && typeof value === 'number' && !isNaN(value)) {
          const closestValue = findClosestValueInArray(codeValue, value);
          return closestValue !== null ? closestValue : codeValue;
        }
        return codeValue;
      };

      // Illuminance Setpoint (from control_data array)
      let illuminanceSetpoint = null;
      if (item.control_data && Array.isArray(item.control_data) && item.control_data.length > 0) {
        const firstControlData = item.control_data[0];
        if (firstControlData && firstControlData.illuminance_setpoint_at_reference_point !== undefined) {
          illuminanceSetpoint = firstControlData.illuminance_setpoint_at_reference_point;
        }
      }
      let finalCodeIlluminanceSetpoint = getClosestCodeValue(illuminanceSetpoint, codeIlluminanceSetpoint);
      results.push({
        section, zone: zoneName, field: 'Illuminance Setpoint', value: illuminanceSetpoint,
        codeValue: finalCodeIlluminanceSetpoint,
        isMatch: codeDaylightingControls && valuesMatch(illuminanceSetpoint, finalCodeIlluminanceSetpoint, section, 'Illuminance Setpoint'),
        displayValue: illuminanceSetpoint !== null && illuminanceSetpoint !== undefined ? String(illuminanceSetpoint) : ''
      });

      // Glare Calculation Azimuth Angle
      const glareAzimuthValue = item.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis;
      const glareAzimuthCodeValue = getClosestCodeValue(glareAzimuthValue, codeDaylightingControls?.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis);
      results.push({
        section, zone: zoneName, field: 'Glare Calculation Azimuth Angle', value: glareAzimuthValue,
        codeValue: glareAzimuthCodeValue,
        isMatch: codeDaylightingControls && valuesMatch(glareAzimuthValue, glareAzimuthCodeValue, section, 'Glare Calculation Azimuth Angle'),
        displayValue: glareAzimuthValue !== null && glareAzimuthValue !== undefined ? String(glareAzimuthValue) : ''
      });

      // Maximum Allowable Discomfort Glare Index
      const maxGlareIndexValue = item.maximum_allowable_discomfort_glare_index;
      const maxGlareIndexCodeValue = getClosestCodeValue(maxGlareIndexValue, codeDaylightingControls?.maximum_allowable_discomfort_glare_index);
      results.push({
        section, zone: zoneName, field: 'Maximum Allowable Discomfort Glare Index', value: maxGlareIndexValue,
        codeValue: maxGlareIndexCodeValue,
        isMatch: codeDaylightingControls && valuesMatch(maxGlareIndexValue, maxGlareIndexCodeValue, section, 'Maximum Allowable Discomfort Glare Index'),
        displayValue: maxGlareIndexValue !== null && maxGlareIndexValue !== undefined ? String(maxGlareIndexValue) : ''
      });

      // Number of Stepped Control Steps
      const steppedControlStepsValue = item.number_of_stepped_control_steps;
      const steppedControlStepsCodeValue = getClosestCodeValue(steppedControlStepsValue, codeDaylightingControls?.number_of_stepped_control_steps);
      results.push({
        section, zone: zoneName, field: 'Number of Stepped Control Steps', value: steppedControlStepsValue,
        codeValue: steppedControlStepsCodeValue,
        isMatch: codeDaylightingControls && valuesMatch(steppedControlStepsValue, steppedControlStepsCodeValue, section, 'Number of Stepped Control Steps'),
        displayValue: steppedControlStepsValue !== null && steppedControlStepsValue !== undefined ? String(steppedControlStepsValue) : ''
      });

      // Probability Lighting Will Be Reset
      const probabilityResetValue = item.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control;
      const probabilityResetCodeValue = getClosestCodeValue(probabilityResetValue, codeDaylightingControls?.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control);
      results.push({
        section, zone: zoneName, field: 'Probability Lighting Will Be Reset', value: probabilityResetValue,
        codeValue: probabilityResetCodeValue,
        isMatch: codeDaylightingControls && valuesMatch(probabilityResetValue, probabilityResetCodeValue, section, 'Probability Lighting Will Be Reset'),
        displayValue: probabilityResetValue !== null && probabilityResetValue !== undefined ? String(probabilityResetValue) : ''
      });

      return results;
    });
  }, []);

  const getWindowShadingData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    
    const windowMaterialShadeData = findDataByPattern(data, ['WindowMaterial:Shade']);
    const windowShadingControlData = findDataByPattern(data, ['WindowShadingControl']);
    
    const results: ComparisonResult[] = [];
    const scheduleCompact = findDataByPattern(data, ['Schedule:Compact', 'ScheduleCompact']);
    const codeScheduleCompact = code?.objects?.['Schedule:Compact'];
    
    const codeWindowMaterialShade = code?.objects?.['WindowMaterial:Shade'] || null;
    const codeWindowShadingControl = code?.objects?.['WindowShadingControl'] || null;
    
    // Process WindowMaterial:Shade entries
    if (windowMaterialShadeData) {
      Object.entries(windowMaterialShadeData).forEach(([key, item]: [string, any]) => {
        const materialName = key;
        const section = 'Window Shading';
        
        // Airflow Permeability
        const airflowPermeability = item.airflow_permeability === 0 ? '0' : (item.airflow_permeability || '');
        results.push({
          section, zone: materialName, field: 'Airflow Permeability', value: item.airflow_permeability,
          codeValue: codeWindowMaterialShade?.airflow_permeability,
          isMatch: codeWindowMaterialShade && valuesMatch(item.airflow_permeability, codeWindowMaterialShade.airflow_permeability, section, 'Airflow Permeability'),
          displayValue: String(airflowPermeability)
        });
        
        // Bottom Opening Multiplier
        const bottomOpening = item.bottom_opening_multiplier === 0 ? '0' : (item.bottom_opening_multiplier || '');
        results.push({
          section, zone: materialName, field: 'Bottom Opening Multiplier', value: item.bottom_opening_multiplier,
          codeValue: codeWindowMaterialShade?.bottom_opening_multiplier,
          isMatch: codeWindowMaterialShade && valuesMatch(item.bottom_opening_multiplier, codeWindowMaterialShade.bottom_opening_multiplier, section, 'Bottom Opening Multiplier'),
          displayValue: String(bottomOpening)
        });
        
        // Left Side Opening Multiplier
        const leftSideOpening = item.left_side_opening_multiplier === 0 ? '0' : (item.left_side_opening_multiplier || '');
        results.push({
          section, zone: materialName, field: 'Left Side Opening Multiplier', value: item.left_side_opening_multiplier,
          codeValue: codeWindowMaterialShade?.left_side_opening_multiplier,
          isMatch: codeWindowMaterialShade && valuesMatch(item.left_side_opening_multiplier, codeWindowMaterialShade.left_side_opening_multiplier, section, 'Left Side Opening Multiplier'),
          displayValue: String(leftSideOpening)
        });
        
        // Right Side Opening Multiplier
        const rightSideOpening = item.right_side_opening_multiplier === 0 ? '0' : (item.right_side_opening_multiplier || '');
        results.push({
          section, zone: materialName, field: 'Right Side Opening Multiplier', value: item.right_side_opening_multiplier,
          codeValue: codeWindowMaterialShade?.right_side_opening_multiplier,
          isMatch: codeWindowMaterialShade && valuesMatch(item.right_side_opening_multiplier, codeWindowMaterialShade.right_side_opening_multiplier, section, 'Right Side Opening Multiplier'),
          displayValue: String(rightSideOpening)
        });
        
        // Top Opening Multiplier
        const topOpening = item.top_opening_multiplier === 0 ? '0' : (item.top_opening_multiplier || '');
        results.push({
          section, zone: materialName, field: 'Top Opening Multiplier', value: item.top_opening_multiplier,
          codeValue: codeWindowMaterialShade?.top_opening_multiplier,
          isMatch: codeWindowMaterialShade && valuesMatch(item.top_opening_multiplier, codeWindowMaterialShade.top_opening_multiplier, section, 'Top Opening Multiplier'),
          displayValue: String(topOpening)
        });
      });
    }
    
    // Process WindowShadingControl entries
    if (windowShadingControlData) {
      Object.entries(windowShadingControlData).forEach(([key, item]: [string, any]) => {
        const zoneName = item.zone_name || key;
        const section = 'Window Shading';
        
        // Schedule - get schedule from revision for this specific WindowShadingControl
        const scheduleName = item.schedule_name;
        const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
        const scheduleDataArray = scheduleData?.data || [];
        const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
        
        // Get code schedule for comparison
        const codeScheduleName = codeWindowShadingControl?.schedule_name;
        const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
        const codeScheduleDataArray = codeScheduleData?.data || [];
        
        results.push({
          section, zone: zoneName, field: 'Schedule', value: scheduleName,
          codeValue: formatScheduleData(codeScheduleDataArray) || codeScheduleName,
          isMatch: code && compareScheduleData(scheduleDataArray, codeScheduleDataArray),
          displayValue: scheduleDisplayValue || scheduleName || ''
        });
      });
    }
    
    return results;
  }, []);

  const MIN_ZONE_HEIGHT_M = 3;

  const getHeightComplianceData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const zoneData = findDataByPattern(data, ['Zone', 'Zones']);
    if (!zoneData) return [];

    const results: ComparisonResult[] = [];
    const surfaceFloorAreas = buildZoneFloorAreasFromSurfaces(data);

    for (const [zoneName, zoneInfo] of Object.entries(zoneData)) {
      const pop = (zoneInfo as any).part_of_total_floor_area;
      if (pop === 'No' || pop === false) continue;

      const floorArea = getZoneFloorArea(zoneName, zoneInfo as any, surfaceFloorAreas);
      const volume = parseNumericValue((zoneInfo as any).volume);
      const height = volume != null && floorArea > 0
        ? (volume / floorArea).toFixed(2)
        : 'N/A';

      if (height === 'N/A') {
        results.push({
          section: 'Geometry',
          zone: zoneName,
          field: 'Height (m)',
          value: null,
          codeValue: MIN_ZONE_HEIGHT_M,
          isMatch: true,
          displayValue: 'N/A',
          includeInMismatchReport: true,
          heightVerification: 'verify_plans'
        } as any);
      } else {
        const h = parseFloat(height);
        results.push({
          section: 'Geometry',
          zone: zoneName,
          field: 'Height (m)',
          value: h,
          codeValue: MIN_ZONE_HEIGHT_M,
          isMatch: true,
          displayValue: height,
          includeInMismatchReport: true,
          heightVerification: 'verify_plans'
        } as any);
      }
    }

    // Sort: numeric height ascending (smallest first), then N/A rows, tie-break by zone name
    results.sort((a, b) => {
      const aNum = (a as any).displayValue !== 'N/A' && (a as any).value != null && Number.isFinite(Number((a as any).value));
      const bNum = (b as any).displayValue !== 'N/A' && (b as any).value != null && Number.isFinite(Number((b as any).value));
      if (aNum && bNum) return Number((a as any).value) - Number((b as any).value);
      if (aNum && !bNum) return -1;
      if (!aNum && bNum) return 1;
      return String((a as any).zone || '').localeCompare(String((b as any).zone || ''));
    });

    return results;
  }, []);

  // Helper to calculate block metrics (reused by Block tab and Report tab)
  const calculateBlockMetrics = (data: any, windowAnalysis: any): any => {
    const zoneData = findDataByPattern(data, ['Zone', 'Zones']);
    if (!zoneData) return {};

    const surfaceFloorAreas = buildZoneFloorAreasFromSurfaces(data);
    const blocks: any = {};
    const peopleData = findDataByPattern(data, ['People', 'Person', 'Occupancy']);
    const lightsData = findDataByPattern(data, ['Lights', 'Lighting', 'Light']);
    const computingData = findDataByPattern(data, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);

    for (const [zoneName, zoneInfo] of Object.entries(zoneData)) {
      if ((zoneInfo as any).part_of_total_floor_area === 'No' || (zoneInfo as any).part_of_total_floor_area === false) continue;

      let blockName = 'Default Block';
      if (zoneName.includes(':')) {
        blockName = zoneName.split(':')[0];
      }

      if (!blocks[blockName]) {
        blocks[blockName] = {
          zones: [],
          totalArea: 0,
          totalPeople: 0,
          totalPeoplePerArea: 0,
          totalWattsPerArea: 0,
          totalComputingPowerPerArea: 0,
          totalEquipmentPowerPerArea: 0,
          zoneCount: 0
        };
      }

      blocks[blockName].zones.push(zoneName);
      const zoneFloorArea = getZoneFloorArea(zoneName, zoneInfo as any, surfaceFloorAreas);
      blocks[blockName].totalArea += zoneFloorArea;
      blocks[blockName].zoneCount++;

      // Accumulate metrics
      if (peopleData) {
        for (const [peopleKey, peopleInfo] of Object.entries(peopleData)) {
          if (getZoneRef(peopleInfo) === zoneName) {
            const peoplePerArea = getPeoplePerFloorArea(peopleInfo) || 0;
            blocks[blockName].totalPeoplePerArea += peoplePerArea;

            const method = (peopleInfo as any).number_of_people_calculation_method;
            if (method === 'People') {
              blocks[blockName].totalPeople += (peopleInfo as any).number_of_people || 0;
            } else if (method === 'People/Area') {
              blocks[blockName].totalPeople += peoplePerArea * zoneFloorArea;
            }
            break;
          }
        }
      }

      if (lightsData) {
         for (const [lightKey, lightInfo] of Object.entries(lightsData)) {
          if (getZoneRef(lightInfo) === zoneName) {
            blocks[blockName].totalWattsPerArea += getWattsPerFloorArea(lightInfo) || 0;
            break;
          }
        }
      }

      if (computingData) {
         for (const [equipKey, equipInfo] of Object.entries(computingData)) {
           const equipName = equipKey.toLowerCase();
           if (equipName.includes('computing') && getZoneRef(equipInfo) === zoneName) {
             blocks[blockName].totalComputingPowerPerArea += getPowerPerFloorArea(equipInfo) || 0;
           }
           if (equipName.includes('equipment') && !equipName.includes('computing') && getZoneRef(equipInfo) === zoneName) {
             blocks[blockName].totalEquipmentPowerPerArea += getPowerPerFloorArea(equipInfo) || 0;
           }
        }
      }
    }
    return blocks;
  };

  const unitFamilyPresent = (data: any, family: 'people' | 'lights' | 'computing' | 'equipment' | 'infiltration' | 'hvac') => {
    const hasMap = (keys: string[]) =>
      keys.some((key) => data?.[key] && typeof data[key] === 'object' && Object.keys(data[key]).length > 0);
    if (family === 'people') return hasMap(['People', 'Person', 'Occupancy']);
    if (family === 'lights') return hasMap(['Lights', 'Lighting', 'Light']);
    if (family === 'infiltration') return hasMap(['ZoneInfiltration:DesignFlowRate', 'Infiltration', 'ZoneInfiltration']);
    if (family === 'hvac') return hasMap(['ZoneHVAC:IdealLoadsAirSystem', 'HVAC', 'IdealLoadsAirSystem']);
    const names: string[] = [];
    for (const key of ['OtherEquipment', 'Equipment', 'ElectricEquipment']) {
      if (data?.[key] && typeof data[key] === 'object') names.push(...Object.keys(data[key]));
    }
    const lower = names.map((name) => name.toLowerCase());
    if (family === 'computing') return lower.some((name) => name.includes('computing'));
    return lower.some((name) => name.includes('equipment') && !name.includes('computing'));
  };

  const getBlockData = useCallback((data: any, code: any): ComparisonResult[] => {
    if (!data) return [];
    const windowAnalysis = analyzeZoneWindows(data);
    const blocks = calculateBlockMetrics(data, windowAnalysis);
    
    return Object.entries(blocks).flatMap(([blockName, blockInfo]: [string, any]) => {
      const avgPeoplePerArea = blockInfo.totalArea > 0 ? (blockInfo.totalPeople / blockInfo.totalArea).toFixed(3) : '0';
      const avgWattsPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalWattsPerArea / blockInfo.zoneCount).toFixed(2) : '0';
      const avgComputingPowerPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalComputingPowerPerArea / blockInfo.zoneCount).toFixed(2) : '0';
      const avgEquipmentPowerPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalEquipmentPowerPerArea / blockInfo.zoneCount).toFixed(2) : '0';

      const results: ComparisonResult[] = [];
      const section = 'Unit';

      // People Compliance
      let peopleCompliance = false;
      let peopleCodeValue = null;
      if (code?.objects?.People) {
        const peopleCode = code.objects.People;
        const peoplePerAreaArray = getPeoplePerFloorArea(peopleCode);
        if (peopleCode.area_range && peoplePerAreaArray) {
          peopleCodeValue = getCodeValueForArea(blockInfo.totalArea, peopleCode.area_range, peoplePerAreaArray);
          if (parseFloat(avgPeoplePerArea) <= parseFloat(peopleCodeValue)) {
            peopleCompliance = true;
          }
        }
      }
      if (unitFamilyPresent(data, 'people')) {
      results.push({
        section, zone: blockName, field: 'People/Area', value: parseFloat(avgPeoplePerArea),
        codeValue: peopleCodeValue,
        isMatch: peopleCompliance,
        displayValue: avgPeoplePerArea
      });
      }

      // Lights Compliance
      let lightsCompliance = false;
      let lightsCodeValue = null;
      if (code?.objects?.Lights) {
        const lightsCode = code.objects.Lights;
        const generalLighting = lightsCode['General lighting'];
        const requiredWattsPerArea =
          getWattsPerFloorArea(generalLighting) ?? getWattsPerFloorArea(lightsCode);
        
        // Apply closest value logic if requiredWattsPerArea is an array
        if (Array.isArray(requiredWattsPerArea)) {
          const avgWattsPerAreaNum = parseFloat(avgWattsPerArea);
          const closestValue = findClosestValueInArray(requiredWattsPerArea, avgWattsPerAreaNum);
          if (closestValue !== null) {
            lightsCodeValue = closestValue;
            // Check compliance: if avgWattsPerArea <= closestValue, it's compliant
            if (avgWattsPerAreaNum <= closestValue) {
              lightsCompliance = true;
            }
          } else {
            lightsCodeValue = requiredWattsPerArea;
            if (requiredWattsPerArea.includes(avgWattsPerAreaNum)) lightsCompliance = true;
          }
        } else {
          lightsCodeValue = requiredWattsPerArea;
          if (requiredWattsPerArea) {
            if (parseFloat(avgWattsPerArea) <= parseFloat(requiredWattsPerArea)) lightsCompliance = true;
          }
        }
      }
      if (unitFamilyPresent(data, 'lights')) {
      results.push({
        section, zone: blockName, field: 'Lights (W/m²)', value: parseFloat(avgWattsPerArea),
        codeValue: lightsCodeValue,
        isMatch: lightsCompliance,
        displayValue: avgWattsPerArea
      });
      }

      // Computing Compliance
      let computingCompliance = false;
      let computingCodeValue = null;
      if (code?.objects?.OtherEquipment) {
        for (const [equipKey, equipInfo] of Object.entries(code.objects.OtherEquipment)) {
          if (equipKey.toLowerCase().includes('computing') && (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo)) {
             computingCodeValue = getCodeValueForArea(blockInfo.totalArea, (equipInfo as any).area_range, getPowerPerFloorArea(equipInfo));
             if (parseFloat(avgComputingPowerPerArea) <= parseFloat(computingCodeValue)) {
               computingCompliance = true;
             }
             break;
          }
        }
      }
      if (unitFamilyPresent(data, 'computing')) {
      results.push({
        section, zone: blockName, field: 'Computing (W/m²)', value: parseFloat(avgComputingPowerPerArea),
        codeValue: computingCodeValue,
        isMatch: computingCompliance,
        displayValue: avgComputingPowerPerArea
      });
      }

      // Equipment Compliance
      let equipmentCompliance = false;
      let equipmentCodeValue = null;
      if (code?.objects?.OtherEquipment) {
        for (const [equipKey, equipInfo] of Object.entries(code.objects.OtherEquipment)) {
           if (equipKey.toLowerCase().includes('equipment') && !equipKey.toLowerCase().includes('computing') && 
              (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo)) {
             equipmentCodeValue = getCodeValueForArea(blockInfo.totalArea, (equipInfo as any).area_range, getPowerPerFloorArea(equipInfo));
             if (parseFloat(avgEquipmentPowerPerArea) <= parseFloat(equipmentCodeValue)) {
               equipmentCompliance = true;
             }
             break;
          }
        }
      }
      if (unitFamilyPresent(data, 'equipment')) {
      results.push({
        section, zone: blockName, field: 'Equipment (W/m²)', value: parseFloat(avgEquipmentPowerPerArea),
        codeValue: equipmentCodeValue,
        isMatch: equipmentCompliance,
        displayValue: avgEquipmentPowerPerArea
      });
      }

      return results;
    });
  }, []);

  // Render functions for each tab
  const renderSiteTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No site data available. Load JSON to view data.</Box>;
    }

    const sections: JSX.Element[] = [];

    // Building Information
    const buildingInfo = extractBuildingInfo(mainData);
    if (Object.keys(buildingInfo).length > 0) {
      const codeBuildingInfo = codeData?.objects?.Building || null;
      sections.push(
        <Section key="building">
          <SectionHeader>Building Information</SectionHeader>
          <SectionContent>{renderTableFromObject(buildingInfo, codeBuildingInfo, 'Building Information')}</SectionContent>
        </Section>
      );
    }

    // Site:Location
    const siteLocationData = extractSiteLocation(mainData);
    if (Object.keys(siteLocationData).length > 0) {
      let codeSiteLocation = null;
      if (codeData?.objects?.['Site:Location']) {
        const codeSiteLoc = codeData.objects['Site:Location'];
        const firstKey = Object.keys(codeSiteLoc)[0];
        codeSiteLocation = codeSiteLoc[firstKey];
      }
      sections.push(
        <Section key="site-location">
          <SectionHeader>Site:Location</SectionHeader>
          <SectionContent>{renderTableFromObject(siteLocationData, codeSiteLocation, 'Site:Location')}</SectionContent>
        </Section>
      );
    }

    // Site:GroundReflectance
    const groundReflectanceData = extractGroundReflectance(mainData);
    if (Object.keys(groundReflectanceData).length > 0) {
      const codeGroundReflectance = codeData?.objects?.['Site:GroundReflectance'] || null;
      sections.push(
        <Section key="ground-reflectance">
          <SectionHeader>Site:GroundReflectance</SectionHeader>
          <SectionContent>{renderTableFromObject(groundReflectanceData, codeGroundReflectance, 'Site:GroundReflectance')}</SectionContent>
        </Section>
      );
    }

    // Site:GroundReflectance:SnowModifier
    const snowModifierData = extractSnowModifier(mainData);
    if (Object.keys(snowModifierData).length > 0) {
      const codeSnowModifier = codeData?.objects?.['Site:GroundReflectance:SnowModifier'] || null;
      sections.push(
        <Section key="snow-modifier">
          <SectionHeader>Site:GroundReflectance:SnowModifier</SectionHeader>
          <SectionContent>{renderTableFromObject(snowModifierData, codeSnowModifier, 'Site:GroundReflectance:SnowModifier')}</SectionContent>
        </Section>
      );
    }

    // Ground Temperature sections
    ['BuildingSurface', 'Deep', 'FCfactorMethod', 'Shallow'].forEach(type => {
      const tempData = extractGroundTemperature(mainData, type);
      if (Object.keys(tempData).length > 0) {
        const codeKey = `Site:GroundTemperature:${type}`;
        const codeTemp = codeData?.objects?.[codeKey] || null;
        sections.push(
          <Section key={`ground-temp-${type.toLowerCase()}`}>
            <SectionHeader>Site:GroundTemperature:{type}</SectionHeader>
            <SectionContent>{renderTableFromObject(tempData, codeTemp, `Site:GroundTemperature:${type}`)}</SectionContent>
          </Section>
        );
      }
    });

    return sections.length > 0 ? <>{sections}</> : <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No site data found in the JSON file</Box>;
  };

  const renderSimulationTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No simulation data available. Load JSON to view data.</Box>;
    }

    const sections: JSX.Element[] = [];
    const patterns = [
      { key: 'EnvironmentalImpactFactors', patterns: ['EnvironmentalImpactFactors', 'Environmental_Impact_Factors'] },
      { key: 'ConvergenceLimits', patterns: ['ConvergenceLimits', 'Convergence_Limits'] },
      { key: 'HeatBalanceAlgorithm', patterns: ['HeatBalanceAlgorithm', 'Heat_Balance_Algorithm'] },
      { key: 'RunPeriod', patterns: ['RunPeriod', 'Run_Period'] },
      { key: 'SurfaceConvectionAlgorithm:Inside', patterns: ['SurfaceConvectionAlgorithm:Inside', 'InsideConvectionAlgorithm', 'SurfaceConvectionAlgorithmInside'] },
      { key: 'SurfaceConvectionAlgorithm:Outside', patterns: ['SurfaceConvectionAlgorithm:Outside', 'OutsideConvectionAlgorithm', 'SurfaceConvectionAlgorithmOutside'] },
      { key: 'Timestep', patterns: ['Timestep', 'TimeStep', 'Time_step'] },
      { key: 'SimulationControl', patterns: ['SimulationControl', 'Simulation_Control'] },
      { key: 'ShadowCalculation', patterns: ['ShadowCalculation', 'Shadow_Calculation'] },
      { key: 'Building', patterns: ['Building', 'BuildingInfo'] },
    ];

    patterns.forEach(({ key, patterns: patternList }) => {
      const data = findDataByPattern(mainData, patternList);
      if (data) {
        const dataKey = Object.keys(data)[0];
        const codeDataObj = codeData?.objects?.[key] || null;
        sections.push(
          <Section key={key.toLowerCase().replace(/:/g, '-')}>
            <SectionHeader>{key}</SectionHeader>
            <SectionContent>{renderTableFromObject(data[dataKey], codeDataObj, key)}</SectionContent>
          </Section>
        );
      }
    });

    return sections.length > 0 ? <>{sections}</> : <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No simulation data found in the JSON file</Box>;
  };

  const renderBlockTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No block data available. Load JSON to view data.</Box>;
    }

    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
    if (!zoneData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No zone data available</Box>;
    }

    const windowAnalysis = analyzeZoneWindows(mainData);
    if ('error' in windowAnalysis && typeof windowAnalysis.error === 'string') {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>{windowAnalysis.error}</Box>;
    }

    // Group zones by block
    const blocks: any = {};
    const blockZoneDetails: any = {};

    for (const [zoneName, zoneInfo] of Object.entries(zoneData)) {
      if ((zoneInfo as any).part_of_total_floor_area === 'No' || (zoneInfo as any).part_of_total_floor_area === false) {
        continue;
      }

      let blockName = 'Default Block';
      let zoneShortName = zoneName;
      if (zoneName.includes(':')) {
        const parts = zoneName.split(':');
        blockName = parts[0];
        zoneShortName = parts.slice(1).join(':');
      }

      if (!blocks[blockName]) {
        blocks[blockName] = {
          zones: [],
          totalArea: 0,
          totalPeoplePerArea: 0,
          totalPeople: 0,
          peopleMethod: 'People/Area',
          totalWattsPerArea: 0,
          totalComputingPowerPerArea: 0,
          totalEquipmentPowerPerArea: 0,
          totalWindows: 0,
          windowOrientations: { North: 0, South: 0, East: 0, West: 0, Unknown: 0 },
          hasNaturalVentilation: false,
          differentOrientations: 0,
          zoneCount: 0
        };
        blockZoneDetails[blockName] = [];
      }

      blocks[blockName].zones.push(zoneShortName);
      const zoneFloorArea = getZoneFloorArea(zoneName, zoneInfo as any, zoneSurfaceFloorAreas);
      blocks[blockName].totalArea += zoneFloorArea;
      blocks[blockName].zoneCount++;

      if (windowAnalysis[zoneName]) {
        const zoneWindowData = windowAnalysis[zoneName];
        blocks[blockName].totalWindows += zoneWindowData.totalWindows;
        Object.keys(zoneWindowData.orientations).forEach(orientation => {
          blocks[blockName].windowOrientations[orientation] += zoneWindowData.orientations[orientation as keyof typeof zoneWindowData.orientations];
        });
        if (zoneWindowData.hasNaturalVentilation) {
          blocks[blockName].hasNaturalVentilation = true;
        }
      }

      const zoneDetail: any = {
        name: zoneName,
        area: zoneFloorArea,
        peoplePerArea: 0,
        wattsPerArea: 0,
        computingPowerPerArea: 0,
        equipmentPowerPerArea: 0,
        windows: windowAnalysis[zoneName] ? windowAnalysis[zoneName].totalWindows : 0,
        windowOrientations: windowAnalysis[zoneName] ? { ...windowAnalysis[zoneName].orientations } : { North: 0, South: 0, East: 0, West: 0, Unknown: 0 },
        hasNaturalVentilation: windowAnalysis[zoneName] ? windowAnalysis[zoneName].hasNaturalVentilation : false,
        differentOrientations: windowAnalysis[zoneName] ? windowAnalysis[zoneName].differentOrientations : 0
      };

      const peopleData = findDataByPattern(mainData, ['People', 'Person', 'Occupancy']);
      if (peopleData) {
        for (const [peopleKey, peopleInfo] of Object.entries(peopleData)) {
          if (getZoneRef(peopleInfo) === zoneName) {
            if ((peopleInfo as any).number_of_people_calculation_method === 'People') {
              blocks[blockName].peopleMethod = 'People';
              blocks[blockName].totalPeople += (peopleInfo as any).number_of_people || 0;
            } else {
              const peopleValue = getPeoplePerFloorArea(peopleInfo) || 0;
              blocks[blockName].totalPeoplePerArea += peopleValue;
              zoneDetail.peoplePerArea = peopleValue;
            }
            break;
          }
        }
      }

      const lightsData = findDataByPattern(mainData, ['Lights', 'Lighting', 'Light']);
      if (lightsData) {
        for (const [lightKey, lightInfo] of Object.entries(lightsData)) {
          if (getZoneRef(lightInfo) === zoneName) {
            const wattsValue = getWattsPerFloorArea(lightInfo) || 0;
            blocks[blockName].totalWattsPerArea += wattsValue;
            zoneDetail.wattsPerArea = wattsValue;
            break;
          }
        }
      }

      const computingData = findDataByPattern(mainData, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
      if (computingData) {
        for (const [equipKey, equipInfo] of Object.entries(computingData)) {
          if (equipKey.toLowerCase().includes('computing') && getZoneRef(equipInfo) === zoneName) {
            const computingValue = getPowerPerFloorArea(equipInfo) || 0;
            blocks[blockName].totalComputingPowerPerArea += computingValue;
            zoneDetail.computingPowerPerArea = computingValue;
            break;
          }
        }
      }

      if (computingData) {
        for (const [equipKey, equipInfo] of Object.entries(computingData)) {
          if ((equipKey.toLowerCase().includes('equipment') && !equipKey.toLowerCase().includes('computing')) &&
            getZoneRef(equipInfo) === zoneName) {
            const equipmentValue = getPowerPerFloorArea(equipInfo) || 0;
            blocks[blockName].totalEquipmentPowerPerArea += equipmentValue;
            zoneDetail.equipmentPowerPerArea = equipmentValue;
            break;
          }
        }
      }

      blockZoneDetails[blockName].push(zoneDetail);
    }

    // Calculate different orientations
    for (const [blockName, blockInfo] of Object.entries(blocks)) {
      (blockInfo as any).differentOrientations = Object.entries((blockInfo as any).windowOrientations)
        .filter(([dir, count]) => (count as number) > 0 && dir !== 'Unknown')
        .length;
    }

    // Check if any block uses "People" method to set header
    const hasPeopleMethod = Object.values(blocks).some((b: any) => b.peopleMethod === 'People');
    const peopleHeader = hasPeopleMethod ? 'People' : 'People/Area';

    return (
      <Box>
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledTableCell>Block Name</StyledTableCell>
              <StyledTableCell>Zones</StyledTableCell>
              <StyledTableCell>Zone Count</StyledTableCell>
              <StyledTableCell>Total Area (m²)</StyledTableCell>
              <StyledTableCell>{peopleHeader}</StyledTableCell>
              <StyledTableCell>Lights (W/m²)</StyledTableCell>
              <StyledTableCell>Computing (W/m²)</StyledTableCell>
              <StyledTableCell>Equipment (W/m²)</StyledTableCell>
              <StyledTableCell>Number of Windows</StyledTableCell>
              <StyledTableCell>Window Orientation</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(blocks).map(([blockName, blockInfo]: [string, any]) => {
              const isPeopleMethod = blockInfo.peopleMethod === 'People';
              const avgPeoplePerArea = isPeopleMethod 
                ? blockInfo.totalPeople 
                : (blockInfo.zoneCount > 0 ? (blockInfo.totalPeoplePerArea / blockInfo.zoneCount).toFixed(3) : '0');
              
              const avgWattsPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalWattsPerArea / blockInfo.zoneCount).toFixed(2) : '0';
              const avgComputingPowerPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalComputingPowerPerArea / blockInfo.zoneCount).toFixed(2) : '0';
              const avgEquipmentPowerPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalEquipmentPowerPerArea / blockInfo.zoneCount).toFixed(2) : '0';

              const orientationText = Object.entries(blockInfo.windowOrientations)
                .filter(([dir, count]) => (count as number) > 0 && dir !== 'Unknown')
                .map(([dir, count]) => `${dir}:${count}`)
                .join(', ') || 'None';

              // Check code compliance for People
              let peopleCompliance = false;
              if (codeData?.objects?.People) {
                const peopleCode = codeData.objects.People;
                
                if (isPeopleMethod && peopleCode.area_range && peopleCode.number_of_people) {
                   // Compliance check for "People" method (Total People vs Code Requirement based on Area)
                   const requiredPeople = getCodeValueForArea(blockInfo.totalArea, peopleCode.area_range, peopleCode.number_of_people);
                   if (blockInfo.totalPeople <= requiredPeople) {
                     peopleCompliance = true;
                   }
                } else if (!isPeopleMethod && peopleCode.area_range && getPeoplePerFloorArea(peopleCode)) {
                  // Compliance check for "People/Area" method
                  const requiredPeoplePerArea = getCodeValueForArea(blockInfo.totalArea, peopleCode.area_range, getPeoplePerFloorArea(peopleCode));
                  if (parseFloat(avgPeoplePerArea) <= parseFloat(requiredPeoplePerArea)) {
                    peopleCompliance = true;
                  }
                }
              }

              // Check code compliance for Lights (W/m²) - WITHOUT area ranges
              let lightsCompliance = false;
              if (codeData?.objects?.Lights) {
                const lightsCode = codeData.objects.Lights;
                // Check for nested "General lighting" structure
                const generalLighting = lightsCode['General lighting'];
                if (generalLighting && getWattsPerFloorArea(generalLighting) != null) {
                  const requiredWattsPerArea = getWattsPerFloorArea(generalLighting);
                  
                  // Handle both single values and arrays for code requirements
                  if (Array.isArray(requiredWattsPerArea)) {
                    // Code value is an array [2,2] - check if our value exists in the array
                    if (requiredWattsPerArea.includes(parseFloat(avgWattsPerArea))) {
                      lightsCompliance = true;
                    }
                  } else {
                    // Code value is a single number - do standard comparison
                    if (parseFloat(avgWattsPerArea) <= parseFloat(requiredWattsPerArea)) {
                      lightsCompliance = true;
                    }
                  }
                } else if (getWattsPerFloorArea(lightsCode) != null) {
                  // Fallback to direct properties
                  const requiredWattsPerArea = getWattsPerFloorArea(lightsCode);
                  
                  // Handle both single values and arrays for code requirements
                  if (Array.isArray(requiredWattsPerArea)) {
                    // Code value is an array [2,2] - check if our value exists in the array
                    if (requiredWattsPerArea.includes(parseFloat(avgWattsPerArea))) {
                      lightsCompliance = true;
                    }
                  } else {
                    // Code value is a single number - do standard comparison
                    if (parseFloat(avgWattsPerArea) <= parseFloat(requiredWattsPerArea)) {
                      lightsCompliance = true;
                    }
                  }
                }
              }

              // Check code compliance for Computing (W/m²) based on area ranges
              let computingCompliance = false;
              if (codeData?.objects?.OtherEquipment) {
                for (const [equipKey, equipInfo] of Object.entries(codeData.objects.OtherEquipment)) {
                  if (equipKey.toLowerCase().includes('computing') && 
                      (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo)) {
                    const requiredComputingPower = getCodeValueForArea(blockInfo.totalArea, (equipInfo as any).area_range, getPowerPerFloorArea(equipInfo));
                    if (parseFloat(avgComputingPowerPerArea) <= parseFloat(requiredComputingPower)) {
                      computingCompliance = true;
                    }
                    break;
                  }
                }
              }

              // Check code compliance for Equipment (W/m²) based on area ranges
              let equipmentCompliance = false;
              if (codeData?.objects?.OtherEquipment) {
                for (const [equipKey, equipInfo] of Object.entries(codeData.objects.OtherEquipment)) {
                  if (equipKey.toLowerCase().includes('equipment') && !equipKey.toLowerCase().includes('computing') &&
                      (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo)) {
                    const requiredEquipmentPower = getCodeValueForArea(blockInfo.totalArea, (equipInfo as any).area_range, getPowerPerFloorArea(equipInfo));
                    if (parseFloat(avgEquipmentPowerPerArea) <= parseFloat(requiredEquipmentPower)) {
                      equipmentCompliance = true;
                    }
                    break;
                  }
                }
              }

              const peopleMissing = !unitFamilyPresent(mainData, 'people');
              const lightsMissing = !unitFamilyPresent(mainData, 'lights');
              const computingMissing = !unitFamilyPresent(mainData, 'computing');
              const equipmentMissing = !unitFamilyPresent(mainData, 'equipment');

              // Determine cell components based on compliance
              const PeopleCell = !peopleMissing && peopleCompliance ? ComplianceMatchCell : StyledTableCell;
              const LightsCell = !lightsMissing && lightsCompliance ? ComplianceMatchCell : StyledTableCell;
              const ComputingCell = !computingMissing && computingCompliance ? ComplianceMatchCell : StyledTableCell;
              const EquipmentCell = !equipmentMissing && equipmentCompliance ? ComplianceMatchCell : StyledTableCell;

              return (
                <TableRow key={blockName}>
                  <StyledTableCell>{blockName}</StyledTableCell>
                  <StyledTableCell>{blockInfo.zones.join(', ')}</StyledTableCell>
                  <StyledTableCell>{blockInfo.zoneCount}</StyledTableCell>
                  <StyledTableCell>{formatArea(blockInfo.totalArea)}</StyledTableCell>
                  <PeopleCell>{peopleMissing ? '—' : avgPeoplePerArea}</PeopleCell>
                  <LightsCell>{lightsMissing ? '—' : avgWattsPerArea}</LightsCell>
                  <ComputingCell>{computingMissing ? '—' : avgComputingPowerPerArea}</ComputingCell>
                  <EquipmentCell>{equipmentMissing ? '—' : avgEquipmentPowerPerArea}</EquipmentCell>
                  <StyledTableCell>{blockInfo.totalWindows}</StyledTableCell>
                  <StyledTableCell>{orientationText}</StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </StyledTable>
      </Box>
    );
  };

  const renderGeometryTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No geometry data available. Load JSON to view data.</Box>;
    }

    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
    if (!zoneData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No zone data available in the JSON</Box>;
    }

    const rowEntries: { zoneName: string; zoneInfo: any; height: string; floorArea: number }[] = [];

    for (const [zoneName, zoneInfo] of Object.entries(zoneData)) {
      const pop = (zoneInfo as any).part_of_total_floor_area;
      if (pop === 'No' || pop === false) continue;

      const floorArea = getZoneFloorArea(zoneName, zoneInfo as any, zoneSurfaceFloorAreas);
      const volume = parseNumericValue((zoneInfo as any).volume);
      const height = volume != null && floorArea > 0
        ? (volume / floorArea).toFixed(2)
        : 'N/A';

      rowEntries.push({ zoneName, zoneInfo, height, floorArea });
    }

    rowEntries.sort((a, b) => {
      const hA = parseFloat(a.height);
      const hB = parseFloat(b.height);
      const aNum = a.height !== 'N/A' && Number.isFinite(hA);
      const bNum = b.height !== 'N/A' && Number.isFinite(hB);
      if (aNum && bNum) return hA - hB;
      if (aNum && !bNum) return -1;
      if (!aNum && bNum) return 1;
      return a.zoneName.localeCompare(b.zoneName);
    });

    const rows: JSX.Element[] = rowEntries.map(({ zoneName, zoneInfo, height, floorArea }) => {
      const HeightCell = height !== 'N/A' ? HeightVerificationCell : StyledTableCell;
      return (
        <TableRow key={zoneName}>
          <StyledTableCell>{zoneName}</StyledTableCell>
          <StyledTableCell>{(zoneInfo as any).part_of_total_floor_area || ''}</StyledTableCell>
          <StyledTableCell>{formatZoneFloorAreaDisplay(zoneInfo as any, floorArea)}</StyledTableCell>
          <StyledTableCell>{(zoneInfo as any).volume === 0 ? '0' : ((zoneInfo as any).volume || '')}</StyledTableCell>
          <HeightCell>{height}</HeightCell>
        </TableRow>
      );
    });

    if (rows.length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No included zones (all excluded as not part of total floor area, or no zone data).</Box>;
    }

    return (
      <Box>
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledTableCell>Zone Name</StyledTableCell>
              <StyledTableCell>Part of Total Floor Area</StyledTableCell>
              <StyledTableCell>Floor Area (m²)</StyledTableCell>
              <StyledTableCell>Volume (m³)</StyledTableCell>
              <StyledTableCell>Height (m)</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </StyledTable>
        <Section sx={{ marginTop: '20px' }}>
          <SectionHeader>Floor-to-floor height (checklist)</SectionHeader>
          <SectionContent>
            <Typography>Zones with <code>part_of_total_floor_area</code> set to No or false are excluded. Implied height = <code>Volume / Floor Area</code> when both are present.</Typography>
            <Typography>Yellow highlighting on Height (m): manual verification against architectural sections (not an automatic pass/fail).</Typography>
          </SectionContent>
        </Section>
      </Box>
    );
  };

  const renderDataTable = (data: any, headers: string[], codeDataObj: any = null, getRowData: (item: any, key: string) => any[]) => {
    if (!data || Object.keys(data).length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No data available</Box>;
    }

    return (
      <StyledTable>
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <StyledTableCell key={header}>{header}</StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(data).map(([key, item]: [string, any]) => {
            const rowData = getRowData(item, key);
            return (
              <TableRow key={key}>
                {rowData.map((cell, idx) => {
                  const { value, codeField, isMatch, codeValue } = cell;
                  const CellComponent = isMatch ? CodeMatchCell : StyledTableCell;
                  return (
                    <CellComponent key={idx}>{value}</CellComponent>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    );
  };

  const renderPeopleTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No people data available. Load JSON to view data.</Box>;
    }

    const peopleData = findDataByPattern(mainData, ['People', 'Person', 'Occupancy']);
    if (!peopleData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No people data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);
    
    // Get code schedule data for People Schedule
    const codeScheduleName = codeData?.objects?.People?.number_of_people_schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Get code schedule data for Air Velocity Schedule
    const codeAirVelocityScheduleName = codeData?.objects?.People?.air_velocity_schedule_name;
    const codeAirVelocityScheduleData = codeAirVelocityScheduleName && codeScheduleCompact ? codeScheduleCompact[codeAirVelocityScheduleName] : null;
    const codeAirVelocityScheduleDataArray = codeAirVelocityScheduleData?.data || [];

    // Get code schedule data for Activity Level Schedule
    const codeActivityLevelScheduleName = codeData?.objects?.People?.activity_level_schedule_name;
    const codeActivityLevelScheduleData = codeActivityLevelScheduleName && codeScheduleCompact ? codeScheduleCompact[codeActivityLevelScheduleName] : null;
    const codeActivityLevelScheduleDataArray = codeActivityLevelScheduleData?.data || [];

    // Get code schedule data for Work Efficiency Schedule
    const codeWorkEfficiencyScheduleName = codeData?.objects?.People?.work_efficiency_schedule_name;
    const codeWorkEfficiencyScheduleData = codeWorkEfficiencyScheduleName && codeScheduleCompact ? codeScheduleCompact[codeWorkEfficiencyScheduleName] : null;
    const codeWorkEfficiencyScheduleDataArray = codeWorkEfficiencyScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const codePeople = codeData?.objects?.People || null;
    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
    
    const headers = ['Zone', 'People Schedule', 'Clothing Method', 'Air Velocity', 'Activity Level', 'Work Efficiency'];

    // Separate zones by calculation method for Number of People and People/Area tables
    const peopleMethodZones: Array<{zoneName: string, peopleEntry: any, zoneArea: number}> = [];
    const peopleAreaMethodZones: Array<{zoneName: string, peopleEntry: any, zoneArea: number}> = [];

    if (peopleData && zoneData) {
      Object.entries(peopleData).forEach(([key, item]: [string, any]) => {
        const zoneName = extractZoneName(key) || getZoneRef(item) || key;
        let peopleEntry = item;
        if (extractZoneName(key) && peopleData) {
          const entryKey = Object.keys(peopleData).find(k => k === `People ${extractZoneName(key)}`);
          if (entryKey) peopleEntry = peopleData[entryKey];
        }

        // Find zone area
        let zoneArea = 0;
        if (zoneData) {
          zoneArea = lookupZoneFloorArea(zoneName, zoneData as Record<string, { floor_area?: unknown }>, zoneSurfaceFloorAreas);
        }

        const method = peopleEntry.number_of_people_calculation_method;
        if (method === 'People') {
          peopleMethodZones.push({ zoneName, peopleEntry, zoneArea });
        } else {
          peopleAreaMethodZones.push({ zoneName, peopleEntry, zoneArea });
        }
      });
    }

    return (
      <Box>
        {/* Number of People Table */}
        {peopleMethodZones.length > 0 && (
          <Box sx={{ marginBottom: '30px' }}>
            <Typography variant="h6" sx={{ marginBottom: '15px' }}>Number of People</Typography>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Zone</StyledTableCell>
                  <StyledTableCell>Zone Size (m²)</StyledTableCell>
                  <StyledTableCell>Number of People</StyledTableCell>
                  {codeData && <StyledTableCell>Code Value</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {peopleMethodZones.map(({ zoneName, peopleEntry, zoneArea }) => {
                  const revisionPeople = peopleEntry.number_of_people || 0;
                  
                  // Calculate expected number of people from code
                  let expectedPeople: number | string = 'N/A';
                  if (codePeople) {
                    if (codePeople.area_range && codePeople.number_of_people && Array.isArray(codePeople.number_of_people)) {
                      const requiredPeople = getCodeValueForArea(zoneArea, codePeople.area_range, codePeople.number_of_people);
                      expectedPeople = requiredPeople.toFixed(2);
                    } else if (codePeople.number_of_people && !Array.isArray(codePeople.number_of_people)) {
                      expectedPeople = String(codePeople.number_of_people);
                    }
                  }

                  return (
                    <TableRow key={zoneName}>
                      <StyledTableCell>{zoneName}</StyledTableCell>
                      <StyledTableCell>{formatArea(zoneArea)}</StyledTableCell>
                      <StyledTableCell>{revisionPeople === 0 ? '0' : String(revisionPeople)}</StyledTableCell>
                      {codeData && <StyledTableCell>{expectedPeople}</StyledTableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </Box>
        )}

        {/* People/Area Table */}
        {peopleAreaMethodZones.length > 0 && (
          <Box sx={{ marginBottom: '30px' }}>
            <Typography variant="h6" sx={{ marginBottom: '15px' }}>People/Area</Typography>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Zone</StyledTableCell>
                  <StyledTableCell>Zone Size (m²)</StyledTableCell>
                  <StyledTableCell>People/Area</StyledTableCell>
                  {codeData && <StyledTableCell>Code Value</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {peopleAreaMethodZones.map(({ zoneName, peopleEntry, zoneArea }) => {
                  const revisionPeoplePerArea = getPeoplePerFloorArea(peopleEntry) || 0;
                  
                  // Calculate expected people_per_zone_floor_area from code
                  let expectedPeoplePerArea: number | string = 'N/A';
                  if (codePeople) {
                    if (codePeople.area_range && getPeoplePerFloorArea(codePeople) && Array.isArray(getPeoplePerFloorArea(codePeople))) {
                      const requiredPeoplePerArea = getCodeValueForArea(zoneArea, codePeople.area_range, getPeoplePerFloorArea(codePeople));
                      expectedPeoplePerArea = requiredPeoplePerArea.toFixed(2);
                    } else if (getPeoplePerFloorArea(codePeople) && !Array.isArray(getPeoplePerFloorArea(codePeople))) {
                      expectedPeoplePerArea = String(getPeoplePerFloorArea(codePeople));
                    }
                  }

                  return (
                    <TableRow key={zoneName}>
                      <StyledTableCell>{zoneName}</StyledTableCell>
                      <StyledTableCell>{formatArea(zoneArea)}</StyledTableCell>
                      <StyledTableCell>{revisionPeoplePerArea === 0 ? '0' : String(revisionPeoplePerArea)}</StyledTableCell>
                      {codeData && <StyledTableCell>{expectedPeoplePerArea}</StyledTableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </Box>
        )}

        {/* Main People Table */}
        <Box sx={{ marginBottom: '30px' }}>
          <Typography variant="h6" sx={{ marginBottom: '15px' }}>People Activity</Typography>
          {renderDataTable(peopleData, headers, codePeople, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneName = extractZoneName(key);
      let peopleEntry = item;
      if (zoneName && peopleData) {
        // Find entry matching "People {zoneName}" pattern
        const entryKey = Object.keys(peopleData).find(k => k === `People ${zoneName}`);
        if (entryKey) {
          peopleEntry = peopleData[entryKey];
        }
      }

      // Look up People Schedule for this specific People entry
      const scheduleName = peopleEntry.number_of_people_schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

      // Look up Air Velocity Schedule for this specific People entry
      const airVelocityScheduleName = peopleEntry.air_velocity_schedule_name;
      const airVelocityScheduleData = airVelocityScheduleName && scheduleCompact ? scheduleCompact[airVelocityScheduleName] : null;
      const airVelocityScheduleDataArray = airVelocityScheduleData?.data || [];
      const airVelocityScheduleDisplayValue = formatScheduleData(airVelocityScheduleDataArray);
      const airVelocityScheduleMatches = codeData && compareScheduleData(airVelocityScheduleDataArray, codeAirVelocityScheduleDataArray);

      // Look up Activity Level Schedule for this specific People entry
      const activityLevelScheduleName = peopleEntry.activity_level_schedule_name;
      const activityLevelScheduleData = activityLevelScheduleName && scheduleCompact ? scheduleCompact[activityLevelScheduleName] : null;
      const activityLevelScheduleDataArray = activityLevelScheduleData?.data || [];
      const activityLevelScheduleDisplayValue = formatScheduleData(activityLevelScheduleDataArray);
      const activityLevelScheduleMatches = codeData && compareScheduleData(activityLevelScheduleDataArray, codeActivityLevelScheduleDataArray);

      // Look up Work Efficiency Schedule for this specific People entry
      const workEfficiencyScheduleName = peopleEntry.work_efficiency_schedule_name;
      const workEfficiencyScheduleData = workEfficiencyScheduleName && scheduleCompact ? scheduleCompact[workEfficiencyScheduleName] : null;
      const workEfficiencyScheduleDataArray = workEfficiencyScheduleData?.data || [];
      const workEfficiencyScheduleDisplayValue = formatScheduleData(workEfficiencyScheduleDataArray);
      const workEfficiencyScheduleMatches = codeData && compareScheduleData(workEfficiencyScheduleDataArray, codeWorkEfficiencyScheduleDataArray);

      const cells = [
        { value: getZoneRef(peopleEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: scheduleDisplayValue || peopleEntry.number_of_people_schedule_name || '', codeField: 'number_of_people_schedule_name', isMatch: scheduleMatches },
        { value: peopleEntry.clothing_insulation_calculation_method || '', codeField: 'clothing_insulation_calculation_method', isMatch: codePeople && valuesMatch(peopleEntry.clothing_insulation_calculation_method, codePeople.clothing_insulation_calculation_method, 'People', 'clothing_insulation_calculation_method') },
        { value: airVelocityScheduleDisplayValue || peopleEntry.air_velocity_schedule_name || '', codeField: 'air_velocity_schedule_name', isMatch: airVelocityScheduleMatches },
        { value: activityLevelScheduleDisplayValue || peopleEntry.activity_level_schedule_name || '', codeField: 'activity_level_schedule_name', isMatch: activityLevelScheduleMatches },
        { value: workEfficiencyScheduleDisplayValue || peopleEntry.work_efficiency_schedule_name || '', codeField: 'work_efficiency_schedule_name', isMatch: workEfficiencyScheduleMatches },
      ];
      return cells;
    })}
        </Box>
      </Box>
    );
  };

  const renderLightsTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No lights data available. Load JSON to view data.</Box>;
    }

    const lightsData = findDataByPattern(mainData, ['Lights', 'Lighting', 'Light']);
    if (!lightsData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No lights data available</Box>;
    }

    // Filter to only include objects with "General lighting" in the key
    const filteredLightsData: any = {};
    Object.entries(lightsData).forEach(([key, value]) => {
      if (isGeneralLightingKey(key)) {
        filteredLightsData[key] = value;
      }
    });

    if (Object.keys(filteredLightsData).length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No General lighting data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get code schedule data for General lighting
    const codeGeneralLighting = codeData?.objects?.Lights?.['General lighting'] || null;
    const codeScheduleName = codeGeneralLighting?.schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const headers = ['Zone', 'Watts/Area', 'Frac. Radiant', 'Frac. Replaceable', 'Frac. Visible', 'Return Air Frac.', 'Schedule'];

    return renderDataTable(filteredLightsData, headers, codeGeneralLighting, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneName = extractZoneName(key);
      let lightsEntry = item;
      if (zoneName && lightsData) {
        // Find entry matching "{zoneName} General lighting" pattern
        const entryKey = Object.keys(lightsData).find(k => k.includes(zoneName) && isGeneralLightingKey(k));
        if (entryKey) {
          lightsEntry = lightsData[entryKey];
        }
      }

      const wattsValue = getWattsPerFloorArea(lightsEntry) === 0 ? '0' :
        (Array.isArray(getWattsPerFloorArea(lightsEntry)) ?
          getWattsPerFloorArea(lightsEntry).join(', ') :
          getWattsPerFloorArea(lightsEntry) || '');

      let wattsMatch = false;
      if (codeGeneralLighting && getWattsPerFloorArea(codeGeneralLighting) !== undefined) {
        const codeValue = getWattsPerFloorArea(codeGeneralLighting);
        if (Array.isArray(codeValue) && !Array.isArray(getWattsPerFloorArea(lightsEntry))) {
          wattsMatch = codeValue.includes(getWattsPerFloorArea(lightsEntry));
        } else if (Array.isArray(codeValue) && Array.isArray(getWattsPerFloorArea(lightsEntry))) {
          wattsMatch = codeValue.some((val: any) => getWattsPerFloorArea(lightsEntry).includes(val));
        } else {
          wattsMatch = valuesMatch(getWattsPerFloorArea(lightsEntry), codeValue, 'Lights', 'Watts/Area');
        }
      }

      // Look up Schedule for this specific Lights entry
      const scheduleName = lightsEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

      return [
        { value: getZoneRef(lightsEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: wattsValue, codeField: 'watts_per_zone_floor_area', isMatch: wattsMatch },
        { value: lightsEntry.fraction_radiant === 0 ? '0' : (lightsEntry.fraction_radiant || ''), codeField: 'fraction_radiant', isMatch: codeGeneralLighting && valuesMatch(lightsEntry.fraction_radiant, codeGeneralLighting.fraction_radiant, 'Lights', 'fraction_radiant') },
        { value: lightsEntry.fraction_replaceable === 0 ? '0' : (lightsEntry.fraction_replaceable || ''), codeField: 'fraction_replaceable', isMatch: codeGeneralLighting && valuesMatch(lightsEntry.fraction_replaceable, codeGeneralLighting.fraction_replaceable, 'Lights', 'fraction_replaceable') },
        { value: lightsEntry.fraction_visible === 0 ? '0' : (lightsEntry.fraction_visible || ''), codeField: 'fraction_visible', isMatch: codeGeneralLighting && valuesMatch(lightsEntry.fraction_visible, codeGeneralLighting.fraction_visible, 'Lights', 'fraction_visible') },
        { value: lightsEntry.return_air_fraction === 0 ? '0' : (lightsEntry.return_air_fraction || ''), codeField: 'return_air_fraction', isMatch: codeGeneralLighting && valuesMatch(lightsEntry.return_air_fraction, codeGeneralLighting.return_air_fraction, 'Lights', 'return_air_fraction') },
        { value: scheduleDisplayValue || lightsEntry.schedule_name || '', codeField: 'schedule_name', isMatch: scheduleMatches },
      ];
    });
  };

  const renderComputingTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No computing gain data available. Load JSON to view data.</Box>;
    }

    const equipmentData = findDataByPattern(mainData, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
    if (!equipmentData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No computing gain data available</Box>;
    }

    // Filter to only include objects with "Computing gain" in the key
    const computingEquipment: any = {};
    for (const [key, equipment] of Object.entries(equipmentData)) {
      if (key.toLowerCase().includes('computing gain')) {
        computingEquipment[key] = equipment;
      }
    }

    if (Object.keys(computingEquipment).length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No Computing gain data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get code schedule data for Computing gain
    const codeComputing = codeData?.objects?.OtherEquipment?.['Computing gain'] || null;
    const codeScheduleName = codeComputing?.schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const headers = ['Zone', 'Power/Area', 'Frac. Latent', 'Frac. Lost', 'Frac. Radiant', 'Schedule'];
    return renderDataTable(computingEquipment, headers, codeComputing, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneName = extractZoneName(key);
      let computingEntry = item;
      if (zoneName && equipmentData) {
        // First try to find exact match "{zoneName} Computing gain"
        let entryKey = Object.keys(equipmentData).find(k => k === `${zoneName} Computing gain`);
        // If not found, try to find entry matching pattern (without number suffix)
        if (!entryKey) {
          entryKey = Object.keys(equipmentData).find(k => 
            k.includes(zoneName) && k.includes('Computing gain') && !k.match(/Computing gain \d+$/)
          );
        }
        if (entryKey) {
          computingEntry = equipmentData[entryKey];
        }
      }

      // Look up Schedule for this specific Computing entry
      const scheduleName = computingEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

      return [
        { value: getZoneRef(computingEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: getPowerPerFloorArea(computingEntry) === 0 ? '0' : (getPowerPerFloorArea(computingEntry) || ''), codeField: 'power_per_zone_floor_area', isMatch: codeComputing && valuesMatch(getPowerPerFloorArea(computingEntry), getPowerPerFloorArea(codeComputing), 'Computing Gain', 'power_per_zone_floor_area') },
        { value: computingEntry.fraction_latent === 0 ? '0' : (computingEntry.fraction_latent || ''), codeField: 'fraction_latent', isMatch: codeComputing && valuesMatch(computingEntry.fraction_latent, (codeComputing as any).fraction_latent, 'Computing Gain', 'fraction_latent') },
        { value: computingEntry.fraction_lost === 0 ? '0' : (computingEntry.fraction_lost || ''), codeField: 'fraction_lost', isMatch: codeComputing && valuesMatch(computingEntry.fraction_lost, (codeComputing as any).fraction_lost, 'Computing Gain', 'fraction_lost') },
        { value: computingEntry.fraction_radiant === 0 ? '0' : (computingEntry.fraction_radiant || ''), codeField: 'fraction_radiant', isMatch: codeComputing && valuesMatch(computingEntry.fraction_radiant, (codeComputing as any).fraction_radiant, 'Computing Gain', 'fraction_radiant') },
        { value: scheduleDisplayValue || computingEntry.schedule_name || '', codeField: 'schedule_name', isMatch: scheduleMatches },
      ];
    });
  };

  const renderEquipmentTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No equipment gain data available. Load JSON to view data.</Box>;
    }

    const equipmentData = findDataByPattern(mainData, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
    if (!equipmentData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No equipment gain data available</Box>;
    }

    // Filter to only include objects with "Equipment gain" in the key
    const equipmentGain: any = {};
    for (const [key, equipment] of Object.entries(equipmentData)) {
      if (key.toLowerCase().includes('equipment gain')) {
        equipmentGain[key] = equipment;
      }
    }

    if (Object.keys(equipmentGain).length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No Equipment gain data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get code schedule data for Equipment gain
    const codeEquipment = codeData?.objects?.OtherEquipment?.['Equipment gain'] || null;
    const codeScheduleName = codeEquipment?.schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const headers = ['Zone', 'Power/Area', 'Frac. Latent', 'Frac. Lost', 'Frac. Radiant', 'Schedule'];
    return renderDataTable(equipmentGain, headers, codeEquipment, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneName = extractZoneName(key);
      let equipmentEntry = item;
      if (zoneName && equipmentData) {
        // Find entry matching "{zoneName} Equipment gain" pattern
        const entryKey = Object.keys(equipmentData).find(k => 
          k.includes(zoneName) && k.includes('Equipment gain')
        );
        if (entryKey) {
          equipmentEntry = equipmentData[entryKey];
        }
      }

      // Look up Schedule for this specific Equipment entry
      const scheduleName = equipmentEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

      return [
        { value: getZoneRef(equipmentEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: getPowerPerFloorArea(equipmentEntry) === 0 ? '0' : (getPowerPerFloorArea(equipmentEntry) || ''), codeField: 'power_per_zone_floor_area', isMatch: codeEquipment && valuesMatch(getPowerPerFloorArea(equipmentEntry), getPowerPerFloorArea(codeEquipment), 'Equipment Gain', 'power_per_zone_floor_area') },
        { value: equipmentEntry.fraction_latent === 0 ? '0' : (equipmentEntry.fraction_latent || ''), codeField: 'fraction_latent', isMatch: codeEquipment && valuesMatch(equipmentEntry.fraction_latent, (codeEquipment as any).fraction_latent, 'Equipment Gain', 'fraction_latent') },
        { value: equipmentEntry.fraction_lost === 0 ? '0' : (equipmentEntry.fraction_lost || ''), codeField: 'fraction_lost', isMatch: codeEquipment && valuesMatch(equipmentEntry.fraction_lost, (codeEquipment as any).fraction_lost, 'Equipment Gain', 'fraction_lost') },
        { value: equipmentEntry.fraction_radiant === 0 ? '0' : (equipmentEntry.fraction_radiant || ''), codeField: 'fraction_radiant', isMatch: codeEquipment && valuesMatch(equipmentEntry.fraction_radiant, (codeEquipment as any).fraction_radiant, 'Equipment Gain', 'fraction_radiant') },
        { value: scheduleDisplayValue || equipmentEntry.schedule_name || '', codeField: 'schedule_name', isMatch: scheduleMatches },
      ];
    });
  };

  const renderPumpTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No pump data available. Load JSON to view data.</Box>;
    }

    const equipmentData = findDataByPattern(mainData, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);
    if (!equipmentData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No pump data available</Box>;
    }

    // Pump objects with a numeric design_level only (0 included for Watt/Area).
    const pumpEquipment: any = {};
    for (const [key, equipment] of Object.entries(equipmentData)) {
      if (key.toLowerCase().includes('pump')) {
        const level = (equipment as any)?.design_level;
        if (
          equipment != null &&
          level !== null &&
          level !== undefined &&
          level !== '' &&
          Number.isFinite(Number(level))
        ) {
          pumpEquipment[key] = equipment;
        }
      }
    }

    if (Object.keys(pumpEquipment).length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No Pump data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get code schedule data for Pump
    let codePump = null;
    if (codeData?.objects?.OtherEquipment) {
      codePump = codeData.objects.OtherEquipment['pump etc'] || null;
      if (!codePump) {
        // Find first entry with "pump" in key
        for (const [key, value] of Object.entries(codeData.objects.OtherEquipment)) {
          if (key.toLowerCase().includes('pump')) {
            codePump = value;
            break;
          }
        }
      }
    }
    const codeScheduleName = codePump?.schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    // Get zone data for floor_area lookup
    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);

    // Get ThermostatSetpoint:DualSetpoint data for setpoint schedule lookup
    const thermostatData = findDataByPattern(mainData, ['ThermostatSetpoint:DualSetpoint']);

    const headers = ['Zone', 'Watt', 'Watt/Area', 'Design Level Calc Method', 'Frac. Latent', 'Frac. Lost', 'Frac. Radiant', 'Fan Schedule', 'Setpoint Schedule'];
    return renderDataTable(pumpEquipment, headers, codePump, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneName = extractZoneName(key) || getZoneRef(item) || key;
      let pumpEntry = item;
      if (zoneName && equipmentData) {
        // Find entry matching "{zoneName} pump" pattern
        const entryKey = Object.keys(equipmentData).find(k => 
          k.includes(zoneName) && k.toLowerCase().includes('pump')
        );
        if (entryKey) {
          pumpEntry = equipmentData[entryKey];
        }
      }

      // Get zone floor_area for Watt/Area calculation
      let floorArea = 0;
      if (zoneData && zoneName) {
        floorArea = lookupZoneFloorArea(zoneName, zoneData as Record<string, { floor_area?: unknown }>, zoneSurfaceFloorAreas);
      }

      // Calculate Watt/Area (design_level / floor_area)
      let wattPerArea = 'N/A';
      if (pumpEntry.design_level !== null && pumpEntry.design_level !== undefined && floorArea > 0) {
        wattPerArea = (pumpEntry.design_level / floorArea).toFixed(2);
      }

      // Look up Fan Schedule for this specific Pump entry
      const scheduleName = pumpEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

      // Look up Setpoint Schedule from ThermostatSetpoint:DualSetpoint
      let setpointScheduleName: string | null = null;
      let setpointScheduleDataArray: any[] = [];
      let setpointScheduleDisplayValue = '';
      let setpointScheduleMatches = false;
      
      if (thermostatData && zoneName) {
        const thermostatKey = findThermostatKeyForZone(thermostatData as Record<string, unknown>, zoneName);

        if (thermostatKey && (thermostatData as any)[thermostatKey]) {
          setpointScheduleName = (thermostatData as any)[thermostatKey].cooling_setpoint_temperature_schedule_name;
          if (setpointScheduleName && scheduleCompact) {
            const setpointScheduleData = (scheduleCompact as any)[setpointScheduleName];
            setpointScheduleDataArray = setpointScheduleData?.data || [];
            setpointScheduleDisplayValue = formatScheduleData(setpointScheduleDataArray);
          }
        }
      }
      
      // Get code setpoint schedule if available
      const codeThermostatData = codeData?.objects?.['ThermostatSetpoint:DualSetpoint'];
      let codeSetpointScheduleDataArray: any[] = [];
      if (codeThermostatData && zoneName) {
        const codeThermostatKey = findThermostatKeyForZone(codeThermostatData as Record<string, unknown>, zoneName);
        if (codeThermostatKey && codeThermostatData[codeThermostatKey]) {
          const codeSetpointScheduleName = codeThermostatData[codeThermostatKey].cooling_setpoint_temperature_schedule_name;
          if (codeSetpointScheduleName && codeScheduleCompact) {
            const codeSetpointScheduleData = codeScheduleCompact[codeSetpointScheduleName];
            codeSetpointScheduleDataArray = codeSetpointScheduleData?.data || [];
          }
        }
      }
      
      setpointScheduleMatches = codeData && compareScheduleData(setpointScheduleDataArray, codeSetpointScheduleDataArray);

      return [
        { value: getZoneRef(pumpEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: pumpEntry.design_level === 0 ? '0' : (pumpEntry.design_level || ''), codeField: 'design_level', isMatch: codePump && valuesMatch(pumpEntry.design_level, (codePump as any).design_level, 'Pump', 'design_level') },
        { value: wattPerArea, codeField: 'watt_per_area', isMatch: false },
        { value: pumpEntry.design_level_calculation_method || '', codeField: 'design_level_calculation_method', isMatch: codePump && valuesMatch(pumpEntry.design_level_calculation_method, (codePump as any).design_level_calculation_method, 'Pump', 'design_level_calculation_method') },
        { value: pumpEntry.fraction_latent === 0 ? '0' : (pumpEntry.fraction_latent || ''), codeField: 'fraction_latent', isMatch: codePump && valuesMatch(pumpEntry.fraction_latent, (codePump as any).fraction_latent, 'Pump', 'fraction_latent') },
        { value: pumpEntry.fraction_lost === 0 ? '0' : (pumpEntry.fraction_lost || ''), codeField: 'fraction_lost', isMatch: codePump && valuesMatch(pumpEntry.fraction_lost, (codePump as any).fraction_lost, 'Pump', 'fraction_lost') },
        { value: pumpEntry.fraction_radiant === 0 ? '0' : (pumpEntry.fraction_radiant || ''), codeField: 'fraction_radiant', isMatch: codePump && valuesMatch(pumpEntry.fraction_radiant, (codePump as any).fraction_radiant, 'Pump', 'fraction_radiant') },
        { value: scheduleDisplayValue || pumpEntry.schedule_name || '', codeField: 'schedule_name', isMatch: scheduleMatches },
        { value: setpointScheduleDisplayValue || setpointScheduleName || '', codeField: 'setpoint_schedule', isMatch: setpointScheduleMatches },
      ];
    });
  };

  const renderInfiltrationTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No infiltration data available. Load JSON to view data.</Box>;
    }

    const infiltrationData = findDataByPattern(mainData, ['ZoneInfiltration:DesignFlowRate', 'Infiltration', 'ZoneInfiltration']);
    if (!infiltrationData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No infiltration data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get code schedule data for Infiltration
    const codeInfiltration = codeData?.objects?.['ZoneInfiltration:DesignFlowRate'] || null;
    const codeScheduleName = codeInfiltration?.schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
    const headers = ['Zone', 'Flow Rate', 'Calc Method', 'Constant Coef.', 'Temp Coef.', 'Velocity Coef.', 'Vel² Coef.', 'Schedule', 'ACH'];

    return renderDataTable(infiltrationData, headers, codeInfiltration, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneNameFromKey = extractZoneName(key);
      let infiltrationEntry = item;
      if (zoneNameFromKey && infiltrationData) {
        // Find entry matching "{zoneName} Infiltration" pattern
        const entryKey = Object.keys(infiltrationData).find(k => 
          k.includes(zoneNameFromKey) && k.includes('Infiltration')
        );
        if (entryKey) {
          infiltrationEntry = infiltrationData[entryKey];
        }
      }

      let calculatedACH = 'N/A';
      const zoneName = getZoneRef(infiltrationEntry);
      if (infiltrationEntry.design_flow_rate_calculation_method === 'AirChanges/Hour' && infiltrationEntry.air_changes_per_hour !== undefined) {
        calculatedACH = String(infiltrationEntry.air_changes_per_hour);
      } else if (infiltrationEntry.design_flow_rate === 0) {
        calculatedACH = '0';
      } else if (zoneData && (zoneData as any)[zoneName] && (zoneData as any)[zoneName].volume && infiltrationEntry.design_flow_rate !== undefined && infiltrationEntry.design_flow_rate !== null) {
        calculatedACH = ((3600 * infiltrationEntry.design_flow_rate) / (zoneData as any)[zoneName].volume).toFixed(2);
      }

      // Only apply code matching for design_flow_rate if it has a value (not empty/undefined)
      const flowRateValue = infiltrationEntry.design_flow_rate === 0 ? '0' : (infiltrationEntry.design_flow_rate || '');
      const flowRateMatch = flowRateValue !== '' && codeInfiltration && (codeInfiltration as any).design_flow_rate !== undefined 
        ? valuesMatch(infiltrationEntry.design_flow_rate, (codeInfiltration as any).design_flow_rate)
        : false;

      // Look up Schedule for this specific Infiltration entry
      const scheduleName = infiltrationEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

      return [
        { value: getZoneRef(infiltrationEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: flowRateValue, codeField: 'design_flow_rate', isMatch: flowRateMatch },
        { value: infiltrationEntry.design_flow_rate_calculation_method || '', codeField: 'design_flow_rate_calculation_method', isMatch: codeInfiltration && valuesMatch(infiltrationEntry.design_flow_rate_calculation_method, (codeInfiltration as any).design_flow_rate_calculation_method) },
        { value: infiltrationEntry.constant_term_coefficient === 0 ? '0' : (infiltrationEntry.constant_term_coefficient || ''), codeField: 'constant_term_coefficient', isMatch: codeInfiltration && valuesMatch(infiltrationEntry.constant_term_coefficient, (codeInfiltration as any).constant_term_coefficient) },
        { value: infiltrationEntry.temperature_term_coefficient === 0 ? '0' : (infiltrationEntry.temperature_term_coefficient || ''), codeField: 'temperature_term_coefficient', isMatch: codeInfiltration && valuesMatch(infiltrationEntry.temperature_term_coefficient, (codeInfiltration as any).temperature_term_coefficient) },
        { value: infiltrationEntry.velocity_term_coefficient === 0 ? '0' : (infiltrationEntry.velocity_term_coefficient || ''), codeField: 'velocity_term_coefficient', isMatch: codeInfiltration && valuesMatch(infiltrationEntry.velocity_term_coefficient, (codeInfiltration as any).velocity_term_coefficient) },
        { value: infiltrationEntry.velocity_squared_term_coefficient === 0 ? '0' : (infiltrationEntry.velocity_squared_term_coefficient || ''), codeField: 'velocity_squared_term_coefficient', isMatch: codeInfiltration && valuesMatch(infiltrationEntry.velocity_squared_term_coefficient, (codeInfiltration as any).velocity_squared_term_coefficient) },
        { value: scheduleDisplayValue || infiltrationEntry.schedule_name || '', codeField: 'schedule_name', isMatch: scheduleMatches },
        { value: calculatedACH, codeField: 'air_changes_per_hour', isMatch: codeInfiltration && valuesMatch(calculatedACH, (codeInfiltration as any).air_changes_per_hour) },
      ];
    });
  };

  const renderNatVentTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No natural ventilation data available. Load JSON to view data.</Box>;
    }

    const natVentData = findDataByPattern(mainData, ['ZoneVentilation:DesignFlowRate', 'NaturalVentilation', 'ZoneVentilation']);
    if (!natVentData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No natural ventilation data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get code schedule data for Nat Vent
    const codeVentilation = codeData?.objects?.['ZoneVentilation:DesignFlowRate'] || null;
    const codeScheduleName = codeVentilation?.schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
    const headers = ['Zone', 'Flow Rate', 'Calc Method', 'Constant Coef.', 'Temp Coef.', 'Velocity Coef.', 'Vel² Coef.', 'Schedule', 'Vent Type', 'Δ Temp', 'Fan Press.', 'Fan Eff.', 'Max Indoor Temp', 'Max Outdoor Temp', 'Max Wind', 'Min Indoor Temp', 'Min Outdoor Temp', 'ACH'];

    return renderDataTable(natVentData, headers, codeVentilation, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneNameFromKey = extractZoneName(key);
      let natVentEntry = item;
      if (zoneNameFromKey && natVentData) {
        // Find entry matching "{zoneName} Nat Vent" pattern
        const entryKey = Object.keys(natVentData).find(k => 
          k.includes(zoneNameFromKey) && k.includes('Nat Vent')
        );
        if (entryKey) {
          natVentEntry = natVentData[entryKey];
        }
      }

      // Check if design_flow_rate is 0 - if so, properties are irrelevant
      const isFlowRateZero = !natVentEntry.design_flow_rate || natVentEntry.design_flow_rate === 0;

      let calculatedACH = 'N/A';
      const zoneName = getZoneRef(natVentEntry);
      if (natVentEntry.design_flow_rate_calculation_method === 'AirChanges/Hour' && natVentEntry.air_changes_per_hour !== undefined) {
        calculatedACH = String(natVentEntry.air_changes_per_hour);
      } else if (natVentEntry.design_flow_rate === 0) {
        calculatedACH = '0';
      } else if (zoneData && (zoneData as any)[zoneName] && (zoneData as any)[zoneName].volume && natVentEntry.design_flow_rate !== undefined && natVentEntry.design_flow_rate !== null) {
        calculatedACH = ((3600 * natVentEntry.design_flow_rate) / (zoneData as any)[zoneName].volume).toFixed(2);
      }

      // Check if ACH is 0 (either calculated or from air_changes_per_hour)
      const isACHZero = calculatedACH === '0' || (natVentEntry.air_changes_per_hour !== undefined && natVentEntry.air_changes_per_hour === 0);
      
      // Properties are irrelevant when flow rate is 0 or ACH is 0
      const arePropertiesIrrelevant = isFlowRateZero || isACHZero;

      // When design_flow_rate is 0, always consider it a match
      const flowRateValue = natVentEntry.design_flow_rate === 0 ? '0' : (natVentEntry.design_flow_rate || '');
      const flowRateMatch = isFlowRateZero 
        ? true 
        : (flowRateValue !== '' && codeVentilation && (codeVentilation as any).design_flow_rate !== undefined 
          ? valuesMatch(natVentEntry.design_flow_rate, (codeVentilation as any).design_flow_rate)
          : false);

      // Look up Schedule for this specific Nat Vent entry
      const scheduleName = natVentEntry.schedule_name;
      const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
      const scheduleDataArray = scheduleData?.data || [];
      const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
      // When flow rate is 0 or ACH is 0, schedule is irrelevant, so mark as match
      const scheduleMatches = arePropertiesIrrelevant ? true : (codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray));

      // ACH matching logic
      // When design_flow_rate is 0, ACH should match if code file's air_changes_per_hour is null, undefined, or 0
      let achMatch = false;
      if (isFlowRateZero) {
        const codeACH = codeVentilation?.air_changes_per_hour;
        achMatch = codeACH === null || codeACH === undefined || codeACH === 0;
      } else if (isACHZero) {
        // When ACH is 0, it's a match if code file's air_changes_per_hour is null, undefined, or 0
        const codeACH = codeVentilation?.air_changes_per_hour;
        achMatch = codeACH === null || codeACH === undefined || codeACH === 0 || valuesMatch(calculatedACH, codeACH);
      } else {
        // Normal comparison
        achMatch = codeVentilation && (codeVentilation as any).air_changes_per_hour !== null && valuesMatch(calculatedACH, (codeVentilation as any).air_changes_per_hour);
      }

      return [
        { value: getZoneRef(natVentEntry) || key, codeField: 'zone_or_zonelist_name', isMatch: false },
        { value: flowRateValue, codeField: 'design_flow_rate', isMatch: flowRateMatch },
        { value: natVentEntry.design_flow_rate_calculation_method || '', codeField: 'design_flow_rate_calculation_method', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.design_flow_rate_calculation_method, (codeVentilation as any).design_flow_rate_calculation_method)) },
        { value: natVentEntry.constant_term_coefficient === 0 ? '0' : (natVentEntry.constant_term_coefficient || ''), codeField: 'constant_term_coefficient', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.constant_term_coefficient, (codeVentilation as any).constant_term_coefficient)) },
        { value: natVentEntry.temperature_term_coefficient === 0 ? '0' : (natVentEntry.temperature_term_coefficient || ''), codeField: 'temperature_term_coefficient', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.temperature_term_coefficient, (codeVentilation as any).temperature_term_coefficient)) },
        { value: natVentEntry.velocity_term_coefficient === 0 ? '0' : (natVentEntry.velocity_term_coefficient || ''), codeField: 'velocity_term_coefficient', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.velocity_term_coefficient, (codeVentilation as any).velocity_term_coefficient)) },
        { value: natVentEntry.velocity_squared_term_coefficient === 0 ? '0' : (natVentEntry.velocity_squared_term_coefficient || ''), codeField: 'velocity_squared_term_coefficient', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.velocity_squared_term_coefficient, (codeVentilation as any).velocity_squared_term_coefficient)) },
        { value: scheduleDisplayValue || natVentEntry.schedule_name || '', codeField: 'schedule_name', isMatch: scheduleMatches },
        { value: natVentEntry.ventilation_type || '', codeField: 'ventilation_type', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.ventilation_type, (codeVentilation as any).ventilation_type)) },
        { value: natVentEntry.delta_temperature === 0 ? '0' : (natVentEntry.delta_temperature || ''), codeField: 'delta_temperature', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.delta_temperature, (codeVentilation as any).delta_temperature)) },
        { value: natVentEntry.fan_pressure_rise === 0 ? '0' : (natVentEntry.fan_pressure_rise || ''), codeField: 'fan_pressure_rise', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.fan_pressure_rise, (codeVentilation as any).fan_pressure_rise)) },
        { value: natVentEntry.fan_total_efficiency === 0 ? '0' : (natVentEntry.fan_total_efficiency || ''), codeField: 'fan_total_efficiency', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.fan_total_efficiency, (codeVentilation as any).fan_total_efficiency)) },
        { value: natVentEntry.maximum_indoor_temperature === 0 ? '0' : (natVentEntry.maximum_indoor_temperature || ''), codeField: 'maximum_indoor_temperature', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.maximum_indoor_temperature, (codeVentilation as any).maximum_indoor_temperature)) },
        { value: natVentEntry.maximum_outdoor_temperature === 0 ? '0' : (natVentEntry.maximum_outdoor_temperature || ''), codeField: 'maximum_outdoor_temperature', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.maximum_outdoor_temperature, (codeVentilation as any).maximum_outdoor_temperature)) },
        { value: natVentEntry.maximum_wind_speed === 0 ? '0' : (natVentEntry.maximum_wind_speed || ''), codeField: 'maximum_wind_speed', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.maximum_wind_speed, (codeVentilation as any).maximum_wind_speed)) },
        { value: natVentEntry.minimum_indoor_temperature === 0 ? '0' : (natVentEntry.minimum_indoor_temperature || ''), codeField: 'minimum_indoor_temperature', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.minimum_indoor_temperature, (codeVentilation as any).minimum_indoor_temperature)) },
        { value: natVentEntry.minimum_outdoor_temperature === 0 ? '0' : (natVentEntry.minimum_outdoor_temperature || ''), codeField: 'minimum_outdoor_temperature', isMatch: arePropertiesIrrelevant ? true : (codeVentilation && valuesMatch(natVentEntry.minimum_outdoor_temperature, (codeVentilation as any).minimum_outdoor_temperature)) },
        { value: calculatedACH, codeField: 'air_changes_per_hour', isMatch: achMatch },
      ];
    });
  };

  const renderMechVentTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No mechanical ventilation data available. Load JSON to view data.</Box>;
    }

    const mechVentData = findDataByPattern(mainData, ['DesignSpecification:OutdoorAir', 'MechanicalVentilation', 'OutdoorAir']);
    if (!mechVentData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No mechanical ventilation data available</Box>;
    }

    const codeOutdoorAir = codeData?.objects?.['DesignSpecification:OutdoorAir'] || null;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeOAScheduleName = (codeOutdoorAir as any)?.outdoor_air_schedule_name;
    const codeOAScheduleData = codeOAScheduleName && codeScheduleCompact ? codeScheduleCompact[codeOAScheduleName] : null;
    const codeOAScheduleDataArray = codeOAScheduleData?.data || [];
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);
    const headers = ['Zone', 'Outdoor Air ACH', 'OA Method', 'OA Schedule'];

    return renderDataTable(mechVentData, headers, codeOutdoorAir, (item, key) => {
      // Check if outdoor_air_flow_air_changes_per_hour is 0
      const isOACHZero = !item.outdoor_air_flow_air_changes_per_hour || item.outdoor_air_flow_air_changes_per_hour === 0;

      // Outdoor Air ACH
      const oaACHValue = item.outdoor_air_flow_air_changes_per_hour === 0 ? '0' : (item.outdoor_air_flow_air_changes_per_hour || '');
      // When outdoor_air_flow_air_changes_per_hour is 0, match if code file's value is null, undefined, or 0
      let oaACHMatch = false;
      if (isOACHZero) {
        const codeOACH = codeOutdoorAir?.outdoor_air_flow_air_changes_per_hour;
        oaACHMatch = codeOACH === null || codeOACH === undefined || codeOACH === 0;
      } else {
        // Normal comparison
        oaACHMatch = oaACHValue !== '' && codeOutdoorAir && (codeOutdoorAir as any).outdoor_air_flow_air_changes_per_hour !== null && (codeOutdoorAir as any).outdoor_air_flow_air_changes_per_hour !== undefined
          ? valuesMatch(item.outdoor_air_flow_air_changes_per_hour, (codeOutdoorAir as any).outdoor_air_flow_air_changes_per_hour)
          : false;
      }

      const oaScheduleName = item.outdoor_air_schedule_name;
      const revOAScheduleData = oaScheduleName && scheduleCompact ? scheduleCompact[oaScheduleName] : null;
      const revOAScheduleDataArray = revOAScheduleData?.data || [];
      const oaScheduleDisplayValue = formatScheduleData(revOAScheduleDataArray);
      let oaScheduleMatch = true;
      if (!isOACHZero) {
        if (!codeOutdoorAir) {
          oaScheduleMatch = false;
        } else if (revOAScheduleDataArray.length > 0 && codeOAScheduleDataArray.length > 0) {
          oaScheduleMatch = Boolean(codeData && compareScheduleData(revOAScheduleDataArray, codeOAScheduleDataArray));
        } else {
          oaScheduleMatch = Boolean(codeOutdoorAir && valuesMatch(oaScheduleName, (codeOutdoorAir as any).outdoor_air_schedule_name));
        }
      }

      return [
        { value: key, codeField: 'name', isMatch: false },
        { value: oaACHValue, codeField: 'outdoor_air_flow_air_changes_per_hour', isMatch: oaACHMatch },
        { value: item.outdoor_air_method || '', codeField: 'outdoor_air_method', isMatch: Boolean(codeOutdoorAir && valuesMatch(item.outdoor_air_method, (codeOutdoorAir as any).outdoor_air_method)) },
        { value: oaScheduleDisplayValue || oaScheduleName || '', codeField: 'outdoor_air_schedule_name', isMatch: oaScheduleMatch },
      ];
    });
  };

  const renderHVACTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No HVAC data available. Load JSON to view data.</Box>;
    }

    const hvacData = findDataByPattern(mainData, ['ZoneHVAC:IdealLoadsAirSystem', 'HVAC', 'IdealLoadsAirSystem']);
    if (!hvacData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No HVAC data available</Box>;
    }

    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);

    // Get ThermostatSetpoint:DualSetpoint data for setpoint schedule lookup
    const thermostatData = findDataByPattern(mainData, ['ThermostatSetpoint:DualSetpoint']);

    // Get code schedule data for HVAC
    const codeHVAC = codeData?.objects?.['ZoneHVAC:IdealLoadsAirSystem'] || null;
    const codeCoolingScheduleName = codeHVAC?.cooling_availability_schedule_name;
    const codeHeatingScheduleName = codeHVAC?.heating_availability_schedule_name;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    const codeCoolingScheduleData = codeCoolingScheduleName && codeScheduleCompact ? codeScheduleCompact[codeCoolingScheduleName] : null;
    const codeCoolingScheduleDataArray = codeCoolingScheduleData?.data || [];
    const codeHeatingScheduleData = codeHeatingScheduleName && codeScheduleCompact ? codeScheduleCompact[codeHeatingScheduleName] : null;
    const codeHeatingScheduleDataArray = codeHeatingScheduleData?.data || [];

    // Get code ThermostatSetpoint:DualSetpoint data
    const codeThermostatData = codeData?.objects?.['ThermostatSetpoint:DualSetpoint'];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        if (Array.isArray(fieldValue)) {
          return `[${fieldValue.join(', ')}]`;
        }
        return typeof fieldValue === 'string' ? fieldValue : String(fieldValue);
      }).join(', ');
    };

    // Compare schedule data arrays (collapse consecutive on-fractions; 0.5 ≡ 1)
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        if (!item2) return false;
        
        // Normalize date strings before comparison (preserves arrays)
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2.field);
        
        // Handle array comparison: if code file (dataArray2) has an array and revision (dataArray1) has a single value,
        // check if the single value exists in the array
        if (Array.isArray(normalizedField2) && !Array.isArray(normalizedField1)) {
          const exact = normalizedField2.some((arrValue: any) => {
            const normalizedArrValue = normalizeDateString(arrValue);
            return valuesMatch(normalizedField1, normalizedArrValue);
          });
          if (exact) return true;
          return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
        } else if (Array.isArray(normalizedField1) && !Array.isArray(normalizedField2)) {
          const exact = normalizedField1.some((arrValue: any) => {
            const normalizedArrValue = normalizeDateString(arrValue);
            return valuesMatch(normalizedArrValue, normalizedField2);
          });
          if (exact) return true;
          return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
        }
        
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const headers = ['Zone', 'Humid Control', 'Dehumid Control', 'Heat Recovery', 'Cool Avail Schedule', 'Cool Limit', 'Heat Avail Schedule', 'Heat Limit', 'Max Heat Humid Ratio', 'Max Heat Temp', 'Min Cool Humid Ratio', 'Min Cool Temp', 'OA Economizer', 'Cooling Setpoint', 'Heating Setpoint'];

    return renderDataTable(hvacData, headers, codeHVAC, (item, key) => {
      // Extract zone name from key and find corresponding entry
      const zoneName = extractZoneName(key);
      let hvacEntry = item;
      if (zoneName && hvacData) {
        // Find entry matching "{zoneName} Ideal Loads Air" pattern
        const entryKey = Object.keys(hvacData).find(k => k.includes(zoneName) && k.toLowerCase().includes('ideal loads air'));
        if (entryKey) {
          hvacEntry = hvacData[entryKey];
        }
      }
      
      // Get schedule names from the zone-specific entry
      const coolingScheduleName = hvacEntry?.cooling_availability_schedule_name;
      const heatingScheduleName = hvacEntry?.heating_availability_schedule_name;

      // Look up cooling schedule data
      const coolingScheduleData = coolingScheduleName && scheduleCompact ? scheduleCompact[coolingScheduleName] : null;
      const coolingScheduleDataArray = coolingScheduleData?.data || [];
      const coolingDisplayValue = formatScheduleData(coolingScheduleDataArray);
      const coolingMatches = codeData && compareScheduleData(coolingScheduleDataArray, codeCoolingScheduleDataArray);
      
      // Format code cooling schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeCoolingScheduleDisplayValue = '';
      if (codeCoolingScheduleDataArray.length > 0) {
        codeCoolingScheduleDisplayValue = formatScheduleDataWithArrayReplacement(
          coolingScheduleDataArray,
          codeCoolingScheduleDataArray
        );
      }

      // Look up heating schedule data
      const heatingScheduleData = heatingScheduleName && scheduleCompact ? scheduleCompact[heatingScheduleName] : null;
      const heatingScheduleDataArray = heatingScheduleData?.data || [];
      const heatingDisplayValue = formatScheduleData(heatingScheduleDataArray);
      const heatingMatches = codeData && compareScheduleData(heatingScheduleDataArray, codeHeatingScheduleDataArray);
      
      // Format code heating schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeHeatingScheduleDisplayValue = '';
      if (codeHeatingScheduleDataArray.length > 0) {
        codeHeatingScheduleDisplayValue = formatScheduleDataWithArrayReplacement(
          heatingScheduleDataArray,
          codeHeatingScheduleDataArray
        );
      }

      // Look up Cooling and Heating Setpoint Schedules from ThermostatSetpoint:DualSetpoint
      let coolingSetpointScheduleName: string | null = null;
      let coolingSetpointScheduleDataArray: any[] = [];
      let coolingSetpointDisplayValue = '';
      let coolingSetpointMatches = false;

      let heatingSetpointScheduleName: string | null = null;
      let heatingSetpointScheduleDataArray: any[] = [];
      let heatingSetpointDisplayValue = '';
      let heatingSetpointMatches = false;

      if (thermostatData && zoneName) {
        const thermostatKey = findThermostatKeyForZone(thermostatData as Record<string, unknown>, zoneName);

        if (thermostatKey && (thermostatData as any)[thermostatKey]) {
          const thermostatEntry = (thermostatData as any)[thermostatKey];
          
          // Get cooling setpoint schedule
          coolingSetpointScheduleName = thermostatEntry.cooling_setpoint_temperature_schedule_name;
          if (coolingSetpointScheduleName && scheduleCompact) {
            const coolingSetpointScheduleData = (scheduleCompact as any)[coolingSetpointScheduleName];
            coolingSetpointScheduleDataArray = coolingSetpointScheduleData?.data || [];
            coolingSetpointDisplayValue = formatScheduleData(coolingSetpointScheduleDataArray);
          }

          // Get heating setpoint schedule
          heatingSetpointScheduleName = thermostatEntry.heating_setpoint_temperature_schedule_name;
          if (heatingSetpointScheduleName && scheduleCompact) {
            const heatingSetpointScheduleData = (scheduleCompact as any)[heatingSetpointScheduleName];
            heatingSetpointScheduleDataArray = heatingSetpointScheduleData?.data || [];
            heatingSetpointDisplayValue = formatScheduleData(heatingSetpointScheduleDataArray);
          }
        }
      }

      // Get code setpoint schedules if available
      // Code file has direct structure (no zone keys), use it directly
      let codeCoolingSetpointScheduleDataArray: any[] = [];
      let codeHeatingSetpointScheduleDataArray: any[] = [];
      if (codeThermostatData) {
        const codeCoolingSetpointScheduleName = codeThermostatData.cooling_setpoint_temperature_schedule_name;
        if (codeCoolingSetpointScheduleName && codeScheduleCompact) {
          const codeCoolingSetpointScheduleData = codeScheduleCompact[codeCoolingSetpointScheduleName];
          codeCoolingSetpointScheduleDataArray = codeCoolingSetpointScheduleData?.data || [];
        }

        const codeHeatingSetpointScheduleName = codeThermostatData.heating_setpoint_temperature_schedule_name;
        if (codeHeatingSetpointScheduleName && codeScheduleCompact) {
          const codeHeatingSetpointScheduleData = codeScheduleCompact[codeHeatingSetpointScheduleName];
          codeHeatingSetpointScheduleDataArray = codeHeatingSetpointScheduleData?.data || [];
        }
      }

      // Compare setpoint schedules
      coolingSetpointMatches = codeData && compareScheduleData(coolingSetpointScheduleDataArray, codeCoolingSetpointScheduleDataArray);
      heatingSetpointMatches = codeData && compareScheduleData(heatingSetpointScheduleDataArray, codeHeatingSetpointScheduleDataArray);

      // Format code cooling setpoint schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeCoolingSetpointDisplayValue = '';
      if (codeCoolingSetpointScheduleDataArray.length > 0) {
        codeCoolingSetpointDisplayValue = formatScheduleDataWithArrayReplacement(
          coolingSetpointScheduleDataArray,
          codeCoolingSetpointScheduleDataArray,
          { closestByTime: true }
        );
      }

      // Format code heating setpoint schedule with array replacement
      // Replace arrays in code schedule with closest value to corresponding revision value
      let codeHeatingSetpointDisplayValue = '';
      if (codeHeatingSetpointScheduleDataArray.length > 0) {
        codeHeatingSetpointDisplayValue = formatScheduleDataWithArrayReplacement(
          heatingSetpointScheduleDataArray,
          codeHeatingSetpointScheduleDataArray
        );
      }

      return [
        { value: key, codeField: 'name', isMatch: false },
        { value: item.humidification_control_type || '', codeField: 'humidification_control_type', isMatch: codeHVAC && valuesMatch(item.humidification_control_type, (codeHVAC as any).humidification_control_type) },
        { value: item.dehumidification_control_type || '', codeField: 'dehumidification_control_type', isMatch: codeHVAC && valuesMatch(item.dehumidification_control_type, (codeHVAC as any).dehumidification_control_type) },
        { value: item.heat_recovery_type || '', codeField: 'heat_recovery_type', isMatch: codeHVAC && valuesMatch(item.heat_recovery_type, (codeHVAC as any).heat_recovery_type) },
        { value: coolingDisplayValue || hvacEntry?.cooling_availability_schedule_name || '', codeField: 'cooling_availability_schedule_name', isMatch: coolingMatches, codeValue: codeCoolingScheduleDisplayValue || codeCoolingScheduleName || '' },
        { value: item.cooling_limit || '', codeField: 'cooling_limit', isMatch: codeHVAC && valuesMatch(item.cooling_limit, (codeHVAC as any).cooling_limit) },
        { value: heatingDisplayValue || hvacEntry?.heating_availability_schedule_name || '', codeField: 'heating_availability_schedule_name', isMatch: heatingMatches, codeValue: codeHeatingScheduleDisplayValue || codeHeatingScheduleName || '' },
        { value: item.heating_limit || '', codeField: 'heating_limit', isMatch: codeHVAC && valuesMatch(item.heating_limit, (codeHVAC as any).heating_limit) },
        { value: item.maximum_heating_supply_air_humidity_ratio === 0 ? '0' : (item.maximum_heating_supply_air_humidity_ratio || ''), codeField: 'maximum_heating_supply_air_humidity_ratio', isMatch: codeHVAC && valuesMatch(item.maximum_heating_supply_air_humidity_ratio, (codeHVAC as any).maximum_heating_supply_air_humidity_ratio) },
        { value: item.maximum_heating_supply_air_temperature === 0 ? '0' : (item.maximum_heating_supply_air_temperature || ''), codeField: 'maximum_heating_supply_air_temperature', isMatch: codeHVAC && valuesMatch(item.maximum_heating_supply_air_temperature, (codeHVAC as any).maximum_heating_supply_air_temperature) },
        { value: item.minimum_cooling_supply_air_humidity_ratio === 0 ? '0' : (item.minimum_cooling_supply_air_humidity_ratio || ''), codeField: 'minimum_cooling_supply_air_humidity_ratio', isMatch: codeHVAC && valuesMatch(item.minimum_cooling_supply_air_humidity_ratio, (codeHVAC as any).minimum_cooling_supply_air_humidity_ratio) },
        { value: item.minimum_cooling_supply_air_temperature === 0 ? '0' : (item.minimum_cooling_supply_air_temperature || ''), codeField: 'minimum_cooling_supply_air_temperature', isMatch: codeHVAC && valuesMatch(item.minimum_cooling_supply_air_temperature, (codeHVAC as any).minimum_cooling_supply_air_temperature) },
        { value: item.outdoor_air_economizer_type || '', codeField: 'outdoor_air_economizer_type', isMatch: codeHVAC && valuesMatch(item.outdoor_air_economizer_type, (codeHVAC as any).outdoor_air_economizer_type) },
        { value: coolingSetpointDisplayValue || coolingSetpointScheduleName || '', codeField: 'cooling_setpoint_temperature_schedule_name', isMatch: coolingSetpointMatches, codeValue: codeCoolingSetpointDisplayValue || codeThermostatData?.cooling_setpoint_temperature_schedule_name || '' },
        { value: heatingSetpointDisplayValue || heatingSetpointScheduleName || '', codeField: 'heating_setpoint_temperature_schedule_name', isMatch: heatingSetpointMatches, codeValue: codeHeatingSetpointDisplayValue || codeThermostatData?.heating_setpoint_temperature_schedule_name || '' },
      ];
    });
  };

  const renderAirflowNetworkTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No AirflowNetwork data available. Load JSON to view data.</Box>;
    }

    const airflowNetworkKeys = findAirflowNetworkKeys(mainData);
    if (airflowNetworkKeys.length === 0) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No AirflowNetwork data found in the JSON file</Box>;
    }

    // Get AirflowNetwork:MultiZone:Zone data for schedule lookup
    const zoneData = findDataByPattern(mainData, ['AirflowNetwork:MultiZone:Zone']);
    
    // Get Schedule:Compact data for looking up schedules
    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);
    
    // Get code schedule data for AirflowNetwork:MultiZone:Zone
    const codeZone = codeData?.objects?.['AirflowNetwork:MultiZone:Zone'] || null;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];
    
    // Get code schedule data for venting_availability_schedule_name
    const codeVentingScheduleName = codeZone?.venting_availability_schedule_name;
    const codeVentingScheduleData = codeVentingScheduleName && codeScheduleCompact ? codeScheduleCompact[codeVentingScheduleName] : null;
    const codeVentingScheduleDataArray = codeVentingScheduleData?.data || [];
    
    // Get code schedule data for ventilation_control_zone_temperature_setpoint_schedule_name
    const codeTempScheduleName = codeZone?.ventilation_control_zone_temperature_setpoint_schedule_name;
    const codeTempScheduleData = codeTempScheduleName && codeScheduleCompact ? codeScheduleCompact[codeTempScheduleName] : null;
    const codeTempScheduleDataArray = codeTempScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    // Helper function to extract zone name from external_node_name
    const extractZoneNameFromExternalNode = (externalNodeName: string, zoneData: any): string | null => {
      if (!externalNodeName || !zoneData) return null;
      
      // Collect all matching zones (to find the longest match)
      const matchingZones: { zoneName: string; length: number }[] = [];
      
      for (const [zoneKey, zoneEntry] of Object.entries(zoneData)) {
        const zoneName = (zoneEntry as any)?.zone_name;
        if (zoneName && typeof zoneName === 'string' && externalNodeName.includes(zoneName)) {
          matchingZones.push({ zoneName, length: zoneName.length });
        }
      }
      
      // Return the longest matching zone name (most specific match)
      if (matchingZones.length > 0) {
        matchingZones.sort((a, b) => b.length - a.length);
        return matchingZones[0].zoneName;
      }
      
      return null;
    };

    const sections: JSX.Element[] = [];

    airflowNetworkKeys.forEach((key) => {
      // Skip excluded sections
      if (EXCLUDED_AIRFLOWNETWORK_SECTIONS.includes(key)) return;
      
      const sectionData = mainData[key];
      if (!sectionData || Object.keys(sectionData).length === 0) return;

      // Extract section name (part after "AirflowNetwork:")
      const sectionName = key.replace(/^AirflowNetwork:/, '');
      
      // Get code data for this section if available
      const codeSectionData = codeData?.objects?.[key] || null;

      // Get all unique parameter names from all objects in this section
      const allParamNames = new Set<string>();
      // Get section-specific excluded fields (if any)
      const sectionExcludedFields = EXCLUDED_AIRFLOWNETWORK_FIELDS_BY_SECTION[key] || [];
      Object.values(sectionData).forEach((item: any) => {
        if (item && typeof item === 'object') {
          Object.keys(item).forEach(param => {
            // Exclude fields that are in the global exclusion list OR section-specific exclusion list
            if (!EXCLUDED_AIRFLOWNETWORK_FIELDS.includes(param) && !sectionExcludedFields.includes(param)) {
              allParamNames.add(param);
            }
          });
        }
      });
      const headers = ['Name', ...Array.from(allParamNames).sort().map(param => formatKey(param))];

      sections.push(
        <Section key={key}>
          <SectionHeader>{sectionName}</SectionHeader>
          <SectionContent>
            <StyledTable>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <StyledTableCell key={header}>{header}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(sectionData)
                  .filter(([itemKey, item]: [string, any]) => {
                    // For AirflowNetwork:MultiZone:Surface, only show entries where external_node_name includes "Win"
                    if (key === 'AirflowNetwork:MultiZone:Surface') {
                      const externalNodeName = item?.external_node_name;
                      return externalNodeName && typeof externalNodeName === 'string' && externalNodeName.includes('Win');
                    }
                    // For AirflowNetwork:MultiZone:WindPressureCoefficientValues, only show entries where name includes "Win"
                    if (key === 'AirflowNetwork:MultiZone:WindPressureCoefficientValues') {
                      return itemKey && typeof itemKey === 'string' && itemKey.includes('Win');
                    }
                    return true; // Show all entries for other sections
                  })
                  .map(([itemKey, item]: [string, any]) => {
                  // Special handling for schedule fields in AirflowNetwork:MultiZone:Surface
                  let ventingScheduleMatch = false;
                  let ventingScheduleDisplayValue = '';
                  let tempScheduleMatch = false;
                  let tempScheduleDisplayValue = '';
                  
                  if (key === 'AirflowNetwork:MultiZone:Surface' && item.external_node_name) {
                    // Extract zone name from external_node_name
                    const extractedZoneName = extractZoneNameFromExternalNode(item.external_node_name, zoneData);
                    
                    if (extractedZoneName && zoneData) {
                      // Find matching Zone entry
                      let matchedZoneEntry: any = null;
                      for (const [zoneKey, zoneEntry] of Object.entries(zoneData)) {
                        if ((zoneEntry as any)?.zone_name === extractedZoneName) {
                          matchedZoneEntry = zoneEntry;
                          break;
                        }
                      }
                      
                      if (matchedZoneEntry) {
                        // Handle venting_availability_schedule_name
                        if (matchedZoneEntry.venting_availability_schedule_name) {
                          const scheduleName = matchedZoneEntry.venting_availability_schedule_name;
                          const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
                          const scheduleDataArray = scheduleData?.data || [];
                          ventingScheduleDisplayValue = formatScheduleData(scheduleDataArray) || scheduleName;
                          ventingScheduleMatch = codeData && compareScheduleData(scheduleDataArray, codeVentingScheduleDataArray);
                        }
                        
                        // Handle ventilation_control_zone_temperature_setpoint_schedule_name
                        if (matchedZoneEntry.ventilation_control_zone_temperature_setpoint_schedule_name) {
                          const scheduleName = matchedZoneEntry.ventilation_control_zone_temperature_setpoint_schedule_name;
                          const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
                          const scheduleDataArray = scheduleData?.data || [];
                          tempScheduleDisplayValue = formatScheduleData(scheduleDataArray) || scheduleName;
                          tempScheduleMatch = codeData && compareScheduleData(scheduleDataArray, codeTempScheduleDataArray);
                        }
                      }
                    }
                  }
                  
                  const cells = [
                    { value: itemKey, codeField: 'name', isMatch: false },
                    ...Array.from(allParamNames).sort().map(param => {
                      // Special handling for venting_availability_schedule_name
                      if (key === 'AirflowNetwork:MultiZone:Surface' && param === 'venting_availability_schedule_name') {
                        const displayValue = ventingScheduleDisplayValue || (item[param] || '');
                        return { value: displayValue, codeField: param, isMatch: ventingScheduleMatch };
                      }
                      
                      // Special handling for ventilation_control_zone_temperature_setpoint_schedule_name
                      if (key === 'AirflowNetwork:MultiZone:Surface' && param === 'ventilation_control_zone_temperature_setpoint_schedule_name') {
                        const displayValue = tempScheduleDisplayValue || (item[param] || '');
                        return { value: displayValue, codeField: param, isMatch: tempScheduleMatch };
                      }
                      
                      const value = item[param];
                      const displayValue = value === null || value === undefined
                        ? ''
                        : typeof value === 'object'
                        ? JSON.stringify(value)
                        : String(value);
                      // For AirflowNetwork, code data structure is different: codeSectionData contains reference values directly
                      // Main: mainData[key][itemKey][param]
                      // Code: codeData.objects[key][param]
                      const isMatch = codeData && codeSectionData && codeSectionData[param] !== undefined
                        ? valuesMatch(value, codeSectionData[param])
                        : false;
                      return { value: displayValue, codeField: param, isMatch };
                    })
                  ];
                  return (
                    <TableRow key={itemKey}>
                      {cells.map((cell, idx) => {
                        const { value, codeField, isMatch } = cell;
                        // Use NameCell for the first column (Name), CodeMatchCell for matches, otherwise StyledTableCell
                        let CellComponent: typeof StyledTableCell;
                        if (idx === 0) {
                          CellComponent = NameCell;
                        } else {
                          CellComponent = isMatch ? CodeMatchCell : StyledTableCell;
                        }
                        return (
                          <CellComponent key={idx}>{value}</CellComponent>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </SectionContent>
        </Section>
      );
    });

    return sections.length > 0 ? <>{sections}</> : <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No AirflowNetwork data found</Box>;
  };

  const renderDaylightingTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No daylighting data available. Load JSON to view data.</Box>;
    }

    const daylightingControlsData = findDataByPattern(mainData, ['Daylighting:Controls']);

    if (!daylightingControlsData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No daylighting data available</Box>;
    }

    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);
    const codeDaylightingControls = codeData?.objects?.['Daylighting:Controls'] || null;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];

    const codeAvailabilityScheduleName = codeDaylightingControls?.availability_schedule_name;
    const codeAvailabilityScheduleData = codeAvailabilityScheduleName && codeScheduleCompact ? codeScheduleCompact[codeAvailabilityScheduleName] : null;
    const codeAvailabilityScheduleDataArray = codeAvailabilityScheduleData?.data || [];

    // Extract illuminance_setpoint from code control_data array
    let codeIlluminanceSetpoint = null;
    if (codeDaylightingControls?.control_data && Array.isArray(codeDaylightingControls.control_data) && codeDaylightingControls.control_data.length > 0) {
      const firstControlData = codeDaylightingControls.control_data[0];
      if (firstControlData && firstControlData.illuminance_setpoint_at_reference_point !== undefined) {
        codeIlluminanceSetpoint = firstControlData.illuminance_setpoint_at_reference_point;
      }
    }

    const sections: JSX.Element[] = [];

    // Daylighting:Controls section
    if (daylightingControlsData) {
      const headers = ['Zone', 'Availability Schedule', 'Daylighting Method', 'Illuminance Setpoint', 'Glare Calculation Azimuth Angle', 'Max Discomfort Glare Index', 'Number of Stepped Control Steps', 'Probability Lighting Will Be Reset'];

      sections.push(
        <Section key="daylighting-controls">
          <SectionHeader>Daylighting:Controls</SectionHeader>
          <SectionContent>
            <StyledTable>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <StyledTableCell key={header}>{header}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(daylightingControlsData).map(([key, item]: [string, any]) => {
                  const zoneName = item.zone_name || key;
                  
                  // Availability Schedule
                  const availabilityScheduleName = item.availability_schedule_name;
                  const availabilityScheduleData = availabilityScheduleName && scheduleCompact ? scheduleCompact[availabilityScheduleName] : null;
                  const availabilityScheduleDataArray = availabilityScheduleData?.data || [];
                  const availabilityScheduleDisplayValue = formatScheduleData(availabilityScheduleDataArray);
                  const availabilityMatches = codeData && compareScheduleData(availabilityScheduleDataArray, codeAvailabilityScheduleDataArray);

                  // Illuminance Setpoint (from control_data array)
                  let illuminanceSetpoint = null;
                  if (item.control_data && Array.isArray(item.control_data) && item.control_data.length > 0) {
                    const firstControlData = item.control_data[0];
                    if (firstControlData && firstControlData.illuminance_setpoint_at_reference_point !== undefined) {
                      illuminanceSetpoint = firstControlData.illuminance_setpoint_at_reference_point;
                    }
                  }
                  const illuminanceMatches = codeDaylightingControls && valuesMatch(illuminanceSetpoint, codeIlluminanceSetpoint);

                  const AvailabilityCell = availabilityMatches ? CodeMatchCell : StyledTableCell;
                  const MethodCell = codeDaylightingControls && valuesMatch(item.daylighting_method, codeDaylightingControls.daylighting_method) ? CodeMatchCell : StyledTableCell;
                  const IlluminanceCell = illuminanceMatches ? CodeMatchCell : StyledTableCell;
                  const GlareCell = codeDaylightingControls && valuesMatch(item.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis, codeDaylightingControls.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis) ? CodeMatchCell : StyledTableCell;
                  const MaxGlareCell = codeDaylightingControls && valuesMatch(item.maximum_allowable_discomfort_glare_index, codeDaylightingControls.maximum_allowable_discomfort_glare_index) ? CodeMatchCell : StyledTableCell;
                  const StepsCell = codeDaylightingControls && valuesMatch(item.number_of_stepped_control_steps, codeDaylightingControls.number_of_stepped_control_steps) ? CodeMatchCell : StyledTableCell;
                  const ProbabilityCell = codeDaylightingControls && valuesMatch(item.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control, codeDaylightingControls.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control) ? CodeMatchCell : StyledTableCell;

                  return (
                    <TableRow key={key}>
                      <NameCell>{zoneName}</NameCell>
                      <AvailabilityCell>
                        {availabilityScheduleDisplayValue || availabilityScheduleName || ''}
                      </AvailabilityCell>
                      <MethodCell>
                        {item.daylighting_method || ''}
                      </MethodCell>
                      <IlluminanceCell>
                        {illuminanceSetpoint !== null && illuminanceSetpoint !== undefined ? String(illuminanceSetpoint) : ''}
                      </IlluminanceCell>
                      <GlareCell>
                        {item.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis !== null && item.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis !== undefined ? String(item.glare_calculation_azimuth_angle_of_view_direction_clockwise_from_zone_y_axis) : ''}
                      </GlareCell>
                      <MaxGlareCell>
                        {item.maximum_allowable_discomfort_glare_index !== null && item.maximum_allowable_discomfort_glare_index !== undefined ? String(item.maximum_allowable_discomfort_glare_index) : ''}
                      </MaxGlareCell>
                      <StepsCell>
                        {item.number_of_stepped_control_steps !== null && item.number_of_stepped_control_steps !== undefined ? String(item.number_of_stepped_control_steps) : ''}
                      </StepsCell>
                      <ProbabilityCell>
                        {item.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control !== null && item.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control !== undefined ? String(item.probability_lighting_will_be_reset_when_needed_in_manual_stepped_control) : ''}
                      </ProbabilityCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </SectionContent>
        </Section>
      );
    }

    return sections.length > 0 ? <>{sections}</> : <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No daylighting data found</Box>;
  };

  const renderWindowShadingTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No window shading data available. Load JSON to view data.</Box>;
    }

    const windowMaterialShadeData = findDataByPattern(mainData, ['WindowMaterial:Shade']);
    const windowShadingControlData = findDataByPattern(mainData, ['WindowShadingControl']);

    if (!windowMaterialShadeData && !windowShadingControlData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No window shading data available</Box>;
    }

    const scheduleCompact = findDataByPattern(mainData, ['Schedule:Compact', 'ScheduleCompact']);
    const codeWindowMaterialShade = codeData?.objects?.['WindowMaterial:Shade'] || null;
    const codeWindowShadingControl = codeData?.objects?.['WindowShadingControl'] || null;
    const codeScheduleCompact = codeData?.objects?.['Schedule:Compact'];

    // Code schedule for WindowShadingControl
    const codeScheduleName = codeWindowShadingControl?.schedule_name;
    const codeScheduleData = codeScheduleName && codeScheduleCompact ? codeScheduleCompact[codeScheduleName] : null;
    const codeScheduleDataArray = codeScheduleData?.data || [];

    // Normalize date strings to handle different formats (MM/DD vs DD Mon)
    const normalizeDateString = (value: any): any => {
      if (typeof value !== 'string') return value;
      
      // Pattern for "Through: MM/DD" format (e.g., "Through: 12/31")
      const mmddPattern = /^(Through:\s*)(\d{1,2})\/(\d{1,2})$/i;
      const mmddMatch = value.match(mmddPattern);
      
      if (mmddMatch) {
        const month = parseInt(mmddMatch[2], 10);
        const day = parseInt(mmddMatch[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (month >= 1 && month <= 12) {
          return `${mmddMatch[1]}${day} ${monthNames[month - 1]}`;
        }
      }
      
      // Pattern for "Through: DD MonthFullName" format (e.g., "Through: 31 December") - normalize to "Through: DD Mon"
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const ddMonthFullPattern = /^(Through:\s*)(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
      const ddMonthFullMatch = value.match(ddMonthFullPattern);
      
      if (ddMonthFullMatch) {
        const day = ddMonthFullMatch[2];
        const monthFullName = ddMonthFullMatch[3];
        const monthIndex = fullMonthNames.findIndex(m => m.toLowerCase() === monthFullName.toLowerCase());
        if (monthIndex !== -1) {
          return `${ddMonthFullMatch[1]}${day} ${monthAbbreviations[monthIndex]}`;
        }
      }
      
      // Pattern for "Through: DD Mon" format (e.g., "Through: 31 Dec") - keep as is
      const ddmonPattern = /^(Through:\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))$/i;
      if (ddmonPattern.test(value)) {
        return value;
      }
      
      // Pattern for "Until: H:MM" or "Until: HH:MM" format - normalize to "Until: HH:MM"
      const timePattern = /^(Until:\s*)(\d{1,2}):(\d{2})$/i;
      const timeMatch = value.match(timePattern);
      if (timeMatch) {
        const hours = parseInt(timeMatch[2], 10);
        const minutes = timeMatch[3];
        // Pad hours with leading zero if needed (0-9 -> 00-09, but keep 10-24 as is)
        const normalizedHours = hours < 10 ? `0${hours}` : String(hours);
        return `${timeMatch[1]}${normalizedHours}:${minutes}`;
      }
      
      // Return unchanged for other values (numbers, etc.)
      return value;
    };

    // Format schedule data for display
    const formatScheduleData = (dataArray: any[]): string => {
      if (!dataArray || dataArray.length === 0) return '';
      return dataArray.map((item: any) => {
        const fieldValue = item.field;
        // Normalize date strings to ensure consistent format (e.g., "Through: 31 Dec")
        const normalizedValue = normalizeDateString(fieldValue);
        return typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);
      }).join(', ');
    };

    // Compare schedule data arrays
    const compareScheduleData = (dataArray1: any[], dataArray2: any[]): boolean => {
      const byTime = compareCompactScheduleData(dataArray1, dataArray2, normalizeDateString);
      if (byTime !== null) return byTime;
      const collapsed1 = normalizeScheduleDataForCompare(dataArray1);
      const collapsed2 = normalizeScheduleDataForCompare(dataArray2);
      if (!collapsed1?.length || !collapsed2?.length) return false;
      if (collapsed1.length !== collapsed2.length) return false;
      return collapsed1.every((item1, index) => {
        const item2 = collapsed2[index];
        // Normalize date strings before comparison
        const normalizedField1 = normalizeDateString(item1.field);
        const normalizedField2 = normalizeDateString(item2?.field);
        if (valuesMatch(normalizedField1, normalizedField2)) return true;
        return availScheduleFieldsEquivalent(normalizedField1, normalizedField2);
      });
    };

    const sections: JSX.Element[] = [];

    // WindowMaterial:Shade section
    if (windowMaterialShadeData) {
      const headers = ['Material Name', 'Airflow Permeability', 'Bottom Opening Multiplier', 'Left Side Opening Multiplier', 'Right Side Opening Multiplier', 'Top Opening Multiplier'];

      sections.push(
        <Section key="window-material-shade">
          <SectionHeader>WindowMaterial:Shade</SectionHeader>
          <SectionContent>
            <StyledTable>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <StyledTableCell key={header}>{header}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(windowMaterialShadeData).map(([key, item]: [string, any]) => {
                  const materialName = key;
                  
                  const airflowPermeability = item.airflow_permeability === 0 ? '0' : (item.airflow_permeability || '');
                  const bottomOpening = item.bottom_opening_multiplier === 0 ? '0' : (item.bottom_opening_multiplier || '');
                  const leftSideOpening = item.left_side_opening_multiplier === 0 ? '0' : (item.left_side_opening_multiplier || '');
                  const rightSideOpening = item.right_side_opening_multiplier === 0 ? '0' : (item.right_side_opening_multiplier || '');
                  const topOpening = item.top_opening_multiplier === 0 ? '0' : (item.top_opening_multiplier || '');

                  const AirflowCell = codeWindowMaterialShade && valuesMatch(item.airflow_permeability, codeWindowMaterialShade.airflow_permeability) ? CodeMatchCell : StyledTableCell;
                  const BottomCell = codeWindowMaterialShade && valuesMatch(item.bottom_opening_multiplier, codeWindowMaterialShade.bottom_opening_multiplier) ? CodeMatchCell : StyledTableCell;
                  const LeftCell = codeWindowMaterialShade && valuesMatch(item.left_side_opening_multiplier, codeWindowMaterialShade.left_side_opening_multiplier) ? CodeMatchCell : StyledTableCell;
                  const RightCell = codeWindowMaterialShade && valuesMatch(item.right_side_opening_multiplier, codeWindowMaterialShade.right_side_opening_multiplier) ? CodeMatchCell : StyledTableCell;
                  const TopCell = codeWindowMaterialShade && valuesMatch(item.top_opening_multiplier, codeWindowMaterialShade.top_opening_multiplier) ? CodeMatchCell : StyledTableCell;

                  return (
                    <TableRow key={key}>
                      <NameCell>{materialName}</NameCell>
                      <AirflowCell>{String(airflowPermeability)}</AirflowCell>
                      <BottomCell>{String(bottomOpening)}</BottomCell>
                      <LeftCell>{String(leftSideOpening)}</LeftCell>
                      <RightCell>{String(rightSideOpening)}</RightCell>
                      <TopCell>{String(topOpening)}</TopCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </SectionContent>
        </Section>
      );
    }

    // WindowShadingControl section
    if (windowShadingControlData) {
      const headers = ['Zone', 'Schedule'];
      if (codeData) {
        headers.push('Code Value');
      }

      sections.push(
        <Section key="window-shading-control">
          <SectionHeader>WindowShadingControl</SectionHeader>
          <SectionContent>
            <StyledTable>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <StyledTableCell key={header}>{header}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(windowShadingControlData).map(([key, item]: [string, any]) => {
                  const zoneName = item.zone_name || key;
                  
                  // Schedule
                  const scheduleName = item.schedule_name;
                  const scheduleData = scheduleName && scheduleCompact ? scheduleCompact[scheduleName] : null;
                  const scheduleDataArray = scheduleData?.data || [];
                  const scheduleDisplayValue = formatScheduleData(scheduleDataArray);
                  const scheduleMatches = codeData && compareScheduleData(scheduleDataArray, codeScheduleDataArray);

                  const ScheduleCell = scheduleMatches ? CodeMatchCell : StyledTableCell;

                  return (
                    <TableRow key={key}>
                      <NameCell>{zoneName}</NameCell>
                      <ScheduleCell>
                        {scheduleDisplayValue || scheduleName || ''}
                      </ScheduleCell>
                      {codeData && (
                        <StyledTableCell>
                          {formatScheduleData(codeScheduleDataArray) || codeScheduleName || 'N/A'}
                        </StyledTableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </SectionContent>
        </Section>
      );
    }

    return sections.length > 0 ? <>{sections}</> : <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No window shading data found</Box>;
  };

  const renderReportTab = () => {
    if (!mainData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No data available. Load JSON to view data.</Box>;
    }
    if (!codeData) {
      return <Box sx={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontStyle: 'italic' }}>No Code JSON loaded. Load Code JSON to view mismatches.</Box>;
    }

    // Calculate block metrics for Block info table (same logic as renderBlockTab)
    const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
    const windowAnalysis = analyzeZoneWindows(mainData);
    const blocks: any = {};

    if (zoneData && !('error' in windowAnalysis)) {
      const peopleData = findDataByPattern(mainData, ['People', 'Person', 'Occupancy']);
      const lightsData = findDataByPattern(mainData, ['Lights', 'Lighting', 'Light']);
      const computingData = findDataByPattern(mainData, ['OtherEquipment', 'Equipment', 'ElectricEquipment']);

      for (const [zoneName, zoneInfo] of Object.entries(zoneData)) {
        if ((zoneInfo as any).part_of_total_floor_area === 'No' || (zoneInfo as any).part_of_total_floor_area === false) {
          continue;
        }

        let blockName = 'Default Block';
        let zoneShortName = zoneName;
        if (zoneName.includes(':')) {
          const parts = zoneName.split(':');
          blockName = parts[0];
          zoneShortName = parts.slice(1).join(':');
        }

        if (!blocks[blockName]) {
          blocks[blockName] = {
            zones: [],
            totalArea: 0,
            totalPeople: 0,
            totalPeoplePerArea: 0,
            totalWattsPerArea: 0,
            totalComputingPowerPerArea: 0,
            totalEquipmentPowerPerArea: 0,
            totalWindows: 0,
            windowOrientations: { North: 0, South: 0, East: 0, West: 0, Unknown: 0 },
            zoneCount: 0
          };
        }

        blocks[blockName].zones.push(zoneShortName);
        const zoneFloorArea = getZoneFloorArea(zoneName, zoneInfo as any, zoneSurfaceFloorAreas);
        blocks[blockName].totalArea += zoneFloorArea;
        blocks[blockName].zoneCount++;

        if (windowAnalysis[zoneName]) {
          const zoneWindowData = windowAnalysis[zoneName];
          blocks[blockName].totalWindows += zoneWindowData.totalWindows;
          Object.keys(zoneWindowData.orientations).forEach(orientation => {
            blocks[blockName].windowOrientations[orientation] += zoneWindowData.orientations[orientation as keyof typeof zoneWindowData.orientations];
          });
        }

        // Accumulate metrics
        if (peopleData) {
          for (const [peopleKey, peopleInfo] of Object.entries(peopleData)) {
            if (getZoneRef(peopleInfo) === zoneName) {
              blocks[blockName].totalPeoplePerArea += getPeoplePerFloorArea(peopleInfo) || 0;

              const method = (peopleInfo as any).number_of_people_calculation_method;
              if (method === 'People') {
                blocks[blockName].totalPeople += (peopleInfo as any).number_of_people || 0;
              } else if (method === 'People/Area') {
                const perArea = getPeoplePerFloorArea(peopleInfo) || 0;
                blocks[blockName].totalPeople += perArea * zoneFloorArea;
              }
              break;
            }
          }
        }

        if (lightsData) {
          for (const [lightKey, lightInfo] of Object.entries(lightsData)) {
            if (getZoneRef(lightInfo) === zoneName) {
              blocks[blockName].totalWattsPerArea += getWattsPerFloorArea(lightInfo) || 0;
              break;
            }
          }
        }

        if (computingData) {
          for (const [equipKey, equipInfo] of Object.entries(computingData)) {
            const equipName = equipKey.toLowerCase();
            if (equipName.includes('computing') && getZoneRef(equipInfo) === zoneName) {
              blocks[blockName].totalComputingPowerPerArea += getPowerPerFloorArea(equipInfo) || 0;
            }
            if (equipName.includes('equipment') && !equipName.includes('computing') && getZoneRef(equipInfo) === zoneName) {
              blocks[blockName].totalEquipmentPowerPerArea += getPowerPerFloorArea(equipInfo) || 0;
            }
          }
        }
      }
    }

    // Calculate compliance for each block and determine which columns to show
    const blockComplianceData: any[] = [];
    let hasPeopleMismatch = false;
    let hasLightsMismatch = false;
    let hasComputingMismatch = false;
    let hasEquipmentMismatch = false;

    Object.entries(blocks).forEach(([blockName, blockInfo]: [string, any]) => {
      const avgPeoplePerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalPeoplePerArea / blockInfo.zoneCount).toFixed(3) : '0';
      const avgWattsPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalWattsPerArea / blockInfo.zoneCount).toFixed(2) : '0';
      const avgComputingPowerPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalComputingPowerPerArea / blockInfo.zoneCount).toFixed(2) : '0';
      const avgEquipmentPowerPerArea = blockInfo.zoneCount > 0 ? (blockInfo.totalEquipmentPowerPerArea / blockInfo.zoneCount).toFixed(2) : '0';

      // Check code compliance for People/Area
      let peopleCompliance = false;
      if (codeData?.objects?.People) {
        const peopleCode = codeData.objects.People;
        if (peopleCode.area_range && getPeoplePerFloorArea(peopleCode)) {
          const requiredPeoplePerArea = getCodeValueForArea(blockInfo.totalArea, peopleCode.area_range, getPeoplePerFloorArea(peopleCode));
          if (parseFloat(avgPeoplePerArea) <= parseFloat(requiredPeoplePerArea)) {
            peopleCompliance = true;
          }
        }
      }
      if (unitFamilyPresent(mainData, 'people') && !peopleCompliance) hasPeopleMismatch = true;

      // Check code compliance for Lights (W/m²)
      let lightsCompliance = false;
      if (codeData?.objects?.Lights) {
        const lightsCode = codeData.objects.Lights;
        const generalLighting = lightsCode['General lighting'];
        const requiredWattsPerArea =
          getWattsPerFloorArea(generalLighting) ?? getWattsPerFloorArea(lightsCode);
        if (requiredWattsPerArea) {
          if (Array.isArray(requiredWattsPerArea)) {
            if (requiredWattsPerArea.includes(parseFloat(avgWattsPerArea))) lightsCompliance = true;
          } else {
            if (parseFloat(avgWattsPerArea) <= parseFloat(requiredWattsPerArea)) lightsCompliance = true;
          }
        }
      }
      if (unitFamilyPresent(mainData, 'lights') && !lightsCompliance) hasLightsMismatch = true;

      // Check code compliance for Computing (W/m²)
      let computingCompliance = false;
      if (codeData?.objects?.OtherEquipment) {
        for (const [equipKey, equipInfo] of Object.entries(codeData.objects.OtherEquipment)) {
          if (equipKey.toLowerCase().includes('computing') && (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo)) {
            const requiredComputingPower = getCodeValueForArea(blockInfo.totalArea, (equipInfo as any).area_range, getPowerPerFloorArea(equipInfo));
            if (parseFloat(avgComputingPowerPerArea) <= parseFloat(requiredComputingPower)) {
              computingCompliance = true;
            }
            break;
          }
        }
      }
      if (unitFamilyPresent(mainData, 'computing') && !computingCompliance) hasComputingMismatch = true;

      // Check code compliance for Equipment (W/m²)
      let equipmentCompliance = false;
      if (codeData?.objects?.OtherEquipment) {
        for (const [equipKey, equipInfo] of Object.entries(codeData.objects.OtherEquipment)) {
          if (equipKey.toLowerCase().includes('equipment') && !equipKey.toLowerCase().includes('computing') &&
            (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo)) {
            const requiredEquipmentPower = getCodeValueForArea(blockInfo.totalArea, (equipInfo as any).area_range, getPowerPerFloorArea(equipInfo));
            if (parseFloat(avgEquipmentPowerPerArea) <= parseFloat(requiredEquipmentPower)) {
              equipmentCompliance = true;
            }
            break;
          }
        }
      }
      if (unitFamilyPresent(mainData, 'equipment') && !equipmentCompliance) hasEquipmentMismatch = true;

      blockComplianceData.push({
        blockName,
        blockInfo,
        avgPeoplePerArea,
        avgWattsPerArea,
        avgComputingPowerPerArea,
        avgEquipmentPowerPerArea,
        peopleCompliance,
        lightsCompliance,
        computingCompliance,
        equipmentCompliance
      });
    });

    const allResults = [
      ...getSiteData(mainData, codeData),
      ...getSimulationData(mainData, codeData),
      ...getBlockData(mainData, codeData),
      ...getPeopleData(mainData, codeData),
      ...getLightsData(mainData, codeData),
      ...getComputingData(mainData, codeData),
      ...getEquipmentData(mainData, codeData),
      ...getPumpData(mainData, codeData),
      ...getInfiltrationData(mainData, codeData),
      ...getNatVentData(mainData, codeData),
      ...getMechVentData(mainData, codeData),
      ...getHVACData(mainData, codeData),
      ...getAirflowNetworkData(mainData, codeData),
      ...getDaylightingData(mainData, codeData),
      ...getWindowShadingData(mainData, codeData),
      ...getHeightComplianceData(mainData, codeData)
    ];

    // Filter for mismatches where a code value exists OR where there's a calculated/actual value
    // This includes cases like ACH where codeValue might be null but a calculated value exists
    const mismatches = allResults.filter((item: any) => {
      // Geometry height rows always appear in the report regardless of isMatch
      if (item.includeInMismatchReport && item.section === 'Geometry' && item.field === 'Height (m)') {
        return true;
      }

      if (item.isMatch) return false; // Only include mismatches
      
      // Exclude Flow Rate and Calc Method from Nat Vent and Infiltration sections
      if ((item.section === 'Nat Vent' || item.section === 'Infiltration') && 
          (item.field === 'Flow Rate' || item.field === 'Calc Method')) {
        return false;
      }
      
      // Include if code value exists
      if (item.codeValue !== undefined && item.codeValue !== null) return true;
      
      // Include if there's a meaningful calculated/actual value even if codeValue is null
      // This handles cases like ACH where the value is calculated from other fields
      if (item.value !== null && item.value !== undefined && 
          item.displayValue !== 'N/A' && item.displayValue !== '' && item.displayValue !== null) {
        return true;
      }
      
      return false;
    });

    const missingUnitLoads = (['people', 'lights', 'computing', 'equipment', 'infiltration', 'hvac'] as const)
      .filter((family) => !unitFamilyPresent(mainData, family));

    if (mismatches.length === 0 && missingUnitLoads.length === 0) {
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#27ae60', fontWeight: 'bold' }}>
          All checked fields match the Code requirements!
        </Box>
      );
    }

    if (mismatches.length === 0) {
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#c0392b', fontWeight: 'bold' }}>
          Required parameters are missing from the model
        </Box>
      );
    }

    // Group mismatches by section, field, and displayValue (Revision Value)
    const groupedMismatches = new Map<string, {
      section: string;
      field: string;
      displayValue: string;
      codeValue: any;
      value: any;
      heightVerification?: string | null;
      zones: string[];
    }>();

    mismatches.forEach((item: any) => {
      const zoneLower = (item.zone || '').toLowerCase();
      const hasReferenceCrack = zoneLower.includes('reference crack conditions');
      const zoneValue = (zoneLower === 'global' || hasReferenceCrack) ? '' : item.zone;
      
      // Geometry height: group by same revision height — merge zones with identical displayValue
      const groupKey = `${item.section}|||${item.field}|||${item.displayValue}`;
      
      if (groupedMismatches.has(groupKey)) {
        const group = groupedMismatches.get(groupKey)!;
        // Add zone if it's not already in the list
        if (zoneValue && !group.zones.includes(zoneValue)) {
          group.zones.push(zoneValue);
        }
      } else {
        groupedMismatches.set(groupKey, {
          section: item.section,
          field: item.field,
          displayValue: item.displayValue,
          codeValue: item.codeValue,
          value: item.value,
          heightVerification: item.heightVerification || null,
          zones: zoneValue ? [zoneValue] : []
        });
      }
    });

    // Convert Map to array for rendering
    const groupedArray = Array.from(groupedMismatches.values());

    // Helper function to find the closest value in an array to a target value
    const findClosestValue = (array: any[], target: number): number => {
      if (array.length === 0) return target;
      
      const numArray = array.map(val => {
        const num = typeof val === 'string' ? parseFloat(val) : Number(val);
        return isNaN(num) ? Infinity : num;
      }).filter(val => val !== Infinity);
      
      if (numArray.length === 0) return target;
      
      let closest = numArray[0];
      let minDiff = Math.abs(numArray[0] - target);
      
      for (let i = 1; i < numArray.length; i++) {
        const diff = Math.abs(numArray[i] - target);
        if (diff < minDiff) {
          minDiff = diff;
          closest = numArray[i];
        }
      }
      
      return closest;
    };

    // Helper function to format Code Value for display based on area
    const formatCodeValueForDisplay = (
      codeValue: any,
      section: string,
      field: string,
      zones: string[],
      mainData: any,
      codeData: any,
      blocks: any,
      displayValue?: any
    ): string => {
      // Check for null, undefined, or string 'null' and return verification required
      if (codeValue === null || codeValue === undefined || codeValue === 'null') {
        return 'Verification required';
      }

      // If codeValue is not an array, return it as is
      if (!Array.isArray(codeValue)) {
        return String(codeValue ?? '');
      }

      // Special case: HeatBalanceAlgorithm → minimum_surface_convection_heat_transfer_coefficient_value and surface_temperature_upper_limit
      // If the code value is an array and we have a revision value, find the closest value
      const discreteValueFields = [
        'minimum_surface_convection_heat_transfer_coefficient_value',
        'surface_temperature_upper_limit'
      ];
      if (section === 'HeatBalanceAlgorithm' && 
          discreteValueFields.includes(field.toLowerCase()) &&
          displayValue !== undefined && displayValue !== null && displayValue !== '') {
        const revisionValue = typeof displayValue === 'string' ? parseFloat(displayValue) : Number(displayValue);
        if (!isNaN(revisionValue)) {
          const closestValue = findClosestValue(codeValue, revisionValue);
          return String(closestValue);
        }
      }

      // Determine if this field uses area_range
      let areaRange: number[] | null = null;
      let valueArray: any[] | null = null;
      let area: number | null = null;

      // Get area based on section
      if (section === 'Block') {
        // For Block section, find the block from zones
        if (zones.length > 0) {
          const blockName = zones[0]; // First zone name
          const blockInfo = blocks[blockName];
          if (blockInfo) {
            area = blockInfo.totalArea;
          }
        }

        // Check for People/Area in Block section
        if (field === 'People/Area' && codeData?.objects?.People) {
          const peopleCode = codeData.objects.People;
          if (peopleCode.area_range && getPeoplePerFloorArea(peopleCode) && Array.isArray(getPeoplePerFloorArea(peopleCode))) {
            areaRange = peopleCode.area_range;
            valueArray = getPeoplePerFloorArea(peopleCode);
          }
        }
        // Computing (W/m²) and Equipment (W/m²) are already calculated correctly in getBlockData
        // so they shouldn't have array codeValue, but handle it just in case
        if (field === 'Computing (W/m²)' || field === 'Equipment (W/m²)') {
          // These should already be calculated, but if they're arrays, handle them
          if (field === 'Computing (W/m²)' && codeData?.objects?.OtherEquipment) {
            for (const [equipKey, equipInfo] of Object.entries(codeData.objects.OtherEquipment)) {
              if (equipKey.toLowerCase().includes('computing') && (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo) && Array.isArray(getPowerPerFloorArea(equipInfo))) {
                areaRange = (equipInfo as any).area_range;
                valueArray = getPowerPerFloorArea(equipInfo);
                break;
              }
            }
          }
          if (field === 'Equipment (W/m²)' && codeData?.objects?.OtherEquipment) {
            for (const [equipKey, equipInfo] of Object.entries(codeData.objects.OtherEquipment)) {
              if (equipKey.toLowerCase().includes('equipment') && !equipKey.toLowerCase().includes('computing') && (equipInfo as any).area_range && getPowerPerFloorArea(equipInfo) && Array.isArray(getPowerPerFloorArea(equipInfo))) {
                areaRange = (equipInfo as any).area_range;
                valueArray = getPowerPerFloorArea(equipInfo);
                break;
              }
            }
          }
        }
      } else if (section === 'Computing Gain' || section === 'Equipment Gain' || section === 'People') {
        // For these sections, get area from zone floor_area
        if (zones.length > 0) {
          const zoneName = zones[0]; // Use first zone
          const zoneData = findDataByPattern(mainData, ['Zone', 'Zones']);
          if (zoneData) {
            area = lookupZoneFloorArea(zoneName, zoneData as Record<string, { floor_area?: unknown }>, zoneSurfaceFloorAreas);
          }
        }

        // Check for Power/Area in Computing Gain section
        if (section === 'Computing Gain' && field === 'Power/Area' && codeData?.objects?.OtherEquipment) {
          const codeComputing = codeData.objects.OtherEquipment['Computing gain'];
          if (codeComputing?.area_range && getPowerPerFloorArea(codeComputing) != null && Array.isArray(getPowerPerFloorArea(codeComputing))) {
            areaRange = codeComputing.area_range;
            valueArray = getPowerPerFloorArea(codeComputing);
          }
        }

        // Check for Power/Area in Equipment Gain section
        if (section === 'Equipment Gain' && field === 'Power/Area' && codeData?.objects?.OtherEquipment) {
          const codeEquipment = codeData.objects.OtherEquipment['Equipment gain'];
          if (codeEquipment?.area_range && getPowerPerFloorArea(codeEquipment) != null && Array.isArray(getPowerPerFloorArea(codeEquipment))) {
            areaRange = codeEquipment.area_range;
            valueArray = getPowerPerFloorArea(codeEquipment);
          }
        }

        // Check for People/Area in People section
        if (section === 'People' && field === 'People/Area' && codeData?.objects?.People) {
          const peopleCode = codeData.objects.People;
          if (peopleCode.area_range && getPeoplePerFloorArea(peopleCode) && Array.isArray(getPeoplePerFloorArea(peopleCode))) {
            areaRange = peopleCode.area_range;
            valueArray = getPeoplePerFloorArea(peopleCode);
          }
        }

        // Check for People field (not People/Area) in People section
        if (section === 'People' && field === 'People' && codeData?.objects?.People) {
          const peopleCode = codeData.objects.People;
          if (peopleCode.area_range && peopleCode.number_of_people && Array.isArray(peopleCode.number_of_people)) {
            areaRange = peopleCode.area_range;
            valueArray = peopleCode.number_of_people;
          }
        }
      }

      // If we have area_range and valueArray and area, calculate the value
      if (areaRange && valueArray && area !== null) {
        const calculatedValue = getCodeValueForArea(area, areaRange, valueArray);
        return String(calculatedValue);
      }

      // If area cannot be determined, return the array with indication
      return codeValue.join(', ');
    };

    return (
      <Box>
        <Typography variant="h5" sx={{ marginBottom: '20px' }}>Validation Report - Mismatches Only</Typography>
        
        {/* Block Information Table */}
        {blockComplianceData.length > 0 && (
          <Box sx={{ marginBottom: '30px' }}>
            <Typography variant="h6" sx={{ marginBottom: '15px' }}>Unit Information</Typography>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Unit</StyledTableCell>
                  <StyledTableCell>Zone</StyledTableCell>
                  <StyledTableCell>Zone Count</StyledTableCell>
                  <StyledTableCell>Total Area (m²)</StyledTableCell>
                  {hasPeopleMismatch && <StyledTableCell>People/Area</StyledTableCell>}
                  {hasLightsMismatch && <StyledTableCell>Lights (W/m²)</StyledTableCell>}
                  {hasComputingMismatch && <StyledTableCell>Computing (W/m²)</StyledTableCell>}
                  {hasEquipmentMismatch && <StyledTableCell>Equipment (W/m²)</StyledTableCell>}
                  <StyledTableCell>Number of Windows</StyledTableCell>
                  <StyledTableCell>Window Orientation</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blockComplianceData.map((data) => {
                  const { blockName, blockInfo, avgPeoplePerArea, avgWattsPerArea, avgComputingPowerPerArea, avgEquipmentPowerPerArea } = data;
                  
                  const orientationText = Object.entries(blockInfo.windowOrientations)
                    .filter(([dir, count]) => (count as number) > 0 && dir !== 'Unknown')
                    .map(([dir, count]) => `${dir}:${count}`)
                    .join(', ') || 'None';

                  return (
                    <TableRow key={blockName}>
                      <StyledTableCell>{blockName}</StyledTableCell>
                      <StyledTableCell>{blockInfo.zones.join(', ')}</StyledTableCell>
                      <StyledTableCell>{blockInfo.zoneCount}</StyledTableCell>
                      <StyledTableCell>{formatArea(blockInfo.totalArea)}</StyledTableCell>
                      {hasPeopleMismatch && <StyledTableCell>{avgPeoplePerArea}</StyledTableCell>}
                      {hasLightsMismatch && <StyledTableCell>{avgWattsPerArea}</StyledTableCell>}
                      {hasComputingMismatch && <StyledTableCell>{avgComputingPowerPerArea}</StyledTableCell>}
                      {hasEquipmentMismatch && <StyledTableCell>{avgEquipmentPowerPerArea}</StyledTableCell>}
                      <StyledTableCell>{blockInfo.totalWindows}</StyledTableCell>
                      <StyledTableCell>{orientationText}</StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </Box>
        )}

        {/* Group mismatches by section and create a table for each section */}
        {(() => {
          // Group by section
          const groupedBySection = new Map<string, typeof groupedArray>();
          groupedArray.forEach(group => {
            // Filter out People/Area from Unit section
            if (group.section === 'Unit' && group.field === 'People/Area') {
              return;
            }
            // Filter out People/Area from People section
            if (group.section === 'People' && group.field === 'People/Area') {
              return;
            }
            if (!groupedBySection.has(group.section)) {
              groupedBySection.set(group.section, []);
            }
            groupedBySection.get(group.section)!.push(group);
          });

          // Sort Geometry height rows by revision height ascending (smallest first; N/A last)
          if (groupedBySection.has('Geometry')) {
            groupedBySection.get('Geometry')!.sort((a, b) => {
              const hA = parseFloat(a.displayValue);
              const hB = parseFloat(b.displayValue);
              if (isNaN(hA) && isNaN(hB)) return 0;
              if (isNaN(hA)) return 1;
              if (isNaN(hB)) return -1;
              return hA - hB;
            });
          }

          const sectionEntries = Array.from(groupedBySection.entries());
          const equipmentToggleSections = sectionEntries.filter(([section, groups]) =>
            isEquipmentGainSection(section) &&
            shouldUseUnitZoneGrouping(section, groups.flatMap((group) => group.zones || []))
          );
          const sharedEquipmentToggleSection = equipmentToggleSections.length > 1
            ? equipmentToggleSections[0][0]
            : null;

          return sectionEntries.map(([section, sectionGroups]) => {
            // For People section, add Number of People and People/Area tables
            const peopleTables = section === 'People' ? (() => {
              const peopleDataForTables = findDataByPattern(mainData, ['People', 'Person', 'Occupancy']);
              const zoneDataForTables = findDataByPattern(mainData, ['Zone', 'Zones']);
              const codePeopleForTables = codeData?.objects?.People || null;

              if (!peopleDataForTables || !zoneDataForTables) return null;

              // Group zones by unit (blockName)
              const peopleMethodUnits: Map<string, {blockName: string, totalArea: number, totalPeople: number, zones: Array<{zoneName: string, peopleEntry: any, zoneArea: number}>}> = new Map();
              const peopleAreaMethodUnits: Map<string, {blockName: string, totalArea: number, totalPeoplePerAreaWeighted: number, zones: Array<{zoneName: string, peopleEntry: any, zoneArea: number}>}> = new Map();

              Object.entries(peopleDataForTables).forEach(([key, item]: [string, any]) => {
                const zoneName = extractZoneName(key) || getZoneRef(item) || key;
                let peopleEntry = item;
                if (extractZoneName(key) && peopleDataForTables) {
                  const entryKey = Object.keys(peopleDataForTables).find(k => k === `People ${extractZoneName(key)}`);
                  if (entryKey) peopleEntry = peopleDataForTables[entryKey];
                }

                // Find zone area
                const zoneArea = lookupZoneFloorArea(
                  zoneName,
                  zoneDataForTables as Record<string, { floor_area?: unknown }>,
                  zoneSurfaceFloorAreas
                );

                // Extract unit name (blockName) from zone name
                let blockName = 'Default Block';
                if (zoneName.includes(':')) {
                  blockName = zoneName.split(':')[0];
                }

                const method = peopleEntry.number_of_people_calculation_method;
                if (method === 'People') {
                  if (!peopleMethodUnits.has(blockName)) {
                    peopleMethodUnits.set(blockName, {
                      blockName,
                      totalArea: 0,
                      totalPeople: 0,
                      zones: []
                    });
                  }
                  const unitData = peopleMethodUnits.get(blockName)!;
                  unitData.zones.push({ zoneName, peopleEntry, zoneArea });
                  unitData.totalArea += zoneArea;
                  unitData.totalPeople += peopleEntry.number_of_people || 0;
                } else {
                  if (!peopleAreaMethodUnits.has(blockName)) {
                    peopleAreaMethodUnits.set(blockName, {
                      blockName,
                      totalArea: 0,
                      totalPeoplePerAreaWeighted: 0,
                      zones: []
                    });
                  }
                  const unitData = peopleAreaMethodUnits.get(blockName)!;
                  unitData.zones.push({ zoneName, peopleEntry, zoneArea });
                  unitData.totalArea += zoneArea;
                  const peoplePerArea = getPeoplePerFloorArea(peopleEntry) || 0;
                  unitData.totalPeoplePerAreaWeighted += peoplePerArea * zoneArea;
                }
              });

              return (
                <>
                  {/* Number of People Table */}
                  {peopleMethodUnits.size > 0 && (
                    <Box sx={{ marginBottom: '30px' }}>
                      <Typography variant="h6" sx={{ marginBottom: '15px' }}>Number of People</Typography>
                      <StyledTable>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>Unit</StyledTableCell>
                            <StyledTableCell>Unit Size (m²)</StyledTableCell>
                            <StyledTableCell>Number of People</StyledTableCell>
                            {isAdmin && <StyledTableCell>Code Value</StyledTableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from(peopleMethodUnits.values()).map((unitData) => {
                            // Calculate expected number of people from code
                            let expectedPeople: number | string = 'N/A';
                            if (codePeopleForTables) {
                              if (codePeopleForTables.area_range && codePeopleForTables.number_of_people && Array.isArray(codePeopleForTables.number_of_people)) {
                                const requiredPeople = getCodeValueForArea(unitData.totalArea, codePeopleForTables.area_range, codePeopleForTables.number_of_people);
                                expectedPeople = requiredPeople.toFixed(2);
                              } else if (codePeopleForTables.number_of_people && !Array.isArray(codePeopleForTables.number_of_people)) {
                                expectedPeople = String(codePeopleForTables.number_of_people);
                              }
                            }

                            return (
                              <TableRow key={unitData.blockName}>
                                <StyledTableCell>{unitData.blockName}</StyledTableCell>
                                <StyledTableCell>{formatArea(unitData.totalArea)}</StyledTableCell>
                                <StyledTableCell>{formatNumberOfPeopleDisplay(unitData.totalPeople)}</StyledTableCell>
                                {isAdmin && <StyledTableCell>{expectedPeople}</StyledTableCell>}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </StyledTable>
                    </Box>
                  )}

                  {/* People/Area Table */}
                  {peopleAreaMethodUnits.size > 0 && (
                    <Box sx={{ marginBottom: '30px' }}>
                      <Typography variant="h6" sx={{ marginBottom: '15px' }}>People/Area</Typography>
                      <StyledTable>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>Unit</StyledTableCell>
                            <StyledTableCell>Unit Size (m²)</StyledTableCell>
                            <StyledTableCell>People/Area</StyledTableCell>
                            {isAdmin && <StyledTableCell>Code Value</StyledTableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from(peopleAreaMethodUnits.values()).map((unitData) => {
                            // Calculate average people per area (weighted by zone area)
                            const avgPeoplePerArea = unitData.totalArea > 0 ? (unitData.totalPeoplePerAreaWeighted / unitData.totalArea) : 0;
                            
                            // Calculate expected people_per_zone_floor_area from code
                            let expectedPeoplePerArea: number | string = 'N/A';
                            if (codePeopleForTables) {
                              if (codePeopleForTables.area_range && getPeoplePerFloorArea(codePeopleForTables) && Array.isArray(getPeoplePerFloorArea(codePeopleForTables))) {
                                const requiredPeoplePerArea = getCodeValueForArea(unitData.totalArea, codePeopleForTables.area_range, getPeoplePerFloorArea(codePeopleForTables));
                                expectedPeoplePerArea = requiredPeoplePerArea.toFixed(2);
                              } else if (getPeoplePerFloorArea(codePeopleForTables) && !Array.isArray(getPeoplePerFloorArea(codePeopleForTables))) {
                                expectedPeoplePerArea = String(getPeoplePerFloorArea(codePeopleForTables));
                              }
                            }

                            return (
                              <TableRow key={unitData.blockName}>
                                <StyledTableCell>{unitData.blockName}</StyledTableCell>
                                <StyledTableCell>{formatArea(unitData.totalArea)}</StyledTableCell>
                                <StyledTableCell>{avgPeoplePerArea === 0 ? '0' : avgPeoplePerArea.toFixed(2)}</StyledTableCell>
                                {isAdmin && <StyledTableCell>{expectedPeoplePerArea}</StyledTableCell>}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </StyledTable>
                    </Box>
                  )}
                </>
              );
            })() : null;

            const renderUnitZoneMismatchTable = (
              groups: typeof sectionGroups,
              tableKey: string,
              field: string,
              selectedUnit: string,
              view: UnitZoneTableView
            ) => {
              const aggregatedRows = sortPumpUnitRows(aggregatePumpSectionGroupsByUnit(groups));
              const filteredRows = prepareUnitZoneTableRows(aggregatedRows, selectedUnit, view);
              if (filteredRows.length === 0) return null;
              const isWattField = isPumpWattField(field);

              return (
                <StyledTable key={tableKey}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>{view === 'byRevisionValue' ? 'Units' : 'Unit'}</StyledTableCell>
                      <StyledTableCell>Zone</StyledTableCell>
                      <StyledTableCell>Revision Value</StyledTableCell>
                      {isAdmin && <StyledTableCell>Code Value</StyledTableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => {
                      const displayValue = isWattField && row.displayValue
                        ? (() => {
                            const n = parseFloat(String(row.displayValue).replace(/,/g, ''));
                            return Number.isFinite(n) ? n.toFixed(1) : row.displayValue;
                          })()
                        : row.displayValue;

                      const unitZoneKeys = (row.zones || []).filter((z) => {
                        const unit = parsePumpZone(z).unit;
                        if (view === 'byRevisionValue') {
                          const units = (row.unit || '').split(',').map((u) => u.trim());
                          return units.includes(unit);
                        }
                        return unit === row.unit;
                      });

                      return (
                        <TableRow key={`${tableKey}-${row.rowKey}-${row.groupIndex}`}>
                          <StyledTableCell>{row.unit || 'Global'}</StyledTableCell>
                          <StyledTableCell>{row.zoneDisplay || '-'}</StyledTableCell>
                          <StyledTableCell>{displayValue}</StyledTableCell>
                          {isAdmin && (
                            <StyledTableCell>
                              {formatCodeValueForDisplay(row.codeValue, row.section, row.field || '', unitZoneKeys, mainData, codeData, blocks, row.displayValue)}
                            </StyledTableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </StyledTable>
              );
            };

            const renderMismatchTable = (
              groups: typeof sectionGroups,
              showFieldColumn: boolean,
              tableKey: string
            ) => (
              <StyledTable key={tableKey}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Zone</StyledTableCell>
                    {showFieldColumn && <StyledTableCell>Field</StyledTableCell>}
                    <StyledTableCell>Revision Value</StyledTableCell>
                    {isAdmin && <StyledTableCell>Code Value</StyledTableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group, index) => {
                    const fieldFormatted = formatKey(group.field || '');
                    const zonesDisplay = group.zones.length > 0 ? group.zones.join(', ') : '';

                    return (
                      <TableRow key={`${group.section}-${group.field}-${group.displayValue}-${index}`}>
                        <StyledTableCell>{zonesDisplay}</StyledTableCell>
                        {showFieldColumn && <StyledTableCell>{fieldFormatted}</StyledTableCell>}
                        <StyledTableCell>{group.displayValue}</StyledTableCell>
                        {isAdmin && (
                          <StyledTableCell>
                            {group.section === 'Geometry' && group.field === 'Height (m)'
                              ? 'Verification required'
                              : formatCodeValueForDisplay(group.codeValue, group.section, group.field || '', group.zones, mainData, codeData, blocks, group.displayValue)}
                          </StyledTableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </StyledTable>
            );

            if (shouldUseUnitZoneGrouping(section, sectionGroups.flatMap((group) => group.zones || []))) {
              const fieldGroups = groupByUnitZoneSectionField(section, sectionGroups);
              const sectionLabel = isPumpSection(section) ? PUMP_SECTION_LABEL : section;
              return (
                <Box key={section} sx={{ marginBottom: '30px' }}>
                  {section === 'People' && peopleTables}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, marginBottom: '15px' }}>
                    <Typography variant="h6">{sectionLabel}</Typography>
                    {(!sharedEquipmentToggleSection || !isEquipmentGainSection(section) || section === sharedEquipmentToggleSection) && (
                      <UnitZoneViewToggle id={`unit-zone-view-${section.replace(/[^a-zA-Z0-9_-]/g, '-')}`} value={unitZoneView} onChange={setUnitZoneView} />
                    )}
                  </Box>
                  {section === 'People' && sectionGroups.length > 0 && (
                    <Typography variant="h6" sx={{ marginBottom: '15px' }}>People Activity</Typography>
                  )}
                  {fieldGroups.map(([field, fieldGroupsForField]) => {
                    const table = renderUnitZoneMismatchTable(fieldGroupsForField, `${section}-${field}`, field, '', unitZoneView);
                    if (!table) return null;
                    return (
                      <Box key={field} sx={{ marginBottom: '24px' }}>
                        <Typography variant="subtitle1" sx={{ marginBottom: '10px', fontWeight: 600 }}>
                          {formatKey(field)}
                        </Typography>
                        {table}
                      </Box>
                    );
                  })}
                </Box>
              );
            }

            return (
              <Box key={section} sx={{ marginBottom: '30px' }}>
                <Typography variant="h6" sx={{ marginBottom: '15px' }}>{section}</Typography>
                {peopleTables}
                {section === 'People' && sectionGroups.length > 0 && (
                  <Typography variant="h6" sx={{ marginBottom: '15px', marginTop: '20px' }}>People Activity</Typography>
                )}
                {renderMismatchTable(sectionGroups, true, section)}
              </Box>
            );
          });
        })()}
      </Box>
    );
  };

  // Check if AirflowNetwork data exists
  const hasAirflowNetworkData = mainData && findAirflowNetworkKeys(mainData).length > 0;

  const tabs = [
    { id: 'files', label: 'Files' },
    { id: 'site', label: 'Site' },
    { id: 'simulation', label: 'Simulation' },
    { id: 'block', label: 'Unit' },
    { id: 'geometry', label: 'Geometry' },
    { id: 'people', label: 'People' },
    { id: 'lights', label: 'Lights' },
    { id: 'computing', label: 'Computing Gain' },
    { id: 'equipment', label: 'Equipment Gain' },
    { id: 'pump', label: 'Pump' },
    { id: 'infiltration', label: 'Infiltration' },
    ...(!hasAirflowNetworkData ? [{ id: 'nat-vent', label: 'Nat Vent' }] : []),
    { id: 'mech-vent', label: 'Mech Vent' },
    { id: 'hvac', label: 'HVAC' },
    ...(hasAirflowNetworkData ? [{ id: 'airflow-network', label: 'AirflowNetwork' }] : []),
    { id: 'daylighting', label: 'Daylighting' },
    { id: 'window-shading', label: 'Window Shading' },
    { id: 'report', label: 'Report' },
  ];

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', padding: '20px', minWidth: '1000px' }}>
      <Container>
        <Header>
          <Typography variant="h1" sx={{ marginBottom: '10px', color: 'white' }}>
            EnergyPlus Data Viewer
          </Typography>
          <Typography variant="body1" sx={{ color: 'white' }}>
            Analyze building energy simulation data
          </Typography>
        </Header>

        <TabsContainer>
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabsContainer>

        <TabContent active={activeTab === 'files'}>
          <Box sx={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
            <Typography variant="h3" sx={{ marginBottom: '15px' }}>
              Revision JSON Data
            </Typography>

            <DropArea
              ref={mainDropAreaRef}
              highlight={mainDropHighlight}
              onDrop={handleMainDrop}
              onDragOver={preventDefaults}
              onClick={() => mainFileInputRef.current?.click()}
            >
              <Typography>Drag & Drop your JSON file here</Typography>
              <Typography>or</Typography>
              <FileInputWrapper>
                <FileInputButton>Choose JSON File</FileInputButton>
                <input
                  ref={mainFileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={(e) => handleMainFileSelect(e.target.files)}
                />
              </FileInputWrapper>
              {mainFileName && (
                <Typography sx={{ marginTop: '10px', fontStyle: 'italic', color: '#7f8c8d' }}>
                  {mainFileName}
                </Typography>
              )}
            </DropArea>
          </Box>

          <Box sx={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
            <Typography variant="h3" sx={{ marginBottom: '15px' }}>
               Code JSON (Optional)
            </Typography>

            <DropArea
              ref={codeDropAreaRef}
              highlight={codeDropHighlight}
              onDrop={handleCodeDrop}
              onDragOver={preventDefaults}
              onClick={() => codeFileInputRef.current?.click()}
            >
              <Typography>Drag & Drop your Code JSON file here</Typography>
              <Typography>or</Typography>
              <FileInputWrapper>
                <FileInputButton>Choose Code JSON File</FileInputButton>
                <input
                  ref={codeFileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={(e) => handleCodeFileSelect(e.target.files)}
                />
              </FileInputWrapper>
              {codeFileName && (
                <Typography sx={{ marginTop: '10px', fontStyle: 'italic', color: '#7f8c8d' }}>
                  {codeFileName}
                </Typography>
              )}
            </DropArea>
          </Box>

          <Section>
            <SectionHeader>File Status</SectionHeader>
            <SectionContent>
              <Typography
                sx={{
                  color: mainFileStatus.includes('Error') ? '#e74c3c' : mainFileStatus.includes('Loaded') ? '#27ae60' : 'inherit',
                  fontWeight: mainFileStatus.includes('Loaded') ? 'bold' : 'normal',
                }}
              >
                {mainFileStatus}
              </Typography>
              <Typography
                sx={{
                  color: codeFileStatus.includes('Error') ? '#e74c3c' : codeFileStatus.includes('Loaded') ? '#27ae60' : 'inherit',
                  fontWeight: codeFileStatus.includes('Loaded') ? 'bold' : 'normal',
                }}
              >
                {codeFileStatus}
              </Typography>
            </SectionContent>
          </Section>
        </TabContent>

        {/* Site Tab */}
        <TabContent active={activeTab === 'site'}>
          {renderSiteTab()}
        </TabContent>

        {/* Simulation Tab */}
        <TabContent active={activeTab === 'simulation'}>
          {renderSimulationTab()}
        </TabContent>

        {/* Unit Tab */}
        <TabContent active={activeTab === 'block'}>
          {renderBlockTab()}
        </TabContent>

        {/* Geometry Tab */}
        <TabContent active={activeTab === 'geometry'}>
          {renderGeometryTab()}
        </TabContent>

        {/* People Tab */}
        <TabContent active={activeTab === 'people'}>
          {renderPeopleTab()}
        </TabContent>

        {/* Lights Tab */}
        <TabContent active={activeTab === 'lights'}>
          {renderLightsTab()}
        </TabContent>

        {/* Computing Gain Tab */}
        <TabContent active={activeTab === 'computing'}>
          {renderComputingTab()}
        </TabContent>

        {/* Equipment Gain Tab */}
        <TabContent active={activeTab === 'equipment'}>
          {renderEquipmentTab()}
        </TabContent>

        {/* Pump Tab */}
        <TabContent active={activeTab === 'pump'}>
          {renderPumpTab()}
        </TabContent>

        {/* Infiltration Tab */}
        <TabContent active={activeTab === 'infiltration'}>
          {renderInfiltrationTab()}
        </TabContent>

        {/* Natural Ventilation Tab — scheduled only when there is no Airflow Network */}
        {!hasAirflowNetworkData && (
          <TabContent active={activeTab === 'nat-vent'}>
            {renderNatVentTab()}
          </TabContent>
        )}

        {/* Mechanical Ventilation Tab */}
        <TabContent active={activeTab === 'mech-vent'}>
          {renderMechVentTab()}
        </TabContent>

        {/* HVAC Tab */}
        <TabContent active={activeTab === 'hvac'}>
          {renderHVACTab()}
        </TabContent>

        {/* AirflowNetwork Tab */}
        {hasAirflowNetworkData && (
          <TabContent active={activeTab === 'airflow-network'}>
            {renderAirflowNetworkTab()}
          </TabContent>
        )}

        {/* Daylighting Tab */}
        <TabContent active={activeTab === 'daylighting'}>
          {renderDaylightingTab()}
        </TabContent>

        {/* Window Shading Tab */}
        <TabContent active={activeTab === 'window-shading'}>
          {renderWindowShadingTab()}
        </TabContent>

        {/* Report Tab */}
        <TabContent active={activeTab === 'report'}>
          {renderReportTab()}
        </TabContent>
      </Container>
    </Box>
  );
};

export default EnergyPlusViewer;

